﻿/**
 * The realm, generated.
 *
 * Twenty-four provinces and eight powers fit on one screen and were written out
 * by hand. A hundred and twenty-odd provinces and two dozen powers do not fit on
 * one screen and must not be written out by hand — that is data entry, not
 * design, and a table that large is a table nobody will ever rebalance.
 *
 * So the realm is *grown* from a fixed seed, and three rules govern it.
 *
 * 1. **It is deterministic.** There is no `Math.random` here and there never may
 *    be. Every irregularity comes from `hash2` — an integer hash of the thing
 *    being decided about — so the same seed gives the same realm on every
 *    machine, in every build, forever. The seed is a constant rather than a
 *    parameter because the realm is *content*: two players talking about
 *    Stonepale must be talking about the same Stonepale.
 *
 * 2. **The heartland is hand-authored and stays hand-authored.** Ashfall,
 *    Deepgate, Stonepale and the other twenty-one carry the setting's voice, and
 *    a realm of procedurally-named nowheres would lose it. They keep their
 *    names, their flavour, their terrain, their yields and their graph exactly as
 *    written; the only thing generation gives them is a place on a bigger
 *    lattice. They occupy the eastern seaboard — which is why The Wake,
 *    Saltmarch, Drownfoot and The Reef are still on the water — and everything
 *    west, north and south of them is frontier.
 *
 * 3. **It is content, so it lives in `src/data`.** Nothing in here has
 *    simulation meaning. `src/sim/campaign` reads this table once at the start of
 *    a campaign and then owns a mutable copy; who holds what afterwards is state.
 *
 * The pipeline, in order:
 *
 *   lattice → land mask → terrain field → names and yields
 *           → links → capitals → territories → banners
 *
 * `provinces.ts` and `nations.ts` are the public faces of this file and re-export
 * everything callers used to get from them. Nothing outside imports `REALM`
 * directly except the tests and `cartography.ts`, which wants the noise.
 */

import type { ResourceKey } from '../sim/types.ts';
import { FACTIONS } from './factions.ts';
import { RACES, racesOfHomeland, factionOfRace, type Homeland, type RaceId } from './races.ts';
import { leaderForKingdom } from './leaders.ts';

// ── The vocabulary (was in provinces.ts) ───────────────────────────────────

/**
 * What the ground is. `COORDINATION.md` names these and nothing invents a
 * twelfth spelling.
 *
 * The last three are new and they arrived together, because they are one idea:
 * the western continent is banded by **rainfall** rather than by decree — wet on
 * its eastern seaboard, arid in its middle, walled by high ground in the far
 * west — and a band needs a word. `'ice'` is the northern continent and any
 * permanent snow.
 */
export type ProvinceTerrain =
  | 'plain'
  | 'hill'
  | 'forest'
  | 'marsh'
  | 'coast'
  | 'mountain'
  | 'ruin'
  | 'jungle'
  | 'desert'
  | 'ice';

/**
 * What lies between two provinces. A road is a highway for an army; a pass is
 * a week of climbing and a very good place to be waiting for one.
 */
export const LinkKind = {
  Wild: 0,
  Road: 1,
  River: 2,
  Pass: 3,
} as const;
export type LinkKindId = (typeof LinkKind)[keyof typeof LinkKind];

export const LINK_NAMES: Record<number, string> = {
  [LinkKind.Wild]: 'open country',
  [LinkKind.Road]: 'old road',
  [LinkKind.River]: 'ford',
  [LinkKind.Pass]: 'pass',
};

/**
 * Turns an army spends crossing, and what the crossing is worth to a defender.
 *
 * **Only the pass came down, and the reason the rest did not is worth keeping.**
 * The owner asked for travel time *"but not as long"*, and the obvious reading —
 * cut every link to one month — was tried and reverted the same hour. A march
 * that costs one month resolves inside a single turn, and a host that is never
 * *on the road* cannot be drawn between two provinces, cannot be turned around
 * halfway, and cannot be intercepted. That is the whole of `marches.ts`, deleted
 * by a constant. Seven tests said so.
 *
 * The journey did get shorter, and by more than this table could have managed:
 * an order is a *destination* now, so the legs run back to back instead of
 * waiting for the player to notice an arrival and issue the next order. The dead
 * time between legs was always the larger half of "too long".
 */
export const LINK_MARCH: Record<number, number> = {
  [LinkKind.Wild]: 2,
  [LinkKind.Road]: 1,
  [LinkKind.River]: 2,
  [LinkKind.Pass]: 2,
};

export interface TerrainProfile {
  name: string;
  /** Multiplier on the defender's strength in a battle fought here. */
  defence: number;
  /** Multiplier on march time into this province. */
  march: number;
  /**
   * What a hold built on this ground is actually made of.
   *
   * > *"when you click on a province it should tell you the climate"*
   *
   * The panel could already name the terrain and its defence multiplier, which
   * answers "what happens if I fight here" and says nothing about the far more
   * consequential question of what happens if I *settle* here. Since the
   * climate pass these are not flavour: `BIOME_MIX` in `sim/map.ts` gives each
   * of these countries a different mix of wood, stone and ore, and
   * `npm run climate` prints the table. A mountain hold really is short of
   * timber; a desert really has almost none.
   *
   * Kept as one short clause because it sits in a panel row, and written as
   * what the player will *have* rather than as a terrain type — "rock and ore
   * underfoot, little timber" is a decision; "montane" is a label.
   */
  climate: string;
}

export const PROVINCE_TERRAIN: Record<ProvinceTerrain, TerrainProfile> = {
  plain: { name: 'Ash Plain', defence: 1, march: 1, climate: 'Open country. Wood to work and stone to walk to — the gentle opening.' },
  hill: { name: 'Hills', defence: 1.25, march: 1.5, climate: 'Stony going. Rock close at hand, the woods thinning out.' },
  forest: { name: 'Deadwood', defence: 1.15, march: 1.5, climate: 'Deep wood. Timber everywhere and almost no stone.' },
  marsh: { name: 'Marsh', defence: 1.2, march: 2, climate: 'Standing water. Poor ground, poorer stone, and wood on the drier banks.' },
  coast: { name: 'Coast', defence: 1.05, march: 1, climate: 'Open shore. Damp, easy going, no great stone.' },
  mountain: { name: 'Mountain', defence: 1.4, march: 2, climate: 'Rock and ore underfoot, and hardly a tree — the woodcutters go a long way.' },
  ruin: { name: 'Ruin', defence: 1.1, march: 1, climate: 'Somebody built here before. The ground is full of their walls.' },
  /**
   * Jungle is forest that fights back: the same cover, and worse going than a
   * marsh, because a column in it is a column in single file.
   */
  jungle: { name: 'Jungle', defence: 1.3, march: 2, climate: 'Timber and water both, in the way of everything.' },
  /**
   * Desert is the opposite trade — nothing to hide behind and nothing in the
   * way, so it is the easiest ground on the board to cross and the worst to be
   * caught standing on.
   */
  desert: { name: 'Desert', defence: 0.95, march: 1, climate: 'Sand, and almost nothing standing on it. Bring your own timber.' },
  /** Ice is a mountain without the cover: as slow, and no better to hold. */
  ice: { name: 'Ice', defence: 1.1, march: 2, climate: 'Frozen. No wood to speak of, bare rock showing through the snow.' },
};

export interface ProvinceDef {
  id: string;
  name: string;
  flavour: string;
  /** Layout position on the world-map screen. UI only; see LATTICE for the range. */
  x: number;
  y: number;
  terrain: ProvinceTerrain;
  /**
   * This holding touches navigable water.
   *
   * A *property of the realm*, not of the drawing, which is why it lives here
   * rather than in `cartography.ts` where the word "coastal" first appeared. It
   * is read off the land mask — a land cell with water in any of its eight
   * neighbours, or one on the rim of the lattice — and it is the gate on
   * everything the sea layer does: only a coastal province may raise a port,
   * only a coastal province may be landed on, and only coastal provinces are
   * joined by sea lanes. `terrain === 'coast'` is a *flavour* decision made by
   * the terrain field and is deliberately a different question; a marsh or a
   * mountain can stand on the water.
   */
  coastal: boolean;
  /**
   * No road reaches this holding: it must be reached by sea or not at all.
   *
   * There are two of them and they sit in the Sundered Sea. Everything in the
   * land layer already handles them correctly by accident of being written
   * carefully — `hopsFrom` returns -1, so `chooseCapitals` will not seat a crown
   * here, `growTerritories` cannot grow into it and `campaignFor` will not set
   * out for it — which means an island is exactly what the brief wanted it to
   * be: ground that only a power with ships can hold.
   */
  island: boolean;
  /** Gold per turn at development 1. */
  wealth: number;
  /** Recruits per turn at development 1. */
  levy: number;
  /** Starting fortification, 0..3. */
  fort: number;
  /** The strategic good this province produces, or '' for none. */
  resource: ResourceKey | '';
  /** Units of `resource` per turn. */
  yield: number;
  /** Nation id that begins holding it, or '' for unclaimed ground. */
  owner: string;
  /** Neighbour ids. Declared once per pair; the graph builder symmetrises. */
  links: string[];
}

export interface NationDef {
  id: string;
  name: string;
  /** Which `FactionDef` supplies its roster, colours and architecture. */
  factionId: string;
  /** Province id it starts from and will defend hardest. */
  capital: string;
  /** 0..1 — how readily it picks a fight it does not have to pick. */
  aggression: number;
  /**
   * 0..1 — how much local superiority it wants before committing. A cautious
   * nation wants two to one; a reckless one will take evens.
   */
  caution: number;
  /** 0..1 — share of income it will pour into troops rather than bank. */
  militarism: number;
  /** One line, in the nation's own voice, for the world-map panel. */
  creed: string;
  /**
   * Map banner colour, when the faction's accent will not do.
   *
   * Colours come from the faction so a banner means the same thing everywhere,
   * and that is the rule for the eight hand-authored powers. It cannot be the
   * rule for two dozen: there are ten factions and twenty-four seats, so most
   * factions seat more than one crown and two crowns of the same faction sharing
   * a colour would be indistinguishable on the one screen where every power is
   * visible at once. Generated nations therefore carry an explicit banner, and
   * the hues are assigned so that no two *neighbours* are close on the wheel.
   */
  banner?: number;
  /**
   * The temperament this nation was cut from, for the panel and for tests.
   * Hand-authored powers have none; it is a property of generation.
   */
  doctrine?: string;
  /**
   * **Which people this crown is**, and the seam `races.ts` published and
   * nothing filled in. Every nation on the board has one, authored or
   * generated, and a generated one is dealt from the *ground its capital
   * stands on* — see `seatRaces`. A region's crowns may share a race; that is
   * the point of a region rather than an accident of it.
   */
  raceId: RaceId;
  /**
   * …and which house of that people. `leaderForKingdom` is pure, total and
   * wraps, so no RNG crosses the seam and two crowns of one people in one
   * region are two different houses who hate each other.
   */
  leaderId: string;
}

export interface SpecialLink {
  a: string;
  b: string;
  kind: LinkKindId;
}

// ── Deterministic noise ────────────────────────────────────────────────────
//
// The same integer hash `cartography.ts` draws its coastline from, and it lives
// here rather than there because the shape of the realm and the drawing of it
// must agree about what "the same seed" means. `cartography.ts` imports these.

/** Integer hash → 0..1. Cheap, well-mixed, and the same everywhere forever. */
export function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix | 0, 374761393) ^ Math.imul(iy | 0, 668265263) ^ Math.imul(seed | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Lattice value noise, 0..1, smooth in both axes. */
export function vnoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);
  const a = hash2(x0, y0, seed);
  const b = hash2(x0 + 1, y0, seed);
  const c = hash2(x0, y0 + 1, seed);
  const d = hash2(x0 + 1, y0 + 1, seed);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

/** Pick from a table by hash. The one place indices are turned into content. */
function pick<T>(table: readonly T[], a: number, b: number, seed: number): T {
  return table[Math.floor(hash2(a, b, seed) * table.length) % table.length];
}

/** 0..1 hash mapped onto a range. */
function span(lo: number, hi: number, a: number, b: number, seed: number): number {
  return lo + hash2(a, b, seed) * (hi - lo);
}

/**
 * Multi-octave value noise, 0..1. The shape of a coast needs more than two.
 *
 * `fbm` above is two octaves and is kept for the fields that want a *region* —
 * moisture, wealth, the slow drifts. A coastline wants detail at four or five
 * scales at once, because that is the difference between an outline and a shore:
 * the long swell of a gulf, the bulge of a peninsula, the bite of a bay, the
 * crinkle of an inlet, all present in the same line.
 */
function fbmN(x: number, y: number, seed: number, octaves: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let f = 1;
  for (let o = 0; o < octaves; o++) {
    sum += vnoise(x * f + o * 13.7, y * f + o * 7.3, seed + o * 101) * amp;
    norm += amp;
    amp *= 0.52;
    f *= 2.07;
  }
  return sum / norm;
}

// ── The sheet of paper ─────────────────────────────────────────────────────

const REALM_SEED = 0x4c415354;

/**
 * The world, in map units, and the raster it is decided on.
 *
 * There is no lattice any more, and that is the whole of this rewrite.
 *
 * The old generator built a 27×19 grid of province sites, nudged each one off
 * its grid point, masked the grid with noise and took a Voronoi of what
 * survived. A jittered lattice under a Voronoi produces uniform quadrilaterals
 * in readable rows and columns — *always* — and no amount of noise on the mask
 * fixes it, because the mask only ever decided which cells were wet. It never
 * touched the size or the shape of one.
 *
 * So the order is reversed. **The land is generated first and the provinces
 * second.** The land is a field sampled on a fine raster — two map units a cell,
 * which is a ninth of a province — and a continent's outline has no idea how
 * many provinces will end up standing on it. Provinces are then scattered
 * *inside* the land by Poisson-disc sampling against a density field, so they
 * come out different sizes and different shapes, and their territories are grown
 * across the raster rather than cut out of the plane. Nothing in the pipeline
 * has a row or a column in it.
 *
 * ── The sheet grew, and nothing already on it moved ──────────────────────
 *
 * It was 492 by 376 with its origin at (10, 10). Two continents were wanted —
 * a large one off the western edge and a northern one beyond the ice — and
 * there was no water left to put them in, so the paper is bigger: the origin
 * moves to (-232, -128) and the extent to 734 by 514. **Every existing
 * coordinate is unchanged**, which is the whole point: the land field is a
 * function of absolute position, so a continent declared at (394, 226) is
 * evaluated through the same warp at the same place and comes out the same
 * shape it was.
 *
 * Two functions had to stop being written in fractions of the half-extent
 * before that was true, and they are `rimPush` and `wasteness` below. Both said
 * "nine tenths of the way to the edge" and "past three fifths of the way to the
 * pole", which are statements about the *paper* rather than about the world —
 * so widening the paper moved the eastern rim off the Hearthland Reach's coast
 * and dragged the frozen band forty units north of the ice it names. They are
 * absolute now, in map units from the edge and in map units from the equator,
 * and the equator is declared rather than derived.
 *
 * Scale is unchanged in the sense that matters to `cartography.ts` — a province
 * is still drawn with constants tuned in map units — but there are twice as many
 * of them and each is about seven tenths the width it was. See
 * `TARGET_PROVINCES`.
 *
 * ── …and then it grew east, for the same reason and with the same rule ────
 *
 * The owner asked for a second half of the world: a large continent east of
 * everything, and a smaller island north-east of that. There was no water left
 * to put them in — the eastern rim ran along the Hearthland Reach's own coast —
 * so the paper is wider again, from 734 to 1210, and the origin does not move.
 * Every existing coordinate is unchanged for the third time, and for the third
 * time that is the whole point.
 *
 * **One thing did have to move, and it is worth naming because it is the same
 * bug as `rimPush` and `wasteness` in a third costume.** Widening the sheet
 * moves its middle, and the middle of the sheet was load-bearing in exactly one
 * place: `seatHeartland` oriented the authored twenty-four's long axis "toward
 * the middle of the board", because that is where the middle sea was on the old
 * paper. It is not any more — the middle of a 1210-wide sheet is out in the
 * Weltering, on the far side of the Reach — and the axis would have flipped, so
 * The Wake, Saltmarch, Drownfoot and The Reef would have been seated inland with
 * Ashfall on the water. The heartland's seaboard now points at **the Sunder**,
 * which is a declared thing with a place of its own, rather than at a property
 * of the paper. `RegionSpec.deg` still measures from the sheet's centre, and may:
 * it is a bearing for placing a label and means the same thing on any paper.
 *
 * The other half of holding the old world still is the **Weltering Sea**: a
 * declared water in the new gap. Without it the eastern rim's pressure simply
 * lifts off the Reach's and the Rimewreath's eastern coasts and they grow out
 * into the new ocean. See `SEAS`.
 */
export const WORLD = {
  originX: -232,
  originY: -128,
  w: 1290,
  h: 514,
  /** Raster pitch. A ninth of a province, which is what buys a drawn coastline. */
  cell: 2,
} as const;

const COLS = Math.round(WORLD.w / WORLD.cell);
const ROWS = Math.round(WORLD.h / WORLD.cell);
const CX = WORLD.originX + WORLD.w / 2;
const CY = WORLD.originY + WORLD.h / 2;
const HX = WORLD.w / 2;
const HY = WORLD.h / 2;

function cellX(col: number): number {
  return WORLD.originX + (col + 0.5) * WORLD.cell;
}
function cellY(row: number): number {
  return WORLD.originY + (row + 0.5) * WORLD.cell;
}
function rasterKey(col: number, row: number): number {
  return row * COLS + col;
}

/**
 * The raster, published — because the *drawing* is grown from it too.
 *
 * `cartography.ts` used to take a Voronoi of the province sites and clip it to a
 * ring. It now traces the boundaries between these cells instead, which is the
 * only way the coastline in the picture and the coastline the simulation
 * believes in can be the same line. See the note at the top of that file.
 */
export interface RealmRaster {
  cols: number;
  rows: number;
  /** Size of a raster cell, in map units. */
  cell: number;
  originX: number;
  originY: number;
  /** Province index per cell, or -1 for water. */
  owner: Int32Array;
  /** Map units from this cell to the nearest land. 0 on land. */
  offshore: Float32Array;
  /**
   * Elevation, 0..1, per cell — the same field `elevationAt` hands the biome
   * chooser, published so the *drawing* can use it too.
   *
   * The chart has always been flat, and it was flat while the generator knew
   * the height of every cell on the board: `RIDGE_FIELD` is precomputed over
   * this exact raster and decides where the mountains go. Nothing drew it. A
   * hillshade wants the field at the pitch the coastline is traced at, which is
   * this one, so it goes in the raster beside the mask rather than being
   * resampled from a function by whoever needs it.
   */
  height: Float32Array;
}

// ── The shape of the world ─────────────────────────────────────────────────

/**
 * What a region *is*, for whoever binds races and starting kingdoms to it.
 *
 * Deliberately about climate and ground rather than about peoples: this file
 * generates geography and has no business deciding who lives there. A race
 * roster picks the regions it wants by character, and the map does not have to
 * change when it does.
 *
 * **The first ten are `Homeland` in `races.ts`, word for word, and that is the
 * whole marriage.** They were two vocabularies that nearly rhymed — the roster
 * wanted `coast`, `waste`, `steppe`, `underground` and `blight` and the map
 * offered `temperate`, `desert` and `volcanic` instead — so `racesOfHomeland`
 * came back empty for five peoples in eleven and the two halves of the game
 * quietly did not join up. Making them the same words is what lets `homelandOf`
 * below be a *total* function, and `provinces.ts` holds a `Record<Homeland, …>`
 * that stops compiling the day the roster invents an eleventh country.
 *
 * The last four are ground with no people on it, which the world is allowed to
 * have: a temperate march, a desert, a frozen shore, a volcanic waste. A region
 * of those characters is somebody's frontier rather than somebody's home.
 */
export type RegionCharacter =
  // The twelve that are `Homeland`, and must stay the twelve that are `Homeland`.
  | 'heartland'
  | 'forest'
  | 'mountain'
  | 'coast'
  | 'isles'
  | 'waste'
  | 'marsh'
  | 'steppe'
  | 'underground'
  | 'blight'
  /**
   * These two were on the second list until the eastern continent, and moving
   * them is the whole of what it cost to give the new peoples somewhere to be.
   *
   * A rainforest and a sand sea were *ground nobody is from*: `GROUND_HOMELAND`
   * folded them into `'forest'` and `'steppe'`, so the Greenmaw seated wood
   * elves and the Long Thirst seated beastkin, because nobody in the roster
   * belonged in either in particular. Somebody does now — the Long Noon and the
   * Thirstless — so the fold is gone and the two characters are countries of
   * their own, exactly as a marsh and a mountain always were. The fourteen
   * `RegionSpec` lines that name them did not change by a character.
   */
  | 'jungle'
  | 'desert'
  // …and ground nobody is from.
  | 'temperate'
  | 'frozen'
  | 'volcanic';

/**
 * A landmass, declared as a *shape* and nothing else.
 *
 * A mass is a chain of anchor discs. `x` and `y` place the middle of the chain
 * **in map units, on the sheet of paper**; `along` is half the chain's length
 * and `width` the anchors' radius, both in map units; `axis` is the bearing the
 * chain runs along, zero pointing east; `curve` bows the chain so a continent is
 * a crescent rather than a sausage.
 *
 * ── Why this is not a bearing and a radius any more ───────────────────────
 *
 * It was `deg` and `at`: a bearing from the middle of the board and a fraction
 * of its half-extent. Every mass, every gulf, every isle and every region seed
 * was placed that way, the great sea was a disc at the origin those bearings
 * were measured from, and the axis of a continent was declared as a *tilt off
 * tangential*. A composition declared in polar coordinates about a hole in the
 * middle **is a wheel**, and no amount of tuning the numbers makes it stop
 * being one: the owner read the last board as "continents arranged around a
 * middle sea" because that is precisely how it was written down.
 *
 * So the wheel is gone from the notation, not merely from the values. A
 * continent is at a place, running in a direction, and the seas are shapes with
 * places of their own (`SEAS`, below) rather than the hub everything else is
 * hung off. Asymmetry is now the cheap thing to express and symmetry is the
 * thing you would have to work at.
 *
 * The field is the sum of the anchors' falloffs, thresholded — but the *point*
 * at which it is thresholded is a domain-warped, five-octave-noise-perturbed
 * copy of the plane, and that is where a coastline comes from. A disc chain
 * under a warp of thirty units at a hundred-and-forty-unit wavelength is not a
 * disc chain any more: it grows peninsulas, it gets bitten into bays, it sheds
 * islets off its headlands, and it does all of that at three scales at once
 * because the warp has three octaves. None of it is drawn by hand and none of it
 * repeats.
 *
 * `isles` are extra anchors — small, offshore, and exempt from everything that
 * digs water out of the field, so an archipelago is a feature the world always
 * has rather than one a seed might decline to produce. They belong to the mass
 * they are declared under, which is the thing a hand-drawn map does without
 * thinking: the isles off a coast are part of that coast's story even when
 * nobody on them can walk home.
 */
interface IsleSpec {
  x: number;
  y: number;
  r: number;
  n: number;
  /** Bearing the little chain runs along, zero east. */
  run: number;
}

interface MassSpec {
  id: string;
  /** For the record and for error messages. Regions carry the map's names. */
  name: string;
  /** One line saying what a player should be able to read off its outline. */
  silhouette: string;
  x: number;
  y: number;
  along: number;
  width: number;
  curve: number;
  /** Bearing the chain runs along, zero east. */
  axis: number;
  anchors: number;
  /** How far an anchor may wander off the chain. */
  wobble: number;
  /** Multiplies the province spacing here: low is crowded, high is empty. */
  density: number;
  /**
   * A range running the *length* of this continent rather than across it.
   *
   * The Worlds Edge Mountains are the shape of the Old World's strategy: one
   * chain a thousand miles long with a handful of passes, and everything east
   * of it is a different world from everything west. A range drawn across a
   * continent divides it in two; a range drawn along one divides it into two
   * *countries*, which is a bigger idea and the one the reference is built on.
   */
  spine?: boolean;
  /**
   * How high this mass's spine stands, 1 being ordinary. Only with `spine`.
   *
   * The far western wall is `1.4`: high enough that its saddles are still
   * mountain, so the continent behind it is genuinely shut in and the rain that
   * would have crossed it does not.
   */
  spineLift?: number;
  /** A hole in the middle of it: a ring continent about an inner sea. */
  ring?: number;
  isles?: readonly IsleSpec[];
  /** The hand-authored twenty-four stand here. Exactly one mass may say so. */
  heart?: boolean;
}

/**
 * The landmasses — five continents, and every one of them a character.
 *
 * The last board had four masses of broadly similar temper spaced evenly round
 * a middle sea, and the owner's verdict was the one that matters: *you could not
 * name any of them from the outline.* A continent that is a rounded blob at a
 * bearing is a region of the map; a continent you can point at is a **place**.
 * So each of the five here is declared to be a different *kind of shape*, and
 * the note beside it says what a player should be able to read off it:
 *
 *   · **The Rimewreath** — a broad band clean across the top of the world, its
 *     ends trailing off into open ocean. The frozen north, and it looks it.
 *   · **The Nightspur** — a long, thin peninsula hanging south off the
 *     Rimewreath's belly and reaching almost to the middle of the board. Three
 *     provinces wide, all of it coast. One of the two silhouettes nobody will
 *     confuse; see `BRIDGED` for how it hangs.
 *   · **Kaldrhûn** — a north–south wall down the western edge with one mountain
 *     spine running its whole length. The map's most strategic line: everything
 *     east of it is a different world from everything west, and there are four
 *     ways through.
 *   · **The Hearthland Reach** — broad and blunt in the east, the widest ground
 *     on the board, with a spine walling its far side off. Twenty-four authored
 *     holdings stand here and the ground behind the spine is where the blight is.
 *   · **The Verdance** — a green shelf across the south, bowed, welded to the
 *     Reach at one isthmus. The only land frontier on the board.
 *   · **Aelloran** — a **ring** about its own inner sea, alone in the
 *     south-western ocean. The other silhouette nobody will confuse.
 *
 * **Fewer, larger, and fewer scraps.** The isle chains are down from four to
 * two and both of them are deliberate: a chain of skerries off the Spur's
 * seaward flank, because a raider coast wants shelter to hide in, and the
 * Sunken Chain south of Aelloran, because a ring wants a tail. Nothing else on
 * the board is an islet unless the warp shed it off a headland, and
 * `MIN_ISLAND_CELLS` throws away the ones too small to hold a holding.
 */
const MASSES: readonly MassSpec[] = [
  {
    id: 'rimewreath',
    name: 'The Rimewreath',
    silhouette: 'a band of ice clean across the top of the world',
    x: 342,
    y: 62,
    along: 130,
    width: 58,
    curve: 20,
    axis: 358,
    anchors: 7,
    wobble: 15,
    density: 1.22,
  },
  {
    id: 'spur',
    name: 'The Nightspur',
    silhouette: 'a long knife of a peninsula hanging into the middle of the sea',
    x: 272,
    y: 186,
    along: 88,
    width: 37,
    curve: 12,
    axis: 94,
    anchors: 7,
    wobble: 5,
    density: 0.84,
    isles: [
      // The skerries a raider coast hides behind...
      { x: 206, y: 218, r: 13, n: 3, run: 74 },
      // ...and one rock alone in the middle of the Sunder, which is what an
      // isle chain exempt from the sea's own push is *for*: land where the
      // field says there should be none, with no road to anywhere, reachable
      // only by ship. Every world wants one and this one has always had two.
      { x: 318, y: 232, r: 9, n: 1, run: 0 },
    ],
  },
  {
    id: 'kaldrhun',
    name: 'Kaldrhûn',
    silhouette: 'a wall of mountains down the whole western edge',
    x: 76,
    y: 254,
    along: 88,
    width: 62,
    curve: -22,
    axis: 92,
    anchors: 6,
    wobble: 13,
    density: 0.98,
    spine: true,
  },
  {
    id: 'hearthreach',
    name: 'The Hearthland Reach',
    silhouette: 'broad and blunt in the east, walled off behind its own spine',
    x: 392,
    y: 226,
    along: 84,
    width: 96,
    curve: 20,
    axis: 100,
    anchors: 6,
    wobble: 13,
    density: 0.88,
    spine: true,
    heart: true,
  },
  {
    id: 'verdance',
    name: 'The Verdance',
    silhouette: 'a green shelf across the south, welded to the Reach at one neck',
    x: 296,
    y: 334,
    along: 96,
    width: 54,
    curve: -16,
    axis: 4,
    anchors: 6,
    wobble: 14,
    density: 1.0,
  },
  {
    id: 'vashtaur',
    name: 'Vashtaur',
    silhouette: 'a great wall of a continent down the western edge, green on one side and bare on the other',
    x: -100,
    y: 200,
    along: 92,
    width: 104,
    curve: 14,
    axis: 92,
    anchors: 7,
    wobble: 16,
    density: 1.0,
    spine: true,
    // Tall enough that the saddles are still mountain: this is the wall that
    // makes the desert behind it, and `RAIN_FIELD` is where that happens.
    spineLift: 1.6,
    isles: [{ x: -22, y: 296, r: 12, n: 3, run: 108 }],
  },
  {
    id: 'hoarfast',
    name: 'The Hoarfast',
    silhouette: 'a second, higher band of ice above the first, and nothing lives on the crest',
    x: 130,
    y: -56,
    along: 186,
    width: 54,
    curve: 14,
    axis: 2,
    anchors: 8,
    wobble: 16,
    density: 1.32,
    spine: true,
    spineLift: 1.3,
  },
  {
    id: 'aelloran',
    name: 'Aelloran',
    silhouette: 'a ring of land about an inner sea, alone in the western ocean',
    x: 118,
    y: 92,
    along: 30,
    width: 56,
    curve: 0,
    axis: 0,
    /**
     * Twelve, not eight. A ring is only a ring while every pair of neighbouring
     * anchors overlaps, and the anchors' radii are hashed off the mass's *index*
     * in `MASSES` — so adding a continent above this one re-rolls the whole
     * chain, and eight anchors leave enough spacing that an unlucky roll opens
     * a strait. Twelve keeps the chain closed on any roll, which is what the
     * realm test's flood-fill actually asserts.
     */
    anchors: 12,
    wobble: 5,
    density: 1.2,
    ring: 46,
    isles: [{ x: 178, y: 112, r: 13, n: 3, run: 66 }],
  },
  /**
   * ── The east, which is the other half of the world ──────────────────────
   *
   * Three masses and one continent. The owner's sketch is a large landmass to
   * the right of everything with a smaller island off its north-east, and the
   * reason it is three declarations rather than one is that a single anchor
   * chain is a sausage: it has a long axis and a short one and nothing else to
   * say. Two chains welded at a neck have a *corner*, and a corner is the
   * cheapest thing on this whole board that makes a continent look drawn — the
   * Nightspur is the same trick used to make a peninsula, and `BRIDGED` is the
   * one line that does it.
   *
   * So Khemadar runs north–south down the east, Orvath runs west–east across
   * its top, the two weld at a neck, and the result is an L a player can point
   * at. Sirrakh sits off the elbow, unbridged and therefore parted from it by
   * the seam rule — an island because nothing says otherwise, which is how an
   * island ought to arrive.
   *
   * **Both bodies carry a spine, and that is what the biomes are made of.**
   * Khemadar's is tall (1.5) and runs its whole length a little off the chain,
   * so the weather off the outer ocean is spent climbing it and the ground in
   * its lee is the driest on the board — a sand sea, with a rain coast on the
   * far side of the same wall. Orvath's is lower (1.15): a temperate continent
   * wants a watershed, not a wall, and the ground each side of it comes out
   * forest and open plain rather than rainforest and desert. Nothing here says
   * "desert" or "jungle"; `RAIN_FIELD` and `terrainAt` say them, and the ranges
   * are the reason they are said where they are.
   */
  {
    id: 'khemadar',
    name: 'Khemadar',
    silhouette: 'the long body of the eastern continent, walled down its length and dry behind the wall',
    x: 716,
    y: 262,
    along: 108,
    width: 140,
    curve: 24,
    axis: 98,
    anchors: 8,
    wobble: 17,
    density: 0.9,
    spine: true,
    // As tall as Vashtaur's wall but a notch shorter, because this one has an
    // ocean on both sides of it and a 1.6 lift would have left nothing but rock
    // between two narrow green rims. 1.58 was tried for a drier lee and gave
    // one back: a taller range is a *wider* one, so the extra sand and the
    // extra rock cancelled and the rain coast lost two holdings.
    spineLift: 1.5,
    isles: [
      // Stepping stones across the Weltering. A hundred-odd units of open water
      // between two halves of a world is a voyage; the same water with three
      // rocks in it is a *route*, and something worth holding.
      { x: 566, y: 268, r: 10, n: 3, run: 96 },
    ],
  },
  {
    id: 'orvath',
    name: 'Orvath',
    silhouette: 'the north arm of the eastern continent, running east off the elbow into cooler ground',
    x: 846,
    y: 168,
    along: 108,
    width: 108,
    curve: -22,
    axis: 12,
    anchors: 7,
    wobble: 16,
    density: 0.94,
    spine: true,
    spineLift: 1.15,
  },
  {
    id: 'sirrakh',
    name: 'Sirrakh',
    silhouette: 'one island alone off the continent’s north-east shoulder, and no road anywhere',
    x: 986,
    y: 66,
    along: 36,
    width: 48,
    curve: 10,
    axis: 46,
    anchors: 4,
    wobble: 9,
    density: 1.1,
    isles: [{ x: 946, y: 112, r: 11, n: 2, run: 130 }],
  },
];

const HEART_MASS = MASSES.findIndex((m) => m.heart === true);


/**
 * A region: a named place inside a continent, and the unit lore is hung on.
 *
 * A region used to *be* a landmass, which was fine when there were eight small
 * ones and useless the moment there were four big ones — "the whole of the
 * western continent" is not a place anybody can write about. So a region is now
 * declared as a **seed**: a name, a character, and a point. Every square of
 * ground goes to the seed nearest it *by walking*, over the same weighted
 * geodesic the provinces themselves are grown by, so a region is bounded by its
 * coast and by the mountain ranges in its way — a peninsula, a basin, the
 * western flank of a spine — and never by a straight line or a bearing.
 *
 * This is deliberately the most re-shapeable table in the file. To move a
 * region, move its seed. To rename one, change one string. To give a continent
 * another kingdom's worth of ground, add a seed on it and the neighbours give
 * way. Nothing else in the pipeline has to be touched, and the owner writing
 * lore per region can have the world reshaped to match it one line at a time.
 *
 * `crowns` is how many generated kingdoms are seated here — *several* per
 * region, which is the owner's standing instruction: a region is a
 * neighbourhood of rivals who may share a race and still want each other's
 * ground, not one wash of colour per people. The contested middle takes none,
 * because it already has the eight authored crowns of the heartland.
 */
interface RegionSpec {
  id: string;
  name: string;
  /** The parenthesised subtitle, the way `MAPREF.md` labels its regions. */
  subtitle: string;
  character: RegionCharacter;
  /** Where its heart is, in map units. Move this line and the region moves. */
  x: number;
  y: number;
  /** Generated kingdoms seated here. */
  crowns: number;
  /**
   * How far this region reaches, against its neighbours. 1 is the default.
   *
   * Ground goes to the nearest seed *by walking*, and on a continent with three
   * seeds on it the one in the middle wins by geometry however little sense that
   * makes: the contested heartland took two thirds of the Reach and the blight
   * behind the passes came out four holdings wide. So a seed may walk faster or
   * slower than its neighbours. This is the dial for *how much of a continent a
   * place is*, and it is a lore decision rather than a geometric one — a small
   * duchy hemmed in by two empires is `0.8`, an empire is `1.3`.
   */
  pull?: number;
  /** Nobody's homeland. Exactly one region says so, and it is the heartland. */
  contested?: boolean;
}

/**
 * ── HOW TO MOVE A PEOPLE, IN ONE LINE ────────────────────────────────────
 *
 * This table is the whole editable surface of the world's *politics*, and it is
 * meant to be edited. Each entry is a name, a subtitle, a **character** and a
 * point on the sheet of paper.
 *
 *   · **To move a region**, change its `x`/`y`. Ground is assigned to the
 *     nearest seed *by walking*, so the neighbours give way and the coasts and
 *     the mountain ranges do the bounding. Nothing else has to change.
 *   · **To move a people**, change a `character`. The dwarves live wherever the
 *     `'mountain'` region is, because `seatRaces` below deals a crown the race
 *     whose `Homeland` matches the ground it stands on. Swap `'mountain'` and
 *     `'forest'` between two lines and the dwarves and the wood elves change
 *     continents, with their kingdoms, their leaders and their banners.
 *   · **To add a region**, add a line. Its neighbours shrink to make room.
 *   · **To make a place busier**, raise `crowns`. Two to five is the range the
 *     board is measured at; `crownsFor` floors it at two and caps it by the
 *     ground actually available.
 *
 * The one rule: **every `Homeland` in `races.ts` needs a region whose character
 * suits it**, or a people has nowhere to be. `HOMELAND_GROUND` states which
 * characters suit which homeland and `tests/realm.test.ts` walks the roster
 * against this table, so deleting the marsh does not silently evict the Litany
 * — it fails a test that names them.
 */
const REGION_SEEDS: readonly RegionSpec[] = [
  // -- The Rimewreath, the ice across the top ------------------------------
  {
    id: 'rimeward',
    name: 'The Rimeward Waste',
    subtitle: 'Beyond the Frozen Gate',
    character: 'waste',
    x: 424,
    y: 56,
    crowns: 2,
  },
  {
    id: 'howling',
    name: 'The Howling Steppe',
    subtitle: 'Grass, and What Runs On It',
    character: 'steppe',
    x: 254,
    y: 76,
    crowns: 2,
  },
  // -- The Nightspur, the knife in the middle of the sea --------------------
  {
    id: 'nightspur',
    name: 'The Nightspur',
    subtitle: 'Harbours of the Long Raid',
    character: 'coast',
    x: 272,
    y: 244,
    crowns: 2,
  },
  // -- Kaldrhun, the western wall ------------------------------------------
  {
    id: 'kalder',
    name: 'Karrun-Dur',
    subtitle: 'Holds Beneath the Peaks',
    character: 'mountain',
    x: 66,
    y: 214,
    crowns: 3,
  },
  {
    id: 'undermarch',
    name: 'The Undermarch',
    subtitle: 'What Was Dug Out From Below',
    character: 'underground',
    x: 96,
    y: 322,
    crowns: 2,
  },
  // -- The Hearthland Reach, the broad east ---------------------------------
  {
    id: 'hearth',
    name: 'The Hearthlands',
    subtitle: 'Contested Marches',
    character: 'heartland',
    x: 350,
    y: 236,
    crowns: 0,
    // The contested middle is *bounded*: the authored twenty-four and a march
    // around them, not two thirds of the continent they stand on.
    pull: 0.9,
    contested: true,
  },
  {
    id: 'greyholt',
    name: 'The Greyholt',
    subtitle: 'Free Cities of the North Reach',
    character: 'temperate',
    x: 428,
    y: 138,
    crowns: 3,
    pull: 1.6,
  },
  {
    id: 'crimson',
    name: 'The Crimson March',
    subtitle: 'Behind the Passes, and Older',
    character: 'blight',
    x: 440,
    y: 302,
    crowns: 2,
    pull: 1.45,
  },
  // -- The Verdance, the green south ---------------------------------------
  {
    id: 'hollowwood',
    name: 'The Hollowwood',
    subtitle: 'One Road In',
    character: 'forest',
    x: 248,
    y: 344,
    crowns: 2,
    pull: 0.9,
  },
  {
    id: 'mire',
    name: 'The Drowned Mire',
    subtitle: 'Plague Fens',
    character: 'marsh',
    x: 362,
    y: 350,
    crowns: 2,
    pull: 1.05,
  },
  // -- Vashtaur, the western wall, banded by the rain ----------------------
  //
  // Three seeds on one line of latitude and the geography does the rest: the
  // walk west is dear across the spine, so the mountain seed takes everything
  // beyond it for the whole height of the continent, and the two seeds east of
  // it divide the rest into a wet seaboard and a dry middle. Nothing here says
  // "band"; `RAIN_FIELD` and `terrainAt` say jungle, desert and mountain, and
  // the bands are what that comes out as.
  {
    id: 'greenmaw',
    name: 'The Greenmaw',
    subtitle: 'Rain, and What Grows In It',
    character: 'jungle',
    x: -18,
    y: 204,
    crowns: 3,
    pull: 1.04,
  },
  {
    id: 'thirst',
    name: 'The Long Thirst',
    subtitle: 'Three Wells and a Road',
    character: 'desert',
    x: -74,
    y: 198,
    crowns: 3,
    pull: 1.02,
  },
  {
    id: 'cloudbreak',
    name: 'The Cloudbreakers',
    subtitle: 'Tall Mountains, Four Passes',
    character: 'mountain',
    x: -142,
    y: 202,
    crowns: 3,
    pull: 1.16,
  },
  // -- The Hoarfast, above the ice -----------------------------------------
  {
    id: 'hoarfast',
    name: 'The Hoarfast',
    subtitle: 'Mountains of Eternal Winter',
    character: 'frozen',
    x: 140,
    y: -58,
    crowns: 3,
  },
  // -- Aelloran, the ring ---------------------------------------------------
  {
    id: 'aelloran',
    name: 'Aelloran',
    subtitle: 'The Ringed Realms',
    character: 'isles',
    x: 112,
    y: 44,
    crowns: 2,
  },
  // -- Khemadar and Orvath, the eastern continent --------------------------
  //
  // Eight seeds on one landmass, and the same trick Vashtaur is built on: put
  // three of them on a line running *across* the continent and let the walking
  // do the rest. The crossing is dear over Khemadar's wall, so the seed west of
  // it takes the rain coast for the whole length of the range, and the ones
  // east of it divide the lee into sand and, further out where the weather
  // reaches again, green. Nothing here says "band". `RAIN_FIELD` says how far
  // the weather got, `terrainAt` turns that into jungle, sand, forest and
  // plain, and the wall standing over the sand is the reason the sand is there.
  {
    id: 'sunfast',
    name: 'The Sunfast Coast',
    subtitle: 'Where the Rain Stops',
    character: 'jungle',
    x: 624,
    y: 286,
    crowns: 3,
    // Hemmed against its own wall on purpose: this is a strip of shore, not a
    // province of the interior, and the mountain seed beside it should have the
    // high ground rather than have to share it.
    pull: 0.9,
  },
  {
    id: 'khemadar',
    name: 'The Khemad Wall',
    subtitle: 'The Long Spine, and Its Saddles',
    character: 'mountain',
    x: 662,
    y: 166,
    crowns: 3,
    pull: 1.08,
  },
  {
    id: 'sandsea',
    name: 'The Sand Sea',
    subtitle: 'A Hundred Leagues Without a Well',
    character: 'desert',
    x: 734,
    y: 218,
    crowns: 3,
    pull: 1.22,
  },
  /**
   * The other half of the same sand, and it is a second region rather than a
   * bigger first one for the reason the Farholt is: fifty holdings under one
   * name is not a neighbourhood, it is a wall of text in the panel. Nothing
   * changes across the line — `terrainAt` is reading the same rainfall on both
   * sides — so what makes them two places is that they are two sets of rivals.
   *
   * `volcanic` was tried here for `MAPREF.md`'s best annotation, *ruins of a
   * shattered empire buried beneath endless sands*, and it was worse rather
   * than better: the ridge gates in `terrainAt` fire before the character
   * switch, so a third of the region went to hill and mountain and only three
   * holdings in eighteen came out ruin, while the board lost four of sand.
   * The buried empire is in the subtitle, where it costs nothing and reads.
   */
  {
    id: 'glasswaste',
    name: 'The Glass Waste',
    subtitle: 'Where an Empire Is Buried',
    character: 'desert',
    x: 764,
    y: 278,
    crowns: 3,
    pull: 1.18,
  },
  {
    id: 'tidemouth',
    name: 'The Tidemouth',
    subtitle: 'Where the Sand Runs Out',
    character: 'marsh',
    x: 748,
    y: 336,
    crowns: 3,
    pull: 0.95,
  },
  {
    id: 'orvath',
    name: 'Orvath',
    subtitle: 'Woods Above the Wall',
    character: 'forest',
    x: 800,
    y: 142,
    crowns: 3,
    pull: 1.0,
  },
  /**
   * The continent's hostile northern rim, and a harbour coast rather than a
   * waste — which is the more interesting of the two things a periphery can be.
   * `wasteness` is already turning the crest above it to ice without anybody
   * asking, so the ground says "edge of the world" on its own; what this seed
   * adds is somebody living on it, in the fjords, with ships.
   */
  {
    id: 'coldward',
    name: 'The Coldward Shore',
    subtitle: 'Harbours Under the Ice',
    character: 'coast',
    x: 826,
    y: 56,
    crowns: 3,
    pull: 0.95,
  },
  {
    id: 'gulfgreen',
    name: 'The Verdant Gulf',
    subtitle: 'Rain, Twice a Day, For Ever',
    character: 'jungle',
    x: 878,
    y: 254,
    crowns: 3,
    pull: 1.0,
  },
  {
    id: 'longveld',
    name: 'The Long Veld',
    subtitle: 'Grass to the Eastern Water',
    character: 'steppe',
    x: 920,
    y: 176,
    crowns: 3,
    pull: 1.0,
  },
  /**
   * The one place on the new continent where the *old* world's peoples have
   * put anything down, and it is on the last shore rather than in the middle —
   * which is what a colony is. `temperate` is the Greyholt's character too, so
   * `GROUND_HOMELAND` seats men here, and the Farholt and the Greyholt are the
   * two ends of the same long argument about who the Ashen answer to.
   *
   * It is also what stopped the Long Veld being a region of sixty holdings.
   * Nothing on this table is allowed to grow past about thirty-five: a region
   * is the unit a player actually holds in their head, and the moment one of
   * them stops fitting, the answer is another seed rather than a bigger one.
   */
  {
    id: 'farholt',
    name: 'The Farholt',
    subtitle: 'Cities of the Last Shore',
    character: 'temperate',
    x: 1002,
    y: 178,
    crowns: 3,
    pull: 1.0,
  },
  {
    id: 'sirrakh',
    name: 'Sirrakh',
    subtitle: 'One Island, and No Road Off It',
    character: 'isles',
    x: 986,
    y: 70,
    crowns: 2,
  },
];

const HEART_REGION = REGION_SEEDS.findIndex((r) => r.contested === true);

/**
 * ── WHERE EACH PEOPLE BELONGS ────────────────────────────────────────────
 *
 * The one table that marries `races.ts` to the map, and the half of the world
 * that was declared on both sides and joined on neither: `RaceDef.homeland`
 * said what country a people is from, `RegionSpec.character` said what country
 * this is, and nothing anywhere read both. Eleven peoples existed and the board
 * seated their *factions* round-robin, so the dwarves opened in the fens as
 * often as in the peaks and the world was eleven colours sprinkled on a board.
 *
 * Read left to right this says **which ground suits a people**: the first entry
 * is its proper country and the rest are the frontiers it will settle for. Read
 * the other way (`GROUND_HOMELAND`) it says **whose country a piece of ground
 * is**, which is what a region needs to know to hand a crown to the right
 * people. Both are total — a `Record` over every key of both vocabularies — so
 * a homeland with nowhere to live, or ground nobody is from, is a type error
 * rather than a people quietly missing from the world.
 */
const HOMELAND_GROUND: Record<Homeland, readonly RegionCharacter[]> = {
  heartland: ['heartland', 'temperate'],
  /**
   * **`forest` used to read `['forest', 'jungle']` and `steppe` used to read
   * `['steppe', 'desert']`, and both of those were the roster admitting it had
   * nobody for the wetter and drier halves of its own countries.**
   *
   * A wood elf will settle a rainforest when there is no rainforest people, and
   * a beastkin will cross a sand sea when there is nobody who lives in one. Now
   * that the Long Noon and the Thirstless exist, keeping the old lines would
   * mean the Choir and the Moonbound *also* named ground that is somebody
   * else's proper country — and `raceForSeat` reads `region.homeland`, not this
   * table, so the only thing the extra entry would still do is make the
   * start-up check pass for the wrong reason. Each people names its own country
   * and the frontiers it will genuinely settle for, which is what this table was
   * always for.
   */
  forest: ['forest'],
  jungle: ['jungle'],
  mountain: ['mountain'],
  coast: ['coast'],
  isles: ['isles'],
  waste: ['waste', 'frozen'],
  marsh: ['marsh'],
  steppe: ['steppe'],
  desert: ['desert'],
  underground: ['underground', 'volcanic'],
  blight: ['blight'],
};

/** Whose country this ground is. The inverse of the table above. */
const GROUND_HOMELAND: Record<RegionCharacter, Homeland> = {
  heartland: 'heartland',
  temperate: 'heartland',
  forest: 'forest',
  /**
   * This line used to read `jungle: 'forest'`, with a comment arguing that a
   * rainforest is a forest and the peoples who belong in one belong in the
   * other. That was true of the roster rather than of the world, and it stopped
   * being true of the roster: the Long Noon belong in a rainforest and nowhere
   * else, and a jungle that answers 'forest' would have seated wood elves on
   * top of them. Same for the sand, one line down.
   */
  jungle: 'jungle',
  mountain: 'mountain',
  coast: 'coast',
  isles: 'isles',
  waste: 'waste',
  frozen: 'waste',
  desert: 'desert',
  marsh: 'marsh',
  steppe: 'steppe',
  underground: 'underground',
  volcanic: 'underground',
  blight: 'blight',
};

/**
 * Every people has somewhere to be, checked at start-up rather than hoped for.
 *
 * `HOMELAND_GROUND` being total says each homeland *names* ground; it does not
 * say the world contains any. This does, against the table the world is
 * actually built from, and it throws rather than shipping a roster with a
 * people who cannot be seated anywhere. It is eleven lookups over eleven lines
 * and it runs once.
 */
(() => {
  const have = new Set<RegionCharacter>(REGION_SEEDS.map((r) => r.character));
  const homeless = (Object.keys(HOMELAND_GROUND) as Homeland[]).filter(
    (h) => !HOMELAND_GROUND[h].some((c) => have.has(c)),
  );
  if (homeless.length > 0) {
    throw new Error(
      `REGION_SEEDS has no ground for ${homeless.join(', ')} — ` +
        `every Homeland in races.ts needs a region whose character suits it.`,
    );
  }
})();


/**
 * Water dug back out of the land: gulfs, estuaries, and one inland sea.
 *
 * A coastline made only of the *outside* of a blob chain is an outline. What
 * makes a shore look drawn is the water that reaches *into* the land — a gulf
 * that nearly cuts a continent in half, an estuary that turns a river mouth into
 * forty miles of navigable coast, an inland sea with a neck a fleet could cork.
 * Each of these is a negative anchor in the same field, so it is bitten by the
 * same warp and the same noise as the coast it opens off, and its mouth is
 * whatever the two shapes leave between them rather than a number typed here.
 */
interface GulfSpec {
  x: number;
  y: number;
  r: number;
  depth: number;
}

const GULFS: readonly GulfSpec[] = [
  // The drowned coast the Hearthlands' own text names: The Wake, Saltmarch and
  // Drownfoot stand on this, on the Reach's western shore.
  { x: 348, y: 206, r: 30, depth: 1.15 },
  { x: 366, y: 282, r: 26, depth: 1.05 },
  // The gulf that bites the Greyholt off the top of the Reach.
  { x: 448, y: 194, r: 30, depth: 1.1 },
  // A great gulf nearly severing Kaldrhun, and the narrows behind it.
  { x: 104, y: 274, r: 34, depth: 1.2 },
  { x: 48, y: 322, r: 26, depth: 1.05 },
  // An inland sea behind Kaldrhun's northern flank, with a neck to the ocean.
  { x: 66, y: 190, r: 26, depth: 1.3 },
  // Fjords along the rimeward ice, and the bay that divides the grass from it.
  { x: 300, y: 46, r: 26, depth: 1.1 },
  { x: 414, y: 34, r: 24, depth: 1.05 },
  // The bay that makes the Verdance a shelf rather than a slab, and the fen
  // mouth at its eastern end.
  { x: 286, y: 302, r: 28, depth: 1.1 },
  { x: 398, y: 366, r: 24, depth: 1.05 },
  // The notches that make the Spur a spur rather than a sausage: a raider
  // coast is all harbours, and a harbour is a bite out of a headland.
  { x: 312, y: 168, r: 15, depth: 0.95 },
  { x: 228, y: 244, r: 15, depth: 0.95 },
  // ...and the inner sea of Aelloran, which is what makes it a ring.
  { x: 118, y: 92, r: 34, depth: 2.6 },
  // ── The east ────────────────────────────────────────────────────────────
  // The great bight in Khemadar's western shore, which is what stops the
  // eastern continent reading as a slab: the rain coast is a curved one, and
  // the water in the curve is the first thing a fleet out of the Weltering
  // finds.
  { x: 632, y: 214, r: 32, depth: 1.15 },
  // The gulf under the elbow — the water Khemadar and Orvath fold around, and
  // the reason the neck between them is a neck rather than a shoulder.
  { x: 712, y: 118, r: 30, depth: 1.1 },
  // Two bites out of Orvath's north coast: fjord country above the forests.
  { x: 798, y: 48, r: 27, depth: 1.05 },
  { x: 944, y: 84, r: 24, depth: 1.0 },
  // ...and the great gulf in the inner corner of the L, where Khemadar's east
  // coast and Orvath's south coast fold toward one another.
  { x: 916, y: 250, r: 27, depth: 1.1 },
  // The long estuary at Khemadar's southern end, where the sand runs out into
  // fen and the continent stops being one piece of ground.
  { x: 734, y: 352, r: 28, depth: 1.1 },
];

/**
 * The great seas, declared as *shapes with places* rather than as a hub.
 *
 * This replaces `midseaPush`, which was a disc at the exact middle of the board
 * that every landmass was forbidden to grow into. That single function was the
 * radial composition: it made the centre of the sheet the one thing the world
 * was organised about, and the continents could not help reading as a wreath
 * around it however they were tuned. It was also the reason the Spur below could
 * not exist — a peninsula reaching for the middle of the map would have been
 * drowned by the very field that made the map look symmetrical.
 *
 * So there are two seas, they are different sizes, neither is centred, and the
 * Sable Spur runs down between them. Each is a chain of negative anchors read
 * through the same domain warp as the land, so its shore has the same bights and
 * headlands the outer coast has, and where a sea chain overlaps a mass the sea
 * wins — that is how an open coast gets carved back into a bay.
 *
 *   · **The Sunder** — the wide eastern basin, the trade spine and the invasion
 *     highway, ringed by the Reach, the Verdance, the Rimewreath and the Spur.
 *   · **The Gale** — the narrower western water between Kaldrhûn, Aelloran and
 *     the Spur's landward flank. Colder, poorer, and the reason the Sable
 *     harbours face east.
 *   · **The Weltering** — the long north–south ocean that parts the old world
 *     from the new one. It is the only sea on the board declared for a
 *     *negative* reason as well as a positive one: without it the eastern rim's
 *     pressure lifts off the Hearthland Reach's and the Rimewreath's coasts the
 *     moment the paper widens, and two shores that were supposed to stay
 *     exactly where they were grow twenty or thirty units out into the new
 *     ocean. It holds them, it opens the strait, and — because a sea here is a
 *     mass with the sign flipped and is read through the same warp — its two
 *     shores are bitten by the same noise, so neither of them reads as a wall.
 */
interface SeaSpec {
  id: string;
  name: string;
  x: number;
  y: number;
  along: number;
  /** Bearing the chain runs along, zero east. */
  axis: number;
  width: number;
  anchors: number;
  push: number;
}

const SEAS: readonly SeaSpec[] = [
  {
    id: 'sunder',
    name: 'The Sunder',
    x: 338,
    y: 216,
    along: 40,
    axis: 68,
    width: 64,
    anchors: 4,
    push: 2.4,
  },
  {
    id: 'gale',
    name: 'The Gale',
    x: 184,
    y: 198,
    along: 48,
    axis: 100,
    width: 56,
    anchors: 4,
    push: 2.2,
  },
  {
    id: 'welter',
    name: 'The Weltering',
    x: 550,
    y: 204,
    // Long, because it has to run the whole height of the board: a channel that
    // stops short of either pole is a channel with a land bridge at the end of
    // it, and the two halves of the world would be one walk again.
    along: 176,
    axis: 92,
    width: 62,
    anchors: 6,
    push: 2.3,
  },
];

/**
 * The Sunder, by name, for the one caller that needs to point at it rather than
 * at the middle of the sheet of paper. See `seatHeartland`.
 */
const SEA_SUNDER = SEAS.find((s) => s.id === 'sunder') ?? SEAS[0];

/**
 * The pairs of landmasses allowed to grow into one another. Everything else is
 * parted by water, and the parting is *derived*.
 *
 * Two masses whose fields overlap would simply weld into one continent, and on
 * eight masses round a middle sea nearly all of them do — which is how a world
 * that is supposed to be in pieces quietly becomes one landmass again. So
 * wherever two masses both have a real claim on the same ground, the weaker
 * claim is *subtracted* rather than added: the seam between them floods. The
 * width of the resulting channel is whatever the two shapes leave, so a strait
 * pinches where the masses nearly touch and opens where they do not, which is
 * the shape a strait has and the reason one is worth holding a fleet in.
 *
 * A short list is exempt, because a world where every frontier is a coastline
 * has no land wars in it. Two peoples who share a land border are a different
 * strategic problem from two who share a channel, and the board wants some of
 * each.
 */
const BRIDGED: ReadonlyArray<readonly [string, string]> = [
  // **This is how a peninsula is made.** The Spur is declared as its own chain
  // — long, thin and bowed — and then permitted to weld to the mass it hangs
  // off. Nothing draws the join: the two fields simply add where they overlap,
  // the warp bites the result, and what comes out is a neck a few provinces
  // wide with a hundred and forty units of peninsula south of it.
  ['spur', 'rimewreath'],
  // The one land frontier between two *continents* on the board, and it is the
  // world's most valuable ground: the isthmus where the Verdance runs up into
  // the Hearthland Reach.
  ['verdance', 'hearthreach'],
  // **The elbow.** Khemadar runs north–south and Orvath runs west–east, and
  // this line is the only thing that makes them one continent rather than two
  // masses in a strait. Everything else about the east is parted by the seam
  // rule for free: Sirrakh is not listed, so it is an island, and neither of
  // these is listed against anything in the west, so the Weltering stays open.
  ['khemadar', 'orvath'],
];

const BRIDGE_PAIRS = new Set<number>(
  BRIDGED.map(([a, b]) => {
    const ia = MASSES.findIndex((m) => m.id === a);
    const ib = MASSES.findIndex((m) => m.id === b);
    return Math.min(ia, ib) * 64 + Math.max(ia, ib);
  }),
);

/** How hard a seam between two unbridged masses is flooded. */
const SEAM_PUSH = 2.1;

/** The land field's threshold. Anything above it is dry. */
const LAND_CUT = 0.335;

/**
 * The domain warp — the single most important function in this file.
 *
 * Three octaves of displacement applied to the plane *before* the land field is
 * read. A circle evaluated through this is not a circle: the long octave drags
 * whole flanks of a continent forty units sideways and opens gulfs where two
 * masses were nearly touching, the middle one grows headlands and inlets at the
 * scale of a province, and the short one crinkles the shore at the scale of the
 * raster. Multi-scale detail on the contour is what makes a coast read as drawn
 * rather than as computed, and it costs six noise lookups.
 */
function warpAt(x: number, y: number): { x: number; y: number } {
  const w1x = (fbmN(x * 0.0074, y * 0.0074, REALM_SEED + 11, 2) - 0.5) * 2 * 28;
  const w1y = (fbmN(x * 0.0074 + 40, y * 0.0074 + 40, REALM_SEED + 23, 2) - 0.5) * 2 * 28;
  const w2x = (vnoise(x * 0.026 + 7, y * 0.026 + 7, REALM_SEED + 37) - 0.5) * 2 * 11;
  const w2y = (vnoise(x * 0.026 + 61, y * 0.026 + 61, REALM_SEED + 41) - 0.5) * 2 * 11;
  const w3x = (vnoise(x * 0.077 + 3, y * 0.077 + 3, REALM_SEED + 53) - 0.5) * 2 * 2.6;
  const w3y = (vnoise(x * 0.077 + 29, y * 0.077 + 29, REALM_SEED + 59) - 0.5) * 2 * 2.6;
  return { x: x + w1x + w2x + w3x, y: y + w1y + w2y + w3y };
}

interface Anchor {
  x: number;
  y: number;
  r: number;
}

/** A unit vector along a bearing, zero east. Every chain runs on one of these. */
function heading(deg: number): { x: number; y: number } {
  const t = (deg * Math.PI) / 180;
  return { x: Math.cos(t), y: Math.sin(t) };
}

/** Wyvill falloff: 1 at the middle, 0 at the rim, and smooth at both. */
function blob(x: number, y: number, a: Anchor): number {
  const dx = x - a.x;
  const dy = y - a.y;
  const q = (dx * dx + dy * dy) / (a.r * a.r);
  if (q >= 1) return 0;
  const s = 1 - q;
  return s * s;
}

/** The anchor chains, resolved once. */
const MASS_ANCHORS: Anchor[][] = MASSES.map((spec, si) => {
  const mid = { x: spec.x, y: spec.y };
  if (spec.ring !== undefined) {
    // A ring continent: the anchors go round a circle instead of along a chain,
    // and the water in the middle is a gulf declared at the same centre. One
    // memorable silhouette is worth twenty anonymous islets.
    const out: Anchor[] = [];
    const n = Math.max(6, spec.anchors);
    for (let k = 0; k < n; k++) {
      const t = (k / n) * Math.PI * 2;
      const r = spec.ring * (0.86 + hash2(si, k, 7757) * 0.3);
      out.push({
        x: mid.x + Math.cos(t) * r * 1.15,
        y: mid.y + Math.sin(t) * r,
        r: spec.width * (0.4 + hash2(si, k + 40, 7759) * 0.3),
      });
    }
    return out;
  }
  const u = heading(spec.axis);
  const ux = u.x;
  const uy = u.y;
  const nx = -uy;
  const ny = ux;
  const out: Anchor[] = [];
  for (let k = 0; k < spec.anchors; k++) {
    const t = spec.anchors === 1 ? 0 : (k / (spec.anchors - 1) - 0.5) * 2;
    const bow = spec.curve * (1 - t * t);
    const jx = (hash2(si, k, 7717) - 0.5) * 2 * spec.wobble;
    const jy = (hash2(si, k + 40, 7727) - 0.5) * 2 * spec.wobble;
    out.push({
      x: mid.x + ux * t * spec.along + nx * bow + jx,
      y: mid.y + uy * t * spec.along + ny * bow + jy,
      // Tapered toward the ends, so a continent has capes rather than two blunt
      // stops, and scaled by a hash so no two anchors are the same disc.
      r: spec.width * (1 - 0.38 * t * t) * (0.76 + hash2(si, k + 80, 7741) * 0.5),
    });
  }
  return out;
});

/** The isle chains, resolved once, keyed by the mass they belong to. */
const ISLE_ANCHORS: Anchor[][] = MASSES.map((spec, si) => {
  const out: Anchor[] = [];
  (spec.isles ?? []).forEach((isle, ii) => {
    const mid = { x: isle.x, y: isle.y };
    const t = (isle.run * Math.PI) / 180;
    for (let k = 0; k < isle.n; k++) {
      const step = (k - (isle.n - 1) / 2) * isle.r * 2.3;
      out.push({
        x: mid.x + Math.cos(t) * step + (hash2(si * 17 + ii, k, 6421) - 0.5) * 2 * 5,
        y: mid.y + Math.sin(t) * step + (hash2(si * 17 + ii, k + 30, 6427) - 0.5) * 2 * 5,
        r: isle.r * (0.72 + hash2(si * 17 + ii, k + 60, 6449) * 0.62),
      });
    }
  });
  return out;
});

const GULF_ANCHORS: Array<Anchor & { depth: number }> = GULFS.map((g) => ({
  x: g.x,
  y: g.y,
  r: g.r,
  depth: g.depth,
}));

/**
 * The seas, resolved into negative anchors, exactly as a mass is resolved into
 * positive ones. A sea is a landmass with the sign flipped, which is the whole
 * of the idea and the reason it can be *anywhere*.
 */
const SEA_ANCHORS: Array<Anchor & { push: number }> = SEAS.flatMap((spec, si) => {
  const u = heading(spec.axis);
  const out: Array<Anchor & { push: number }> = [];
  for (let k = 0; k < spec.anchors; k++) {
    const t = spec.anchors === 1 ? 0 : (k / (spec.anchors - 1) - 0.5) * 2;
    out.push({
      x: spec.x + u.x * t * spec.along + (hash2(si + 90, k, 8161) - 0.5) * 2 * 9,
      y: spec.y + u.y * t * spec.along + (hash2(si + 90, k + 40, 8167) - 0.5) * 2 * 9,
      r: spec.width * (1 - 0.24 * t * t) * (0.82 + hash2(si + 90, k + 80, 8171) * 0.36),
      push: spec.push,
    });
  }
  return out;
});

/**
 * The seas, as a push *down* on the land field.
 *
 * The same sum-of-falloffs the land is built from, subtracted instead of added
 * and read at the same warped point, so a sea's shore is bitten by exactly the
 * noise the outer coast is and the two cannot be told apart by looking. Where a
 * sea anchor overlaps a mass the water wins, which is how the Sunder carves the
 * Reach's western shore into bays rather than merely stopping short of it.
 */
function seaPush(wx: number, wy: number): number {
  let v = 0;
  for (const a of SEA_ANCHORS) v += a.push * blob(wx, wy, a);
  return v;
}

/** The rim of the sheet is always ocean, so the world is never a rectangle. */
/**
 * How wide the band of guaranteed ocean is, in **map units from the sheet's own
 * edge** rather than as a fraction of its half-extent.
 *
 * The fraction was the same thing on a fixed sheet and a different thing the
 * moment the sheet grew: nine tenths of a wider half-extent is further out, so
 * widening the paper to the west quietly moved the *eastern* rim twenty units
 * off the Hearthland Reach's coast and regrew a shore that was supposed to stay
 * exactly where it was. Twenty-five units is what the old fraction came to on
 * the old sheet, so this is the same rim, said in a way that does not move.
 */
const RIM_MARGIN = 25;

function rimPush(x: number, y: number): number {
  const west = WORLD.originX + RIM_MARGIN - x;
  const east = x - (WORLD.originX + WORLD.w - RIM_MARGIN);
  const north = WORLD.originY + RIM_MARGIN - y;
  const south = y - (WORLD.originY + WORLD.h - RIM_MARGIN);
  const over = Math.max(west, east, north, south);
  if (over <= 0) return 0;
  const t = Math.min(1, over / RIM_MARGIN);
  return 3.4 * t * t;
}

/**
 * The land field at a point: how dry it is, and which mass says so.
 *
 * Everything is read at the warped point except the rim, which is a property of
 * the sheet of paper rather than of the world.
 */
function landAt(x: number, y: number): { v: number; mass: number; isle: boolean } {
  const w = warpAt(x, y);
  let cont = 0;
  let best = 0;
  let bestMass = 0;
  let second = 0;
  let secondMass = -1;
  for (let m = 0; m < MASS_ANCHORS.length; m++) {
    let s = 0;
    for (const a of MASS_ANCHORS[m]) s += blob(w.x, w.y, a);
    for (const a of ISLE_ANCHORS[m]) s += blob(w.x, w.y, a) * 1.35;
    if (s > best) {
      second = best;
      secondMass = bestMass;
      best = s;
      bestMass = m;
    } else if (s > second) {
      second = s;
      secondMass = m;
    }
    cont += s;
  }
  // The seam between two masses floods, unless the two are bridged.
  const seam =
    secondMass >= 0 && second > 0 && !BRIDGE_PAIRS.has(Math.min(bestMass, secondMass) * 64 + Math.max(bestMass, secondMass))
      ? SEAM_PUSH * second
      : 0;
  let dug = 0;
  for (const g of GULF_ANCHORS) dug += g.depth * blob(w.x, w.y, g);
  const rim = rimPush(x, y);
  // The isles are exempt from everything that digs water out, which is what
  // makes an archipelago a promise rather than a hope.
  let isle = 0;
  for (let m = 0; m < ISLE_ANCHORS.length; m++) {
    for (const a of ISLE_ANCHORS[m]) isle += blob(w.x, w.y, a) * 1.6;
  }
  const grain = (fbmN(x * 0.0265, y * 0.0265, REALM_SEED + 3, 4) - 0.5) * 0.52;
  const open = cont - seam - dug - seaPush(w.x, w.y) - rim + grain;
  const rock = isle - rim + grain * 0.7;
  /**
   * `isle` is set where the ground is standing on a *declared* archipelago
   * rather than on a continent, and the island cull reads it.
   *
   * It cannot be recovered afterwards by looking at where the anchors were
   * written down: the whole field is read through a thirty-unit domain warp, so
   * an isle declared at one point comes out as land forty units away, and
   * "which component contains the anchor's centre" answers about open water
   * most of the time. The field is the only thing that knows.
   */
  return { v: Math.max(open, rock), mass: bestMass, isle: rock > open };
}

// ── Ranges, and the passes through them ────────────────────────────────────

/**
 * A mountain range is a *line*, not a patch of high noise.
 *
 * Thresholding a noise field high gives mountain blobs, and you walk round a
 * blob. The lesson the owner took from the Old World map is that geography is
 * strategy — a range matters because there are three ways through it and
 * everybody fights over them — and that only happens if the high ground runs in
 * a chain from one coast of a landmass to the other.
 *
 * So each mass carries one or two ranges, declared as a polyline drawn *across*
 * its chain rather than along it: the range cuts the continent in two. A
 * lengthwise modulation runs along the line, so the crest rises and falls — and
 * the low places in that modulation are where the passes come out, because the
 * link builder is told to spare a crest crossing in proportion to how low the
 * crest is there. A saddle is a pass. That is the rule, and it is one line.
 */
export interface Range {
  pts: Array<{ x: number; y: number }>;
  /** Half-width of the high ground, in map units. */
  half: number;
  /** Which mass it belongs to, for the record. */
  mass: number;
  seed: number;
  /**
   * How high this range stands against the others, 1 being ordinary.
   *
   * The crest of a range rises and falls along its length and the lows are the
   * passes; `lift` multiplies that whole profile. A range at 1.35 is high enough
   * that its *saddles* are still mountain, which is what "tall mountains" means
   * on a board where a mountain is a province: not a taller glyph, but fewer
   * ways through and a wider band of ground nobody farms.
   */
  lift: number;
}

const RANGES: Range[] = (() => {
  const out: Range[] = [];
  MASSES.forEach((spec, si) => {
    const chain = MASS_ANCHORS[si];
    if (spec.ring !== undefined) return;

    /**
     * The great spine: a range running the *length* of the continent.
     *
     * This is the Worlds Edge, and it is the single most strategic line on a map
     * of this shape. It follows the anchor chain, offset a little to one side so
     * the continent is divided into a broad half and a narrow one rather than
     * into two equal strips, and it runs from near one end to near the other so
     * there is no walking round it. Everything about whether it can be *crossed*
     * is decided elsewhere, by the saddles.
     */
    if (spec.spine) {
      const pts: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < chain.length; i++) {
        const a = chain[i];
        const b = chain[Math.min(chain.length - 1, i + 1)];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.max(1e-6, Math.hypot(dx, dy));
        const off = spec.width * (0.18 + hash2(si, i, 5189) * 0.34);
        pts.push({ x: a.x + (-dy / len) * off, y: a.y + (dx / len) * off });
      }
      out.push({
        pts,
        half: (21 + hash2(si, 0, 5179) * 6) * (spec.spineLift ?? 1),
        mass: si,
        seed: si * 31,
        lift: spec.spineLift ?? 1,
      });
    }

    // …and one or two shorter ranges thrown across it, which is what turns a
    // continent with a spine into a continent with *corners*.
    const count = spec.along > 110 ? 2 : 1;
    for (let k = 0; k < count; k++) {
      const t = count === 1 ? 0.5 : 0.28 + k * 0.44;
      const i = Math.min(chain.length - 2, Math.floor(t * (chain.length - 1)));
      const fr = t * (chain.length - 1) - i;
      const a = chain[i];
      const b = chain[i + 1];
      const mx = a.x + (b.x - a.x) * fr;
      const my = a.y + (b.y - a.y) * fr;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.max(1e-6, Math.hypot(dx, dy));
      const nx = -dy / len;
      const ny = dx / len;
      const reach = spec.width * 1.5;
      const pts: Array<{ x: number; y: number }> = [];
      for (let step = 0; step <= 4; step++) {
        const u = (step / 4 - 0.5) * 2;
        const bend = (hash2(si * 13 + k, step, 5171) - 0.5) * 2 * 18;
        pts.push({
          x: mx + nx * u * reach + (dx / len) * bend,
          y: my + ny * u * reach + (dy / len) * bend,
        });
      }
      out.push({
        pts,
        half: 15 + hash2(si, k + 9, 5179) * 5,
        mass: si,
        seed: si * 31 + k + 1,
        lift: 1,
      });
    }
  });
  return out;
})();

function segDistance(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): { d: number; t: number } {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / len2)) : 0;
  return { d: Math.hypot(px - (ax + vx * t), py - (ay + vy * t)), t };
}

/**
 * How high the ground stands on the ranges, 0..1, and how strong the crest is
 * where it stands. The second number is what decides where a pass can be.
 */
function ridgeExact(x: number, y: number): { h: number; crest: number } {
  let best = 0;
  let crest = 1;
  for (const r of RANGES) {
    for (let i = 0; i < r.pts.length - 1; i++) {
      const a = r.pts[i];
      const b = r.pts[i + 1];
      const hit = segDistance(x, y, a.x, a.y, b.x, b.y);
      if (hit.d > r.half * 1.6) continue;
      // Along the range, the crest rises and falls. The lows are the saddles.
      const along = (i + hit.t) / (r.pts.length - 1);
      const strength = (0.42 + vnoise(along * 5.4 + 3, r.seed * 1.7, REALM_SEED + 733) * 0.58) * r.lift;
      const k = Math.max(0, 1 - hit.d / (r.half * 1.6));
      const h = k * k * strength;
      if (h > best) {
        best = h;
        crest = strength;
      }
    }
  }
  return { h: best, crest };
}

/**
 * …and the same thing, read off a table.
 *
 * `ridgeExact` walks every segment of every range, which is fifty-odd distance
 * calculations, and the ranges are asked about once per raster cell for the
 * elevation, once per cell again for the cost of walking it, and once per
 * candidate site per iteration of the spacing solver. That is nine million
 * segment tests and most of a second of the game's start-up, for a field that is
 * smooth at a scale of ten units and is being sampled at two.
 *
 * So it is computed once, over the raster, and read back. The table is the same
 * field; nothing that asks for it can tell, because nothing that asks for it
 * cares about detail finer than a raster cell.
 */
const RIDGE_FIELD: { h: Float32Array; crest: Float32Array } = (() => {
  const h = new Float32Array(COLS * ROWS);
  const crest = new Float32Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const hit = ridgeExact(cellX(col), cellY(row));
      const k = row * COLS + col;
      h[k] = hit.h;
      crest[k] = hit.crest;
    }
  }
  return { h, crest };
})();

/**
 * The whole board's elevation, computed once over the raster.
 *
 * `elevationAt` is cheap but not free — it is a ridge lookup plus three octaves
 * of fbm — and the hillshade reads every cell and its two neighbours, so a
 * per-pixel call would be three hundred thousand fbm evaluations at boot. The
 * ridge field beside it is already precomputed for exactly this reason; this
 * follows it.
 */
const HEIGHT_FIELD: Float32Array = (() => {
  const h = new Float32Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      h[row * COLS + col] = elevationAt(cellX(col), cellY(row));
    }
  }
  return h;
})();

function ridgeAt(x: number, y: number): { h: number; crest: number } {
  const col = Math.max(0, Math.min(COLS - 1, Math.floor((x - WORLD.originX) / WORLD.cell)));
  const row = Math.max(0, Math.min(ROWS - 1, Math.floor((y - WORLD.originY) / WORLD.cell)));
  const k = row * COLS + col;
  return { h: RIDGE_FIELD.h[k], crest: RIDGE_FIELD.crest[k] };
}

/** Elevation, 0..1: the ranges, plus enough noise that the rest is not flat. */
function elevationAt(x: number, y: number): number {
  const r = ridgeAt(x, y);
  return 0.26 + r.h * 0.72 + (fbmN(x * 0.019, y * 0.019, REALM_SEED + 101, 3) - 0.5) * 0.44;
}

/**
 * How wet the ground is: **how far it is from the sea, with the mountains in
 * the way counted twice.**
 *
 * This was three octaves of noise and nothing else, which is why every continent
 * on the last board was damp in patches rather than wet at the edges and dry in
 * the middle. The owner asked for a western continent banded jungle, then
 * desert, then tall mountains — and the honest way to get bands is not to paint
 * them. It is to say where the water comes from and let the ground answer,
 * because then the desert has a *reason*, and the reason is standing over it
 * where a player can see it.
 *
 * The model is continentality with an orographic barrier, which is the shortest
 * true thing you can say about rainfall: **weather comes off the sea, and it
 * does not get far inland, and it gets a great deal less far if it has to climb
 * over something.** So this is a cost-distance transform outward from every cell
 * of open water, over a field that costs `1` to cross flat ground and up to
 * `1 + RAIN_CLIMB` to cross a crest, and wetness is that distance run through a
 * falling exponential.
 *
 * Three things fall out of it and all three are why it is worth the Dijkstra:
 *
 *   · a wide continent has a dry middle *without anybody saying so*, and how dry
 *     depends on how wide it is;
 *   · ground behind a tall range is drier than ground the same distance from the
 *     water with nothing in the way — which is the rain shadow, and it is the
 *     whole of Vashtaur's desert;
 *   · an inland sea makes its own shores green, and a strait is worth a band of
 *     forest on the far side. That one is free, and it is the sort of thing that
 *     makes a generated map look observed rather than computed.
 *
 * It is normalised to a mean of a half over the land, on purpose: every
 * threshold in `terrainAt` was tuned against noise whose mean was a half, and
 * the point of this change is to *organise* moisture rather than to drown the
 * board. Without it the field reads near one on every coast and the world comes
 * out three-fifths marsh.
 */
const RAIN_REACH = 74;
/** What a full crest costs, against flat ground at 1. */
const RAIN_CLIMB = 5.2;
/** How much of the answer is the model, and how much keeps a band's edge ragged. */
const RAIN_WEIGHT = 0.66;
/**
 * Where the middle of the wetness scale is, in raw `exp(-cost/RAIN_REACH)`.
 *
 * Measured, not chosen: it is the mean this field had over the land of the
 * pre-eastern board, which is the board `terrainAt`'s thresholds were tuned
 * against. Declared here so that it stays that mean when the world's shape
 * changes. See the long note in `buildRain`.
 */
const RAIN_BASE = 0.7273;

let RAIN_FIELD: Float32Array | null = null;

/**
 * Built from the finished land mask rather than from the field, and therefore
 * built inside `buildRealm` rather than at module load: "where is the sea" is a
 * question `buildRaster` has already answered better than `landAt` can, because
 * it has drowned the specks and filled the puddles.
 */
function buildRain(r: LandRaster): Float32Array {
  const n = COLS * ROWS;
  const dist = new Float64Array(n).fill(Infinity);
  const heapK: number[] = [];
  const heapD: number[] = [];
  const push = (k: number, d: number): void => {
    heapK.push(k);
    heapD.push(d);
    let i = heapK.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heapD[p] <= heapD[i]) break;
      [heapD[p], heapD[i]] = [heapD[i], heapD[p]];
      [heapK[p], heapK[i]] = [heapK[i], heapK[p]];
      i = p;
    }
  };
  const pop = (): number => {
    const top = heapK[0];
    const lk = heapK.pop() as number;
    const ld = heapD.pop() as number;
    if (heapK.length > 0) {
      heapK[0] = lk;
      heapD[0] = ld;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const rr = l + 1;
        let m = i;
        if (l < heapD.length && heapD[l] < heapD[m]) m = l;
        if (rr < heapD.length && heapD[rr] < heapD[m]) m = rr;
        if (m === i) break;
        [heapD[m], heapD[i]] = [heapD[i], heapD[m]];
        [heapK[m], heapK[i]] = [heapK[i], heapK[m]];
        i = m;
      }
    }
    return top;
  };

  const cost = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    const h = RIDGE_FIELD.h[k];
    cost[k] = 1 + RAIN_CLIMB * h * h;
  }
  for (let k = 0; k < n; k++) {
    if (r.land[k]) continue;
    dist[k] = 0;
    push(k, 0);
  }
  const step = WORLD.cell;
  const diag = WORLD.cell * Math.SQRT2;
  while (heapK.length > 0) {
    const d = heapD[0];
    const k = pop();
    if (d > dist[k]) continue;
    const col = k % COLS;
    const row = (k - col) / COLS;
    for (let i = 0; i < N8.length; i++) {
      const c = col + N8[i][0];
      const rw = row + N8[i][1];
      if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
      const nk = rasterKey(c, rw);
      const nd = d + (i < 4 ? step : diag) * (cost[k] + cost[nk]) * 0.5;
      if (nd >= dist[nk]) continue;
      dist[nk] = nd;
      push(nk, nd);
    }
  }

  const wet = new Float32Array(n);
  for (let k = 0; k < n; k++) wet[k] = Math.exp(-dist[k] / RAIN_REACH);
  // Re-centre on a half over the land, and open the contrast a little so the
  // difference between a seaboard and an interior is worth a different terrain.
  /**
   * **Re-centred on a declared constant, not on this board's own mean.**
   *
   * It used to divide by the mean of `wet` over whatever land existed, and that
   * is a *rank* rather than a measurement: it says "half of the ground there
   * happens to be is wetter than the middle", so the answer for one province
   * depends on which continents the world contains. The eastern continent
   * proved it. Its interior is the driest ground on the board by a wide margin,
   * so adding it pulled the board's mean from 0.727 to 0.664 — and every
   * province in the *west*, none of which had moved a unit, was measured against
   * a drier middle and came out wetter. The Greyholt's free cities went from
   * plains and woodland to half fen, and Aelloran's ring went from forest to
   * marsh, on a change that was supposed to happen two thousand units away.
   *
   * This is the same bug as `rimPush`, `wasteness` and `seatHeartland`'s axis,
   * for the fourth time: a statement about *the paper and what is on it*, where
   * a statement about the world was meant. So the middle of the scale is a
   * number. `RAIN_BASE` is the mean the old board actually had, which is the
   * board every threshold in `terrainAt` was tuned against, and it does not
   * move when a continent is added, removed or reshaped. Wetness is now an
   * absolute property of a piece of ground: a dry continent is dry, rather than
   * merely drier than its neighbours.
   *
   * The contrast term is unchanged, and still opens the gap between a seaboard
   * and an interior enough to be worth a different terrain.
   */
  for (let k = 0; k < n; k++) {
    wet[k] = Math.max(0, Math.min(1, 0.5 + (wet[k] - RAIN_BASE) * 1.35));
  }
  return wet;
}

function rainAt(x: number, y: number): number {
  if (!RAIN_FIELD) return 0.5;
  const col = Math.max(0, Math.min(COLS - 1, Math.floor((x - WORLD.originX) / WORLD.cell)));
  const row = Math.max(0, Math.min(ROWS - 1, Math.floor((y - WORLD.originY) / WORLD.cell)));
  return RAIN_FIELD[row * COLS + col];
}

/**
 * How wet the ground is, 0..1: mostly where the weather got to, and enough
 * noise that a band has a ragged edge rather than a ruled one.
 */
function moistureAt(x: number, y: number): number {
  const noise = fbmN(x * 0.017 - 21, y * 0.017 + 55, REALM_SEED + 211, 3);
  return Math.max(0, Math.min(1, rainAt(x, y) * RAIN_WEIGHT + noise * (1 - RAIN_WEIGHT)));
}

/**
 * How far north or south of the temperate belt a latitude is, 0..1.
 *
 * `MAPREF.md`'s Frozen Wastes and Howling Wastes: the map's border *and* a
 * reason not to go there. Latitude only, which is the whole idea — a mass whose
 * end runs north ends in ice and one that runs east does not.
 *
 * **The equator is declared, not derived from the paper.** It used to be the
 * middle of the sheet and the band a fraction of its half-height, which meant
 * adding a northern continent moved the tropics: the fens at y 350 would have
 * come out temperate and the ice at y 60 merely cold, on a map where nothing
 * about either had changed. `EQUATOR` is where it was when the belt was tuned
 * and `TEMPERATE_REACH` is how far the belt ran, both in map units.
 */
const EQUATOR = 198;
/** Map units from the equator before the ground begins to turn hostile. */
const TEMPERATE_REACH = 109;
/** …and over how many more it becomes wholly so. */
const HOSTILE_RAMP = 56;

/**
 * Exported for the chart, which needs the same answer this file needs.
 *
 * The hillshade's snowline used to be one height for the whole world, so the
 * Rimewreath — a region that exists *because* this function says the ground
 * there is hostile — got snow on a tenth of its cells and bare rock on the rest.
 * A latitude the generator already believes in is the field to bend the snowline
 * by, and re-deriving it in `ui/relief.ts` from a second copy of `EQUATOR` is
 * how the two would drift apart the first time either moved.
 */
export function wasteness(y: number): number {
  return Math.max(0, Math.min(1, (Math.abs(y - EQUATOR) - TEMPERATE_REACH) / HOSTILE_RAMP));
}

function isWasteAt(y: number): boolean {
  return wasteness(y) > 0.5;
}


// ── The heartland, hand-authored ───────────────────────────────────────────

interface HeartSpec {
  id: string;
  name: string;
  flavour: string;
  /** Position within the heartland's own six-by-four block. */
  col: number;
  row: number;
  terrain?: ProvinceTerrain;
  wealth?: number;
  levy?: number;
  fort?: number;
  resource?: ResourceKey | '';
  yield?: number;
  owner?: string;
  /** Heartland neighbours, exactly as they were written. */
  links: string[];
}

/**
 * The twenty-four the realm was built around, unchanged in everything except
 * where they sit. Their graph is theirs: the lattice does not add edges between
 * two heartland provinces, so the shape of the old board survives inside the new
 * one and every route the campaign tests walk still exists.
 */
const HEARTLAND: HeartSpec[] = [
  { id: 'ashfall', name: 'Ashfall', flavour: 'It snowed grey here for a year and nobody agreed on what had burned.', col: 0, row: 0, terrain: 'ruin', wealth: 8, levy: 7, resource: 'stone', yield: 4, owner: 'ashen', links: ['greywater', 'emberhold'] },
  { id: 'greywater', name: 'Greywater', flavour: 'A town on a river that stopped being drinkable inside living memory.', col: 1, row: 0, terrain: 'marsh', wealth: 9, levy: 9, resource: 'grain', yield: 5, links: ['thornmere', 'lastfield'] },
  { id: 'thornmere', name: 'Thornmere', flavour: 'Hedges grown for eight hundred years by people who never once trimmed them.', col: 2, row: 0, terrain: 'forest', wealth: 7, levy: 10, resource: 'timber', yield: 6, links: ['sablereach', 'rookmoor'] },
  { id: 'sablereach', name: 'Sablereach', flavour: 'The Litany keeps its dead here because the ground is polite about it.', col: 3, row: 0, terrain: 'plain', wealth: 12, levy: 8, fort: 1, owner: 'litany', links: ['cold_anvil', 'gallowdown'] },
  { id: 'cold_anvil', name: 'The Cold Anvil', flavour: 'A horde capital, if you are generous about the word capital.', col: 4, row: 0, terrain: 'hill', wealth: 11, levy: 16, fort: 2, resource: 'iron', yield: 5, owner: 'ironfang', links: ['the_wake', 'ironscar'] },
  { id: 'the_wake', name: 'The Wake', flavour: 'Named for what follows the Ironfang out of it, not for anything on a map.', col: 5, row: 0, terrain: 'coast', wealth: 9, levy: 13, owner: 'ironfang', links: ['saltmarch'] },

  { id: 'emberhold', name: 'Emberhold', flavour: 'The Ashen Crown, and one fire that has not gone out yet.', col: 0, row: 1, terrain: 'hill', wealth: 16, levy: 12, fort: 2, resource: 'stone', yield: 5, owner: 'ashen', links: ['lastfield', 'dunmarrow'] },
  { id: 'lastfield', name: 'Lastfield', flavour: 'The realm’s bread, on ground nobody can hold and everybody has tried.', col: 1, row: 1, terrain: 'plain', wealth: 14, levy: 11, fort: 1, resource: 'grain', yield: 9, owner: 'ashen', links: ['rookmoor', 'the_hush'] },
  { id: 'rookmoor', name: 'Rookmoor', flavour: 'The birds arrived first and have never seen a reason to leave.', col: 2, row: 1, terrain: 'marsh', wealth: 10, levy: 9, owner: 'litany', links: ['gallowdown', 'verge'] },
  { id: 'gallowdown', name: 'Gallowdown', flavour: 'The Pale Litany holds court. Attendance in the flesh is not required.', col: 3, row: 1, terrain: 'plain', wealth: 15, levy: 10, fort: 2, resource: 'souls', yield: 4, owner: 'litany', links: ['ironscar', 'bramblewold'] },
  { id: 'ironscar', name: 'Ironscar', flavour: 'Everything worth having here is underground, and so is everyone.', col: 4, row: 1, terrain: 'hill', wealth: 12, levy: 10, fort: 1, resource: 'iron', yield: 8, owner: 'ironfang', links: ['saltmarch', 'deepgate'] },
  { id: 'saltmarch', name: 'Saltmarch', flavour: 'The Congregation preaches here, mostly to itself, mostly at volume.', col: 5, row: 1, terrain: 'marsh', wealth: 10, levy: 15, fort: 1, owner: 'gutter', links: ['drownfoot'] },

  { id: 'dunmarrow', name: 'Dunmarrow', flavour: 'Barrows on barrows. The Moonbound hunt across them and do not dig.', col: 0, row: 2, terrain: 'hill', wealth: 9, levy: 12, owner: 'moonbound', links: ['the_hush', 'pyrewood'] },
  { id: 'the_hush', name: 'The Hush', flavour: 'A forest that goes quiet when you arrive. It does not stay quiet.', col: 1, row: 2, terrain: 'forest', wealth: 11, levy: 14, fort: 1, resource: 'timber', yield: 7, owner: 'moonbound', links: ['verge', 'lowmoot'] },
  { id: 'verge', name: 'The Verge', flavour: 'Where the Choir’s singing stops being audible and starts being weather.', col: 2, row: 2, terrain: 'forest', wealth: 12, levy: 9, resource: 'timber', yield: 6, owner: 'verdant', links: ['bramblewold', 'kilnhollow'] },
  { id: 'bramblewold', name: 'Bramblewold', flavour: 'The Verdant Choir, and several things that used to be trees.', col: 3, row: 2, terrain: 'forest', wealth: 14, levy: 10, fort: 2, resource: 'grain', yield: 6, owner: 'verdant', links: ['deepgate', 'stonepale'] },
  { id: 'deepgate', name: 'Deepgate', flavour: 'Karrun-Dûr’s front door. It is a door in the sense that a mountain is.', col: 4, row: 2, terrain: 'mountain', wealth: 16, levy: 9, fort: 3, resource: 'iron', yield: 7, owner: 'karrun', links: ['drownfoot', 'underscar'] },
  { id: 'drownfoot', name: 'Drownfoot', flavour: 'Tidal. Twice a day it is somebody else’s problem entirely.', col: 5, row: 2, terrain: 'coast', wealth: 11, levy: 12, owner: 'gutter', links: ['the_reef'] },

  { id: 'pyrewood', name: 'Pyrewood', flavour: 'Cut for pyres for two centuries. It has opinions about axes.', col: 0, row: 3, terrain: 'forest', wealth: 8, levy: 11, resource: 'timber', yield: 8, owner: 'moonbound', links: ['lowmoot'] },
  { id: 'lowmoot', name: 'Lowmoot', flavour: 'A parliament of villages that has never once reached a decision.', col: 1, row: 3, terrain: 'plain', wealth: 10, levy: 9, resource: 'grain', yield: 5, links: ['kilnhollow'] },
  { id: 'kilnhollow', name: 'Kilnhollow', flavour: 'The Vigil bakes its bread and its heretics in the same ovens. Allegedly.', col: 2, row: 3, terrain: 'plain', wealth: 13, levy: 8, fort: 1, resource: 'stone', yield: 6, owner: 'vigil', links: ['stonepale'] },
  { id: 'stonepale', name: 'Stonepale', flavour: 'Nine hundred oaths, one wall, and a great deal of gold leaf.', col: 3, row: 3, terrain: 'hill', wealth: 17, levy: 7, fort: 3, resource: 'gold', yield: 6, owner: 'vigil', links: ['underscar'] },
  { id: 'underscar', name: 'Underscar', flavour: 'A hole with a nation in it, and the nation is winning.', col: 4, row: 3, terrain: 'mountain', wealth: 13, levy: 8, fort: 2, resource: 'stone', yield: 7, owner: 'karrun', links: ['the_reef'] },
  { id: 'the_reef', name: 'The Reef', flavour: 'Nobody claims it. Something down there may disagree.', col: 5, row: 3, terrain: 'coast', wealth: 12, levy: 6, resource: 'souls', yield: 3, links: [] },
];

/** The links that are something other than open country, in the heartland. */
const HEART_SPECIAL: SpecialLink[] = [
  { a: 'ashfall', b: 'greywater', kind: LinkKind.Road },
  { a: 'greywater', b: 'lastfield', kind: LinkKind.Road },
  { a: 'emberhold', b: 'lastfield', kind: LinkKind.Road },
  { a: 'lastfield', b: 'rookmoor', kind: LinkKind.Road },
  { a: 'rookmoor', b: 'gallowdown', kind: LinkKind.Road },
  { a: 'thornmere', b: 'rookmoor', kind: LinkKind.River },
  { a: 'gallowdown', b: 'ironscar', kind: LinkKind.River },
  { a: 'ironscar', b: 'deepgate', kind: LinkKind.Pass },
  { a: 'deepgate', b: 'underscar', kind: LinkKind.Pass },
  { a: 'dunmarrow', b: 'pyrewood', kind: LinkKind.Pass },
  { a: 'the_hush', b: 'verge', kind: LinkKind.River },
  { a: 'verge', b: 'bramblewold', kind: LinkKind.Road },
  { a: 'bramblewold', b: 'stonepale', kind: LinkKind.Road },
  { a: 'kilnhollow', b: 'stonepale', kind: LinkKind.Road },
  { a: 'saltmarch', b: 'drownfoot', kind: LinkKind.River },
  { a: 'drownfoot', b: 'the_reef', kind: LinkKind.River },
];

/**
 * The eight powers the setting was written around. Untouched but for one line
 * each: **which people they are**.
 *
 * They are authored rather than derived because `raceOfFaction` cannot answer
 * it — two of these fly a faction banner no race declares (`gutter`) and two
 * races share another (`ironfang`), so the bridge would put the Gutter
 * Congregation down as men. These eight are named characters in the setting and
 * their peoples are a setting decision: the Congregation is the warrens and the
 * Horde is the moot, which is what their creeds have always said.
 *
 * `leaderId` is filled in by `buildNations` rather than written here, because
 * which house of a people a crown gets depends on how many crowns of that
 * people the world already has — and these eight come first.
 */
const HEART_NATIONS: NationDef[] = [
  { id: 'ashen', name: 'The Ashen Crown', factionId: 'ashen', capital: 'emberhold', aggression: 0.42, caution: 0.62, militarism: 0.55, creed: 'Hold the bread country. Everything else is negotiable and always has been.', raceId: 'ashen', leaderId: '' },
  { id: 'litany', name: 'The Pale Litany', factionId: 'litany', capital: 'gallowdown', aggression: 0.55, caution: 0.5, militarism: 0.62, creed: 'We are patient because we can afford to be. Our levies do not age.', raceId: 'litany', leaderId: '' },
  { id: 'ironfang', name: 'The Ironfang Horde', factionId: 'ironfang', capital: 'cold_anvil', aggression: 0.9, caution: 0.22, militarism: 0.85, creed: 'Walls are a delay. Delays end.', raceId: 'splitmoot', leaderId: '' },
  /**
   * **`raceId` moved from `cogwarren` to `undertow`, and it closes a compromise
   * this table wrote down when it was made.** The note above says two of the
   * eight fly a faction banner no race declares, and names `gutter` as one:
   * `factions.ts` has the Congregation down as `race: 'Ratmen'` with a roster of
   * swarms, tunnellers and censer-rats, and the roster had no ratkin, so the
   * nearest thing — goblins who live in a warren — was written in by hand. The
   * Undertow are ratkin, so the Congregation is ratkin, and the bridge and the
   * setting now say the same thing.
   */
  { id: 'gutter', name: 'The Gutter Congregation', factionId: 'gutter', capital: 'saltmarch', aggression: 0.7, caution: 0.3, militarism: 0.78, creed: 'There are more of us than there is of anything. That is the sermon.', raceId: 'undertow', leaderId: '' },
  { id: 'moonbound', name: 'The Moonbound', factionId: 'moonbound', capital: 'the_hush', aggression: 0.72, caution: 0.34, militarism: 0.6, creed: 'We do not hold ground. We hold the things standing on it.', raceId: 'moonbound', leaderId: '' },
  { id: 'verdant', name: 'The Verdant Choir', factionId: 'verdant', capital: 'bramblewold', aggression: 0.3, caution: 0.72, militarism: 0.45, creed: 'The wood grows back. So do we. Let them tire first.', raceId: 'verdant', leaderId: '' },
  { id: 'karrun', name: 'Karrun-Dûr', factionId: 'karrun', capital: 'deepgate', aggression: 0.24, caution: 0.82, militarism: 0.5, creed: 'Dig. Wall. Wait. Nobody has ever won a siege of Deepgate and several have tried.', raceId: 'karrun', leaderId: '' },
  { id: 'vigil', name: 'The Gilded Vigil', factionId: 'vigil', capital: 'stonepale', aggression: 0.5, caution: 0.55, militarism: 0.7, creed: 'Fewer, better, and paid for in advance.', banner: 0xdfd8c4, raceId: 'vigil', leaderId: '' },
];

// ── Naming ─────────────────────────────────────────────────────────────────
//
// Compound place-names in the same register as the twenty-four: a hard,
// unromantic head and an old-English tail. The lists are long enough that the
// realm never repeats itself and short enough that everything in them sounds
// like it belongs on the same map.

const HEADS = [
  'Ash', 'Bram', 'Barrow', 'Bone', 'Black', 'Cold', 'Corvin', 'Dun', 'Fell', 'Gallow',
  'Grey', 'Grim', 'Harrow', 'Hollow', 'Iron', 'Kiln', 'Marl', 'Mire', 'Moss', 'Pyre',
  'Quill', 'Rime', 'Rook', 'Salt', 'Sable', 'Sedge', 'Sorrow', 'Stone', 'Thorn', 'Tinder',
  'Wain', 'Weald', 'Whin', 'Wold', 'Wrack', 'Yarrow', 'Fen', 'Crow', 'Cairn', 'Loam',
  'Nettle', 'Drear', 'Elder', 'Gorse', 'Hale', 'Slack', 'Bracken', 'Char', 'Dimm', 'Tarn',
] as const;

const TAILS = [
  'fall', 'water', 'mere', 'reach', 'gate', 'down', 'scar', 'march', 'hollow', 'pale',
  'wood', 'moot', 'foot', 'fen', 'dell', 'crag', 'hearth', 'bridge', 'ford', 'barrow',
  'watch', 'keep', 'cairn', 'moor', 'hold', 'mark', 'rest', 'span', 'cross', 'vale',
  'garth', 'wick', 'stead', 'hythe', 'combe', 'gill', 'thwaite', 'ness', 'shaw', 'holt',
  'lea', 'beck', 'tarn', 'dyke', 'gap', 'row', 'burn', 'mouth', 'edge', 'side',
] as const;

/** The rarer form: a single word with the definite article, as The Hush has. */
const SOLOS = [
  'Sump', 'Scour', 'Wend', 'Reave', 'Quiet', 'Tally', 'Gnarl', 'Weeping', 'Long Wold',
  'Stillmoor', 'Bell', 'Kindling', 'Hanging Fen', 'Nine Stones', 'Blackwater', 'Sallow',
  'Cold Furrow', 'Ossuary', 'Thin Country', 'Winnowing', 'Broken Mile', 'Ember Waste',
  'Salt Flat', 'Grieving',
] as const;

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * A name nobody else has.
 *
 * Rejection sampling on a hash rather than a running counter, because a counter
 * would make one province's name depend on how many were generated before it —
 * and then adding a province in the north would silently rename one in the south.
 */
function placeName(key: number, taken: Set<string>): string {
  for (let salt = 0; salt < 64; salt++) {
    const roll = hash2(key, salt, REALM_SEED ^ 0x9e37);
    let name: string;
    if (roll < 0.07) {
      name = `The ${pick(SOLOS, key, salt, 5701)}`;
    } else {
      const head = pick(HEADS, key, salt, 6113);
      const tail = pick(TAILS, key, salt * 7 + 3, 7919);
      // "Ashash", "Fenfen" and their cousins. Reject rather than patch.
      if (head.toLowerCase() === tail.slice(0, head.length)) continue;
      name = head + tail;
    }
    if (taken.has(name)) continue;
    taken.add(name);
    return name;
  }
  // Unreachable at any realm size this lattice can produce, and cheap insurance.
  const fallback = `Marchland ${key}`;
  taken.add(fallback);
  return fallback;
}

/**
 * One line of flavour, in the voice.
 *
 * Written per terrain rather than per province, which is the honest trade: a
 * hundred bespoke lines is the data entry this file exists to avoid, and a
 * hundred lines that all say "a place of hills" would be worse than none. Seven
 * per terrain and a hash means a run of neighbouring hills does not repeat
 * itself, and the twenty-four that carry the setting still have their own.
 */
const FLAVOURS: Record<ProvinceTerrain, readonly string[]> = {
  plain: [
    'Flat, ploughed, and fought over by people who wanted neither the plough nor the ground.',
    'Good soil, bad history. The two are not unrelated.',
    'They grow enough here to be worth robbing and not enough to hire anyone.',
    'The wind comes off the waste and nobody has yet planted anything that stops it.',
    'Three villages, one road, and an argument about which came first.',
    'Open country. Whoever is strongest this season is the government.',
    'Barley, mostly. The rest is other people’s business.',
  ],
  hill: [
    'Steep enough to be a nuisance, not steep enough to be a wall.',
    'Sheep, stone and long sightlines. The last of those is why anyone holds it.',
    'Every rise here has something buried under it and nobody will say what.',
    'The old watchfires are still stacked. Nobody remembers whose turn it is.',
    'A hard climb for an army and a hard living for everyone else.',
    'They mine here, badly, and have done for longer than the mining was worth.',
    'Ridges like knuckles. You can see three days’ march in every direction.',
  ],
  forest: [
    'Dead standing timber for a mile. It burns beautifully and nobody dares.',
    'The trees were cut once and came back wrong. Nobody has tried twice.',
    'You can hide an army in here. Several people have. None of them came out whole.',
    'Charcoal country. The people are grey to the wrists and grey to the eye.',
    'Old wood, older paths, and a rule about not leaving them after dark.',
    'They log it in strips so the wood cannot see what is being done to it.',
    'Something keeps the deer thin here, and it is not the winters.',
  ],
  marsh: [
    'Half of it is water and the other half is thinking about it.',
    'Reed, peat and fever. The peat is the only one worth anything.',
    'The causeways are older than the villages and better maintained.',
    'They bury nobody here. The ground gives everything back eventually.',
    'A month of standing water, a month of standing mud, and ten of standing it.',
    'Eels, mostly. And whatever the eels have been eating.',
    'You do not march across this. You wade, and you count everyone twice after.',
  ],
  coast: [
    'The sea takes a house a year and the town has never once moved.',
    'A harbour, a wall, and a very old argument with the tide.',
    'Salt in the bread, salt in the timber, salt in the temper.',
    'They fish, and they take what the fishing brings up that is not fish.',
    'Wreck country. Half the roofbeams here have been to sea.',
    'The shallows are lethal and the pilots are expensive. Both are on purpose.',
    'Nowhere to run inland and nowhere to run out. So nobody runs.',
  ],
  mountain: [
    'A road, a gorge, and a great many places to stand above both.',
    'Nothing grows. Everything that matters is under it anyway.',
    'Snow eight months, siege the other four, by long tradition.',
    'The pass is the province. The rest is a rumour of goats.',
    'Cut into the rock, roofed by the rock, and unbothered by anything above it.',
    'Every approach is a killing ground and the locals have named all of them.',
    'Cold enough that the dead keep, which has proved useful more than once.',
  ],
  ruin: [
    'Something stood here that was worth the burning. Nobody agrees what.',
    'Streets under the grass, and the grass grows in the pattern of the streets.',
    'Squatters in a palace, and the palace is winning.',
    'They quarry it. The stone is better than anything cut since.',
    'The walls stand to a man’s height everywhere and to nobody’s use.',
    'It has a name in three languages and a population in none of them.',
    'Worth holding only because whoever holds it can be seen holding it.',
  ],
  jungle: [
    'Green to the horizon and every yard of it holding something with an opinion.',
    'The road was cut four times. Three of the cuttings are gone.',
    'It rains at four. You can set a watch by it, and the garrison does.',
    'Fever country. The tax collector comes once and sends letters after that.',
    'Everything grows and nothing keeps. Grain rots in the sack it was cut into.',
    'A column can march through it in single file or not at all.',
    'The trees were here before the province and expect to outlast it.',
  ],
  desert: [
    'Stone, sun, and the shortest tempers on the continent.',
    'Three wells. Whoever holds the wells holds everything between them.',
    'The wind moves the dunes and the dunes move the border.',
    'Nothing to hide behind for a day’s ride in any direction.',
    'Caravans, and a tax on caravans, and men who dispute the tax.',
    'What grows here was planted by somebody who has since given up.',
    'Old bones in the sand, and older walls under it.',
  ],
  ice: [
    'The snow has not left in living memory and nobody expects it to.',
    'Ground so hard the dead are stacked until spring, and spring is a rumour.',
    'Wind off the glacier, and a wall built facing it that nobody mans.',
    'Six weeks of daylight, and forty-six of the other thing.',
    'The pass closes in the first month and opens in the eleventh, if it opens.',
    'Whale-oil, sealskin, and a grudge kept warm because nothing else is.',
    'A hold cut into blue ice, and the ice is load-bearing.',
  ],
};

// ── The raster ─────────────────────────────────────────────────────────────

/** Four- and eight-neighbourhoods, in the order everything here walks them. */
const N4: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const N8: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/**
 * Land under about this much ground is drowned — **unless somebody meant it.**
 *
 * Two thresholds in one rule, because there are two kinds of small island and
 * they want opposite treatment. A *leftover* is a chunk the domain warp bit off
 * a headland or a flooded seam stranded in a strait: it has no reason to be
 * there, it reads as rubble, and the owner's verdict on the last board named it
 * — *"small fragments should be deliberate archipelagos with a reason, not
 * leftovers"*. An *archipelago* is an `IsleSpec`: somebody wrote it down,
 * because a raider coast wants skerries to hide behind and a ring wants a tail.
 *
 * A size threshold cannot tell them apart — a four-province leftover is bigger
 * than a one-province archipelago — so the rule is not size alone. Ground that
 * a declared isle anchor stands on is **kept whatever its size**, and everything
 * else under nine hundred square units, which is a little over two holdings, is
 * drowned. That is why this number is now three times what it was and the board
 * has *fewer* islets than it did rather than more.
 */
const MIN_ISLAND_CELLS = 225;
/** A puddle this size or smaller is filled in rather than drawn. */
const MAX_PUDDLE_CELLS = 26;

interface LandRaster {
  land: Uint8Array;
  mass: Int32Array;
  /** Connected component of land, or -1 on water. */
  piece: Int32Array;
  /** Map units to the nearest water, on land; 0 on water. */
  inland: Float32Array;
  /** Map units to the nearest land, on water; 0 on land. */
  offshore: Float32Array;
}

/**
 * Chamfer distance transform, two passes, in map units.
 *
 * Exact enough at 3-4% error and about forty times cheaper than the alternative,
 * and everything it feeds — how crowded the provinces are near a shore, where
 * the engraved sea lines go, where a wave tick is allowed — is a soft judgement
 * that cannot tell the difference.
 */
function chamfer(seed: Uint8Array): Float32Array {
  const d = new Float32Array(COLS * ROWS);
  const BIG = 1e9;
  for (let i = 0; i < d.length; i++) d[i] = seed[i] ? 0 : BIG;
  const a = WORLD.cell;
  const b = WORLD.cell * Math.SQRT2;
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      let v = d[k];
      if (col > 0) v = Math.min(v, d[k - 1] + a);
      if (row > 0) v = Math.min(v, d[k - COLS] + a);
      if (row > 0 && col > 0) v = Math.min(v, d[k - COLS - 1] + b);
      if (row > 0 && col < COLS - 1) v = Math.min(v, d[k - COLS + 1] + b);
      d[k] = v;
    }
  }
  for (let row = ROWS - 1; row >= 0; row--) {
    for (let col = COLS - 1; col >= 0; col--) {
      const k = rasterKey(col, row);
      let v = d[k];
      if (col < COLS - 1) v = Math.min(v, d[k + 1] + a);
      if (row < ROWS - 1) v = Math.min(v, d[k + COLS] + a);
      if (row < ROWS - 1 && col < COLS - 1) v = Math.min(v, d[k + COLS + 1] + b);
      if (row < ROWS - 1 && col > 0) v = Math.min(v, d[k + COLS - 1] + b);
      d[k] = v;
    }
  }
  return d;
}

/** Label the connected components of a mask, four-connected. */
function components(mask: Uint8Array): { label: Int32Array; sizes: number[] } {
  const label = new Int32Array(COLS * ROWS).fill(-1);
  const sizes: number[] = [];
  const queue = new Int32Array(COLS * ROWS);
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || label[start] >= 0) continue;
    const id = sizes.length;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    label[start] = id;
    while (head < tail) {
      const k = queue[head++];
      const col = k % COLS;
      const row = (k - col) / COLS;
      for (const [dc, dr] of N4) {
        const c = col + dc;
        const r = row + dr;
        if (c < 0 || r < 0 || c >= COLS || r >= ROWS) continue;
        const nk = rasterKey(c, r);
        if (!mask[nk] || label[nk] >= 0) continue;
        label[nk] = id;
        queue[tail++] = nk;
      }
    }
    sizes.push(tail);
  }
  return { label, sizes };
}

/**
 * The land, decided cell by cell, then cleaned.
 *
 * Cleaning is two rules and both are about the drawing rather than the
 * simulation. A puddle of a dozen cells inside a province would be a hole in a
 * polygon the map has no way to draw, so it is filled. A rock of a dozen cells
 * offshore cannot hold a holding, so it is drowned — the archipelagos are
 * anchored large enough to survive this, which is the whole reason they are
 * declared rather than rolled.
 */
function buildRaster(): LandRaster {
  const land = new Uint8Array(COLS * ROWS);
  const mass = new Int32Array(COLS * ROWS).fill(-1);
  const declared = new Uint8Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      const hit = landAt(cellX(col), cellY(row));
      if (hit.v > LAND_CUT) {
        land[k] = 1;
        mass[k] = hit.mass;
        if (hit.isle) declared[k] = 1;
      }
    }
  }

  // Fill the puddles: any water not joined to the open ocean and small enough
  // to be an accident of the noise.
  const water = new Uint8Array(COLS * ROWS);
  for (let i = 0; i < land.length; i++) water[i] = land[i] ? 0 : 1;
  const sea = components(water);
  // The ocean is whichever water component touches the rim, and the rim is
  // always water, so it is the component of the top-left cell.
  const ocean = sea.label[0];
  for (let i = 0; i < water.length; i++) {
    const id = sea.label[i];
    if (id < 0 || id === ocean) continue;
    if (sea.sizes[id] > MAX_PUDDLE_CELLS) continue;
    land[i] = 1;
  }

  // Drown the leftovers, and spare the archipelagos somebody declared.
  let pieces = components(land);
  const spared = new Set<number>();
  for (let i = 0; i < land.length; i++) {
    if (declared[i] && pieces.label[i] >= 0) spared.add(pieces.label[i]);
  }
  for (let i = 0; i < land.length; i++) {
    const id = pieces.label[i];
    if (id >= 0 && pieces.sizes[id] < MIN_ISLAND_CELLS && !spared.has(id)) land[i] = 0;
  }
  pieces = components(land);
  for (let i = 0; i < land.length; i++) if (!land[i]) mass[i] = -1;

  // A land cell whose mass is unset (it can happen where two chains overlap and
  // the field is carried by the noise alone) takes its nearest neighbour's.
  for (let pass = 0; pass < 3; pass++) {
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const k = rasterKey(col, row);
        if (!land[k] || mass[k] >= 0) continue;
        for (const [dc, dr] of N8) {
          const c = col + dc;
          const r = row + dr;
          if (c < 0 || r < 0 || c >= COLS || r >= ROWS) continue;
          const m = mass[rasterKey(c, r)];
          if (m >= 0) {
            mass[k] = m;
            break;
          }
        }
      }
    }
  }

  const wet = new Uint8Array(COLS * ROWS);
  for (let i = 0; i < land.length; i++) wet[i] = land[i] ? 0 : 1;
  const inland = chamfer(wet);
  const offshore = chamfer(land);
  for (let i = 0; i < land.length; i++) {
    if (land[i]) offshore[i] = 0;
    else inland[i] = 0;
  }
  return { land, mass, piece: pieces.label, inland, offshore };
}

// ── Where the provinces go ─────────────────────────────────────────────────

/**
 * How far apart two holdings should be here, in map units.
 *
 * **This is the answer to "why is every province the same size".** It was the
 * lattice pitch, a constant, and a constant pitch is the single loudest tell
 * that a map was generated. It is now a field, and it says the things a
 * cartographer would say: a rich coast is crowded, a waste is empty, the deep
 * interior of a continent is thinly held, and none of it is quite a formula
 * because a slow noise term moves it about.
 *
 * The spread is better than two to one in spacing, which is better than four to
 * one in ground: the largest holdings on the board are four times the area of
 * the smallest, and they are where you would expect them to be.
 */
const SPACING_BASE = 19.5;

function spacingAt(x: number, y: number, inland: number, mass: number): number {
  let s = SPACING_BASE;
  // A coast is where the people are.
  s -= 5.6 * Math.max(0, 1 - inland / 26);
  // …and the deep interior is where they are not.
  s += 5.2 * Math.max(0, Math.min(1, (inland - 30) / 46));
  // The frozen north and the howling south: ground nobody wants.
  s += 8.4 * wasteness(y);
  // High ground carries fewer people than low.
  s += 3.6 * Math.max(0, ridgeAt(x, y).h);
  // And a drift, so the density is a landscape rather than a rule.
  s += (fbmN(x * 0.011, y * 0.011, REALM_SEED + 55, 2) - 0.5) * 2 * 3.6;
  if (mass >= 0) s *= MASSES[mass].density;
  return Math.max(13, Math.min(33, s));
}

interface Site {
  x: number;
  y: number;
  col: number;
  row: number;
  mass: number;
  key: number;
  /** The spacing this seat claimed, kept so the sampler never recomputes it. */
  want: number;
}

interface Candidate {
  x: number;
  y: number;
  col: number;
  row: number;
  mass: number;
  /** Wanted spacing at the global dial of 1. It scales linearly with the dial. */
  base: number;
}

/**
 * Poisson-disc sampling, deterministic, against the density field.
 *
 * Dart-throwing rather than Bridson: one candidate per raster cell, jittered
 * inside it, visited in an order fixed by a hash of the cell, and kept unless
 * something already accepted is closer than the two sites' mean spacing. That
 * gives blue-noise with the *variance a lattice does not have* — sites are never
 * closer than the local radius and are frequently a good deal further, which is
 * exactly the irregularity a hand-drawn map has. A relaxation pass would undo
 * it, so there is none.
 */
function candidatesFor(r: LandRaster): Candidate[] {
  const cand: Array<Candidate & { ord: number }> = [];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const key = rasterKey(col, row);
      if (!r.land[key]) continue;
      const x = cellX(col) + (hash2(col, row, 4093) - 0.5) * WORLD.cell;
      const y = cellY(row) + (hash2(col + 71, row + 17, 8191) - 0.5) * WORLD.cell;
      cand.push({
        x,
        y,
        col,
        row,
        mass: r.mass[key],
        base: spacingAt(x, y, r.inland[key], r.mass[key]),
        ord: hash2(col * 3 + 1, row * 5 + 2, 2971),
      });
    }
  }
  cand.sort((a, b) => a.ord - b.ord || a.row - b.row || a.col - b.col);
  return cand;
}

function sampleSites(cand: Candidate[], k: number): Site[] {
  const BUCKET = 34;
  const bcols = Math.ceil(WORLD.w / BUCKET) + 2;
  const buckets = new Map<number, Site[]>();
  const out: Site[] = [];
  for (const c of cand) {
    const want = c.base * k;
    const bx = Math.floor((c.x - WORLD.originX) / BUCKET) + 1;
    const by = Math.floor((c.y - WORLD.originY) / BUCKET) + 1;
    let ok = true;
    for (let dy = -1; dy <= 1 && ok; dy++) {
      for (let dx = -1; dx <= 1 && ok; dx++) {
        const list = buckets.get((by + dy) * bcols + (bx + dx));
        if (!list) continue;
        for (const s of list) {
          if (Math.hypot(s.x - c.x, s.y - c.y) < (want + s.want) / 2) {
            ok = false;
            break;
          }
        }
      }
    }
    if (!ok) continue;
    const site: Site = {
      x: c.x,
      y: c.y,
      col: c.col,
      row: c.row,
      mass: c.mass,
      key: Math.round(c.x * 4) * 100003 + Math.round(c.y * 4),
      want,
    };
    out.push(site);
    const bk = by * bcols + bx;
    const list = buckets.get(bk);
    if (list) list.push(site);
    else buckets.set(bk, [site]);
  }
  return out;
}

/**
 * How many holdings the board wants. See the note on cost, below.
 *
 * **This number is not a taste decision; it is the size of the world divided by
 * the size of a province.** It went 380 → 650 with the eastern continent, and
 * the ratio is exactly the ratio of land: 35,578 raster cells of dry ground
 * before, 60,500 after, which is seventy per cent more world. Holding 380 while
 * the land grew would not have made the board simpler — it would have made every
 * province on it seventy per cent larger, which is a different game everywhere
 * including the twenty-four holdings that were never supposed to change. The
 * density *field* (`spacingAt`) decides where holdings are crowded and where
 * they are not; this decides how big one is, and it should move when and only
 * when the ground does.
 */
const TARGET_PROVINCES = 650;

/**
 * The global dial on spacing, found rather than written.
 *
 * The density *field* decides where provinces are crowded; this decides how many
 * there are in all. Solving for it by bisection rather than typing a number
 * means the count stays where the AI council was measured whatever else moves —
 * a wider gulf, a longer range, a different seed — instead of quietly drifting
 * to two hundred the next time the shape of a continent is tuned.
 *
 * **The cost, measured.** The council loops nations × provinces and
 * `tools/campaign-sweep.ts` prints it. 150 provinces and 28 crowns is what the
 * board was before this rewrite; it is what the board is after it, and the sweep
 * says so. The binding limit was never the arithmetic — it is that past about
 * two hundred names the realm stops being a place a player can hold in their
 * head.
 */
function solveSites(r: LandRaster): Site[] {
  const cand = candidatesFor(r);
  let lo = 0.55;
  let hi = 1.8;
  let best = sampleSites(cand, 1);
  for (let i = 0; i < 11; i++) {
    const mid = (lo + hi) / 2;
    const got = sampleSites(cand, mid);
    if (Math.abs(got.length - TARGET_PROVINCES) < Math.abs(best.length - TARGET_PROVINCES)) {
      best = got;
    }
    if (got.length > TARGET_PROVINCES) lo = mid;
    else hi = mid;
  }
  return best;
}

/**
 * Territory, grown across the land rather than cut out of the plane.
 *
 * Every land cell goes to the site it is *nearest by walking*, not nearest as
 * the crow flies, and the walk is dearer uphill. Three things fall out of that,
 * and all three are the difference between a chart and a diagram:
 *
 *   · a province cannot reach across a bay to ground on the far shore, because
 *     the walk does not go that way — so a coastal holding hugs its own inlet;
 *   · a province stops at a ridge, because crossing one costs three times what
 *     crossing a field does, so the mountain ranges become borders the way real
 *     ones are;
 *   · every territory is connected, which a Voronoi cell of a fragmented world
 *     is not, and which the drawing needs since a cell is one closed ring.
 */
function grow(r: LandRaster, sites: Site[]): Int32Array {
  const owner = new Int32Array(COLS * ROWS).fill(-1);
  const cost = new Float32Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      if (!r.land[k]) continue;
      cost[k] = 1 + 2.4 * ridgeAt(cellX(col), cellY(row)).h;
    }
  }

  /**
   * **Float64, and it has to be.** The heap carries full-precision doubles and
   * this table used to be `Float32Array`. Rounding a settled distance *up* on
   * the way into the table makes the guard `nd >= dist[nk]` accept a path that
   * is genuinely longer, which re-opens a settled cell and re-opens everything
   * downstream of it — a lazy Dijkstra that no longer terminates in edge time.
   * It had been quietly doing a hundred times the work it needed to since the
   * day it was written, and on one tuning change to the region table it stopped
   * terminating at all and took a four-gigabyte heap with it.
   */
  const dist = new Float64Array(COLS * ROWS).fill(Infinity);
  // A binary heap. Forty-five thousand cells and a third of a million
  // relaxations; an array scan would be quadratic and this is twenty lines.
  const heapK: number[] = [];
  const heapD: number[] = [];
  const push = (k: number, d: number): void => {
    heapK.push(k);
    heapD.push(d);
    let i = heapK.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heapD[p] <= heapD[i]) break;
      [heapD[p], heapD[i]] = [heapD[i], heapD[p]];
      [heapK[p], heapK[i]] = [heapK[i], heapK[p]];
      i = p;
    }
  };
  const pop = (): number => {
    const top = heapK[0];
    const lastK = heapK.pop() as number;
    const lastD = heapD.pop() as number;
    if (heapK.length > 0) {
      heapK[0] = lastK;
      heapD[0] = lastD;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const rr = l + 1;
        let m = i;
        if (l < heapD.length && heapD[l] < heapD[m]) m = l;
        if (rr < heapD.length && heapD[rr] < heapD[m]) m = rr;
        if (m === i) break;
        [heapD[m], heapD[i]] = [heapD[i], heapD[m]];
        [heapK[m], heapK[i]] = [heapK[i], heapK[m]];
        i = m;
      }
    }
    return top;
  };

  sites.forEach((s, i) => {
    const k = rasterKey(s.col, s.row);
    dist[k] = 0;
    owner[k] = i;
    push(k, 0);
  });

  const step = WORLD.cell;
  const diag = WORLD.cell * Math.SQRT2;
  while (heapK.length > 0) {
    const k = pop();
    const col = k % COLS;
    const row = (k - col) / COLS;
    const here = dist[k];
    for (let n = 0; n < N8.length; n++) {
      const c = col + N8[n][0];
      const rw = row + N8[n][1];
      if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
      const nk = rasterKey(c, rw);
      if (!r.land[nk]) continue;
      const d = here + (n < 4 ? step : diag) * (cost[k] + cost[nk]) * 0.5;
      if (d >= dist[nk]) continue;
      dist[nk] = d;
      owner[nk] = owner[k];
      push(nk, d);
    }
  }
  return owner;
}

/**
 * Two faults the growth can leave behind, and the chart cannot survive either.
 *
 * **A territory in two pieces.** The walk is eight-connected, so a province can
 * end up owning a cell joined to the rest of it only at a corner. A cell is one
 * closed ring on the map; two pieces is two rings, and the second one draws as a
 * stray polygon in the middle of somebody else's ground. Every province is
 * therefore cut back to the four-connected piece its own seat stands on, and
 * what is left over is given to whichever neighbour has the longest frontier
 * with it.
 *
 * **A corner where four regions meet in a checkerboard.** Two provinces touching
 * diagonally with two of somebody else's cells on the other diagonal is a point
 * where the boundary crosses itself, and a boundary that crosses itself cannot
 * be traced into a simple ring at all. One cell of the four is flipped, which
 * costs four square units of ground and removes the whole class of degeneracy.
 * The same rule covers a diagonal pinch in the *water*, which is the version of
 * this fault that would put a knot in the coastline.
 */
function polish(r: LandRaster, owner: Int32Array, sites: Site[]): void {
  // 1. One four-connected piece per province.
  const keep = new Uint8Array(COLS * ROWS);
  const queue = new Int32Array(COLS * ROWS);
  sites.forEach((s, i) => {
    const start = rasterKey(s.col, s.row);
    if (owner[start] !== i) return;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    keep[start] = 1;
    while (head < tail) {
      const k = queue[head++];
      const col = k % COLS;
      const row = (k - col) / COLS;
      for (const [dc, dr] of N4) {
        const c = col + dc;
        const rw = row + dr;
        if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
        const nk = rasterKey(c, rw);
        if (keep[nk] || owner[nk] !== i) continue;
        keep[nk] = 1;
        queue[tail++] = nk;
      }
    }
  });
  for (let i = 0; i < owner.length; i++) if (owner[i] >= 0 && !keep[i]) owner[i] = -2;
  for (let pass = 0; pass < 40; pass++) {
    let moved = false;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const k = rasterKey(col, row);
        if (owner[k] !== -2) continue;
        const tally = new Map<number, number>();
        for (const [dc, dr] of N4) {
          const c = col + dc;
          const rw = row + dr;
          if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
          const o = owner[rasterKey(c, rw)];
          if (o < 0) continue;
          tally.set(o, (tally.get(o) ?? 0) + 1);
        }
        let best = -1;
        let bestN = 0;
        for (const [o, n] of [...tally.entries()].sort((a, b) => a[0] - b[0])) {
          if (n > bestN) {
            bestN = n;
            best = o;
          }
        }
        if (best >= 0) {
          owner[k] = best;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  for (let i = 0; i < owner.length; i++) if (owner[i] === -2) owner[i] = -1;

  // 2. No checkerboard corners, anywhere, land or water.
  for (let pass = 0; pass < 4; pass++) {
    let fixed = 0;
    for (let row = 0; row < ROWS - 1; row++) {
      for (let col = 0; col < COLS - 1; col++) {
        const a = owner[rasterKey(col, row)];
        const b = owner[rasterKey(col + 1, row)];
        const c = owner[rasterKey(col, row + 1)];
        const d = owner[rasterKey(col + 1, row + 1)];
        if (a === d && b === c && a !== b) {
          owner[rasterKey(col + 1, row)] = a;
          fixed++;
        }
      }
    }
    if (fixed === 0) break;
  }

  // 3. The mask, restated from the answer, and the distances with it.
  for (let i = 0; i < owner.length; i++) r.land[i] = owner[i] >= 0 ? 1 : 0;
  const wet = new Uint8Array(COLS * ROWS);
  for (let i = 0; i < r.land.length; i++) wet[i] = r.land[i] ? 0 : 1;
  const inland = chamfer(wet);
  const offshore = chamfer(r.land);
  for (let i = 0; i < r.land.length; i++) {
    r.inland[i] = r.land[i] ? inland[i] : 0;
    r.offshore[i] = r.land[i] ? 0 : offshore[i];
  }
}

/**
 * Move each seat to the openest ground its province has.
 *
 * The Poisson sample decides *where a province is*; it has no opinion about
 * where the town in it should be drawn, and the two are not the same question.
 * A seat that the growth left near an edge — because a neighbour grew harder
 * from the other side, or because the shore came in — is a town mark sitting on
 * its own border with no room for its name, and after the smoothing that turns a
 * raster boundary into a drawn line it can end up a whisker *outside* the
 * territory it names.
 *
 * So every seat is moved to the cell of its own ground furthest from any edge of
 * it. That is a couple of units for most of them and nothing at all for the
 * rest; what it buys is the guarantee the chart needs, which is that a town is
 * inside the province it belongs to with room around it, on every seed.
 */
function reseat(owner: Int32Array, sites: Site[]): void {
  const edge = new Uint8Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      if (owner[k] < 0) continue;
      for (const [dc, dr] of N4) {
        const c = col + dc;
        const rw = row + dr;
        const o = c < 0 || rw < 0 || c >= COLS || rw >= ROWS ? -1 : owner[rasterKey(c, rw)];
        if (o !== owner[k]) {
          edge[k] = 1;
          break;
        }
      }
    }
  }
  const depth = chamfer(edge);
  const best = new Array<number>(sites.length).fill(-1);
  const bestD = new Array<number>(sites.length).fill(-1);
  for (let k = 0; k < owner.length; k++) {
    const p = owner[k];
    if (p < 0) continue;
    if (depth[k] > bestD[p]) {
      bestD[p] = depth[k];
      best[p] = k;
    }
  }
  sites.forEach((s, i) => {
    if (best[i] < 0) return;
    const col = best[i] % COLS;
    const row = (best[i] - col) / COLS;
    // A little jitter still, so two hundred town dots are not on a two-unit
    // grid — but never more than the room the cell actually has.
    const room = Math.min(0.7, Math.max(0, bestD[i] - WORLD.cell) * 0.4);
    s.col = col;
    s.row = row;
    s.x = cellX(col) + (hash2(col, row, 4093) - 0.5) * 2 * room;
    s.y = cellY(row) + (hash2(col + 71, row + 17, 8191) - 0.5) * 2 * room;
  });
}

/**
 * Which region each square of ground belongs to.
 *
 * The same weighted geodesic walk the provinces are grown by, from the region
 * seeds instead of from the province seats. Two things fall out of using the
 * same walk, and both are the point: a region cannot jump a strait, because the
 * walk does not go that way — so a region is always a coherent piece of one
 * continent — and a region *stops at a range*, because crossing one costs three
 * times what crossing a field does. "The western flank of the spine" is a thing
 * this produces rather than a thing anybody has to describe.
 */
function growRegions(r: LandRaster): Int32Array {
  const owner = new Int32Array(COLS * ROWS).fill(-1);
  const cost = new Float32Array(COLS * ROWS);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      if (!r.land[k]) continue;
      cost[k] = 1 + 2.4 * ridgeAt(cellX(col), cellY(row)).h;
    }
  }

  // A seed lands where it lands; if that is water, it takes the nearest ground.
  const starts: number[] = [];
  REGION_SEEDS.forEach((spec) => {
    const p = { x: spec.x, y: spec.y };
    let best = -1;
    let bestD = Infinity;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const k = rasterKey(col, row);
        if (!r.land[k]) continue;
        const d = Math.hypot(cellX(col) - p.x, cellY(row) - p.y);
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      }
    }
    starts.push(best);
  });

  /**
   * **One walk per seed, and the seeds compete afterwards.**
   *
   * The obvious implementation is a single multi-source Dijkstra with every
   * seed pushed at zero, and that is what this was — until `pull` arrived. A
   * per-seed speed makes the edge cost depend on *who is walking*, which is no
   * longer a shortest-path problem: two seeds of different speeds leapfrog each
   * other across the same ground, each re-opening cells the other had settled,
   * and the frontier queue grows without bound. It did not merely run slowly;
   * it exhausted a four-gigabyte heap on a change of 0.1 to one region's dial.
   *
   * So each seed gets its own honest Dijkstra over the same cost field, and the
   * *comparison* carries the weight: ground goes to whichever seed's walk, once
   * divided by its `pull`, is shortest. Eleven walks over forty-six thousand
   * cells is a few hundred milliseconds at start-up, it is obviously correct,
   * and `pull` can be any positive number without the algorithm caring.
   */
  const score = new Float64Array(COLS * ROWS).fill(Infinity);
  // Float64 for the same reason `grow` needs it: see the note there.
  const dist = new Float64Array(COLS * ROWS);
  const queue: Array<{ k: number; d: number }> = [];
  const push = (k: number, d: number): void => {
    queue.push({ k, d });
    let i = queue.length - 1;
    while (i > 0) {
      const par = (i - 1) >> 1;
      if (queue[par].d <= queue[i].d) break;
      [queue[par], queue[i]] = [queue[i], queue[par]];
      i = par;
    }
  };
  const pop = (): { k: number; d: number } => {
    const top = queue[0];
    const last = queue.pop() as { k: number; d: number };
    if (queue.length > 0) {
      queue[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const rr = l + 1;
        let m = i;
        if (l < queue.length && queue[l].d < queue[m].d) m = l;
        if (rr < queue.length && queue[rr].d < queue[m].d) m = rr;
        if (m === i) break;
        [queue[m], queue[i]] = [queue[i], queue[m]];
        i = m;
      }
    }
    return top;
  };

  const step = WORLD.cell;
  const diag = WORLD.cell * Math.SQRT2;
  starts.forEach((from, seed) => {
    if (from < 0) return;
    const pull = REGION_SEEDS[seed].pull ?? 1;
    dist.fill(Infinity);
    queue.length = 0;
    dist[from] = 0;
    push(from, 0);
    while (queue.length > 0) {
      const { k, d: here } = pop();
      if (here > dist[k]) continue;
      const col = k % COLS;
      const row = (k - col) / COLS;
      for (let n = 0; n < N8.length; n++) {
        const c = col + N8[n][0];
        const rw = row + N8[n][1];
        if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
        const nk = rasterKey(c, rw);
        if (!r.land[nk]) continue;
        const nd = here + (n < 4 ? step : diag) * (cost[k] + cost[nk]) * 0.5;
        if (nd >= dist[nk]) continue;
        dist[nk] = nd;
        push(nk, nd);
      }
    }
    for (let k = 0; k < dist.length; k++) {
      if (dist[k] === Infinity) continue;
      const s = dist[k] / pull;
      if (s < score[k]) {
        score[k] = s;
        owner[k] = seed;
      }
    }
  });

  /**
   * An offshore isle no seed can walk to takes the region whose *heart* is
   * nearest. The isles off a coast belong to that coast's story even when
   * nobody on them can walk home, which is what a hand-drawn map does without
   * thinking about it.
   */
  const hearts = REGION_SEEDS.map((spec) => ({ x: spec.x, y: spec.y }));
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      if (!r.land[k] || owner[k] >= 0) continue;
      let best = 0;
      let bestD = Infinity;
      hearts.forEach((h, i) => {
        const d = Math.hypot(cellX(col) - h.x, cellY(row) - h.y);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      owner[k] = best;
    }
  }
  return owner;
}

/** Fill any water a single province encloses: a cell is one ring, not two. */
function fillHoles(r: LandRaster, owner: Int32Array): void {
  const water = new Uint8Array(COLS * ROWS);
  for (let i = 0; i < owner.length; i++) water[i] = r.land[i] ? 0 : 1;
  const sea = components(water);
  const ocean = sea.label[0];
  for (let id = 0; id < sea.sizes.length; id++) {
    if (id === ocean) continue;
    // Whose ground surrounds it?
    let only = -2;
    for (let i = 0; i < water.length && only !== -1; i++) {
      if (sea.label[i] !== id) continue;
      const col = i % COLS;
      const row = (i - col) / COLS;
      for (const [dc, dr] of N8) {
        const c = col + dc;
        const rw = row + dr;
        if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
        const o = owner[rasterKey(c, rw)];
        if (o < 0) continue;
        if (only === -2) only = o;
        else if (only !== o) {
          only = -1;
          break;
        }
      }
    }
    if (only < 0) continue;
    for (let i = 0; i < water.length; i++) {
      if (sea.label[i] !== id) continue;
      r.land[i] = 1;
      owner[i] = only;
      r.mass[i] = -1;
    }
  }
}

// ── The heartland, seated on the new ground ────────────────────────────────

/**
 * The twenty-four are hand-authored and stay hand-authored — but they no longer
 * sit on a six-by-four block of lattice, because there is no lattice.
 *
 * They are matched onto sampled sites instead. The Hearthlands' own ground is
 * measured for its long and short axes, the authored block's `col`/`row` is read
 * as a position on those axes, and each authored holding takes the free site
 * nearest to where it ought to be — with a heavy penalty on seating one of the
 * three the text calls a coast anywhere but on the water. The result keeps the
 * old board's *shape* (Ashfall in one corner, The Reef in the other, the roads
 * running the way they always ran) while every one of the twenty-four now has an
 * irregular territory the size the density field says it should be.
 */
function seatHeartland(
  sites: Site[],
  coastal: boolean[],
  piece: Int32Array,
  regionCells: Int32Array,
): number[] {
  /**
   * All twenty-four on **one piece of ground**, and this is not a nicety.
   *
   * The heartland's graph is authored and is added to the board verbatim — that
   * is the promise, and every campaign test and the opening of every game walks
   * it. If the twenty-four are seated across three islets then Emberhold's road
   * to Dunmarrow is drawn across open water and an army marches over the sea.
   * So the pool is the largest four-connected piece of the Hearthlands, with the
   * next pieces added only if that one cannot seat them all.
   */
  /**
   * The heartland region first, the continent behind it second.
   *
   * The twenty-four have to stand somewhere with a *seaboard* — The Wake,
   * Saltmarch, Drownfoot and The Reef are named for the water they stand on, and
   * the whole naval layer is invisible to a player whose opening holdings are
   * all inland. On a continent two hundred units across, "somewhere on this
   * landmass" is no longer an answer to that. So the pool is the ground the
   * heartland *region* won, and the region's seed is a line in `REGION_SEEDS`
   * placed on the western shore where it faces the great sea. Move that one
   * line and the whole heartland moves with it.
   */
  const onRegion = sites
    .map((s, i) => ({ s, i }))
    .filter(
      (e) => e.s.mass === HEART_MASS && regionCells[rasterKey(e.s.col, e.s.row)] === HEART_REGION,
    );
  const onMass =
    onRegion.length >= HEARTLAND.length
      ? onRegion
      : sites.map((s, i) => ({ s, i })).filter((e) => e.s.mass === HEART_MASS);
  const bySize = new Map<number, number>();
  for (const e of onMass) {
    const k = piece[rasterKey(e.s.col, e.s.row)];
    bySize.set(k, (bySize.get(k) ?? 0) + 1);
  }
  const pieces = [...bySize.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
  const wanted = new Set<number>();
  let have = 0;
  for (const [k, n] of pieces) {
    if (have >= HEARTLAND.length) break;
    wanted.add(k);
    have += n;
  }
  const pool = onMass.filter((e) => wanted.has(piece[rasterKey(e.s.col, e.s.row)]));
  let mx = 0;
  let my = 0;
  for (const e of pool) {
    mx += e.s.x;
    my += e.s.y;
  }
  mx /= Math.max(1, pool.length);
  my /= Math.max(1, pool.length);
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const e of pool) {
    const dx = e.s.x - mx;
    const dy = e.s.y - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  // Principal axis of the mass, closed form for a 2×2 covariance.
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  let ux = Math.cos(theta);
  let uy = Math.sin(theta);
  const vx = -uy;
  const vy = ux;
  /**
   * Column 5 — The Wake, Saltmarch, Drownfoot, The Reef — is the seaboard, so
   * the long axis is oriented to run toward the sea those four stand on.
   *
   * **That sea is named, and this used to point at the middle of the sheet
   * instead.** It was the same place while the Sunder happened to sit in the
   * middle of the paper, and it stopped being the same place the moment the
   * paper grew a second half: the centre of a 1210-wide sheet is out in the
   * Weltering, on the *far* side of the Reach, so the test flipped and the
   * authored twenty-four were about to be seated back to front — Ashfall on
   * the water and The Reef inland. Third instance of the same bug in this file
   * (`rimPush`, `wasteness`, and now this), and the fix is the same one every
   * time: say the thing you meant about the world, not about the paper.
   */
  if ((SEA_SUNDER.x - mx) * ux + (SEA_SUNDER.y - my) * uy < 0) {
    ux = -ux;
    uy = -uy;
  }
  let u0 = Infinity;
  let u1 = -Infinity;
  let v0 = Infinity;
  let v1 = -Infinity;
  for (const e of pool) {
    const du = (e.s.x - mx) * ux + (e.s.y - my) * uy;
    const dv = (e.s.x - mx) * vx + (e.s.y - my) * vy;
    u0 = Math.min(u0, du);
    u1 = Math.max(u1, du);
    v0 = Math.min(v0, dv);
    v1 = Math.max(v1, dv);
  }

  const want = HEARTLAND.map((h) => {
    const fu = 0.12 + ((h.col + 0.5) / 6) * 0.82;
    const fv = 0.1 + ((h.row + 0.5) / 4) * 0.8;
    const du = u0 + (u1 - u0) * fu;
    const dv = v0 + (v1 - v0) * fv;
    return { x: mx + ux * du + vx * dv, y: my + uy * du + vy * dv };
  });

  const seats = new Array<number>(HEARTLAND.length).fill(-1);
  const used = new Set<number>();
  // Longest-constrained first: the coasts have the fewest places they can go.
  const order = HEARTLAND.map((_h, i) => i).sort((a, b) => {
    const ca = HEARTLAND[a].terrain === 'coast' ? 0 : 1;
    const cb = HEARTLAND[b].terrain === 'coast' ? 0 : 1;
    return ca - cb || a - b;
  });
  for (const hi of order) {
    const h = HEARTLAND[hi];
    let best = -1;
    let bestScore = Infinity;
    for (const e of pool) {
      if (used.has(e.i)) continue;
      let score = Math.hypot(e.s.x - want[hi].x, e.s.y - want[hi].y);
      if (h.terrain === 'coast' && !coastal[e.i]) score += 260;
      if (score < bestScore) {
        bestScore = score;
        best = e.i;
      }
    }
    if (best < 0) {
      // The mass could not seat them all; take anything still free.
      for (let i = 0; i < sites.length && best < 0; i++) if (!used.has(i)) best = i;
    }
    used.add(best);
    seats[hi] = best;
  }
  return seats;
}


// ── What the ground is ─────────────────────────────────────────────────────

/**
 * Terrain, read off the fields at the holding's own seat.
 *
 * The ranges are tested first and they are the only rule here that can produce a
 * run of mountains long enough to have to go round — which is the point of
 * having ranges at all. Under them, **rainfall** and elevation decide, and the
 * region's `character` leans the result: a desert reach is not merely a plain
 * with a different name on the map, and a marsh reach drowns.
 *
 * `wet` is no longer noise. It is `RAIN_FIELD` — weather off the eastern ocean,
 * spent climbing over the ranges in its way — with enough noise on top to keep
 * an edge ragged. That is what turns three region seeds on one line of latitude
 * into jungle, desert and mountain going west, and it is why the desert has a
 * wall standing over it rather than a label.
 */
function terrainAt(x: number, y: number, shore: boolean, character: RegionCharacter): ProvinceTerrain {
  const ridge = ridgeAt(x, y);
  const height = elevationAt(x, y);
  const wet = moistureAt(x, y);
  const r = hash2(Math.round(x * 4), Math.round(y * 4), REALM_SEED + 307);
  const polar = wasteness(y);

  if (polar > 0.5) {
    // The frozen north and the howling south. High ground is mountain, the rest
    // is ice where it is cold enough to stay ice and broken ground where it is
    // not — and the further from the equator the less of the second there is.
    if (ridge.h > 0.36) return 'mountain';
    if (r < polar * 0.62) return 'ice';
    if (shore && r < 0.72) return 'coast';
    return r < 0.86 ? 'mountain' : 'hill';
  }
  if (ridge.h > 0.4) return 'mountain';
  if (ridge.h > 0.27 && !shore) return 'hill';

  switch (character) {
    /**
     * Rain shadow, and the reason the middle of Vashtaur is what it is. Sand
     * where the weather never arrives, broken rock where a little does, and the
     * odd ruin of somebody who tried anyway.
     */
    case 'desert':
      if (shore && r < 0.34) return 'coast';
      if (height > 0.74) return 'mountain';
      if (wet > 0.54) return r < 0.5 ? 'plain' : 'hill';
      if (r < 0.1) return 'ruin';
      return wet < 0.44 || r < 0.72 ? 'desert' : 'hill';
    /**
     * …and the seaboard the same weather hits first. Jungle is the wettest
     * ground on the board and it is only ever found where the rain gets to.
     */
    case 'jungle':
      if (shore && r < 0.3) return 'coast';
      if (height > 0.76) return 'mountain';
      if (wet < 0.42) return r < 0.5 ? 'forest' : 'plain';
      return wet > 0.56 || r < 0.72 ? 'jungle' : 'forest';
    case 'marsh':
      if (shore && r < 0.3) return 'coast';
      if (height > 0.74) return 'hill';
      return wet > 0.4 || r < 0.5 ? 'marsh' : 'forest';
    case 'forest':
      if (shore && r < 0.42) return 'coast';
      if (height > 0.74) return 'mountain';
      return wet > 0.36 || r < 0.55 ? 'forest' : 'hill';
    /** Eternal winter: ice and rock, and the rock is mostly under the ice. */
    case 'frozen':
      if (shore && r < 0.26) return 'coast';
      if (height > 0.6 || ridge.h > 0.2) return 'mountain';
      return r < 0.74 ? 'ice' : 'hill';
    case 'volcanic':
      if (shore && r < 0.32) return 'coast';
      if (height > 0.62) return 'mountain';
      return r < 0.36 ? 'ruin' : r < 0.62 ? 'hill' : 'plain';
    case 'mountain':
      if (shore && r < 0.3) return 'coast';
      if (height > 0.58) return 'mountain';
      return r < 0.6 ? 'hill' : 'plain';
    default:
      if (shore && height < 0.58 && r < 0.5) return 'coast';
      if (height > 0.74) return 'mountain';
      if (height > 0.6) return 'hill';
      // Both of these used to be read against a moisture that was pure noise.
      // Rainfall is now *correlated* with low ground near the water, so the two
      // conditions co-occur where they used to be independent and the same
      // numbers drowned a fifth of the board. Marsh is now the wettest low
      // ground rather than merely damp low ground.
      if (height < 0.32 && wet > 0.68) return 'marsh';
      if (wet > 0.6) return 'forest';
      if (r < 0.07) return 'ruin';
      return 'plain';
  }
}

const RESOURCE_BY_TERRAIN: Record<ProvinceTerrain, readonly (ResourceKey | '')[]> = {
  plain: ['grain', 'grain', 'grain', 'timber', ''],
  hill: ['iron', 'stone', 'stone', 'gold', ''],
  forest: ['timber', 'timber', 'timber', 'grain', ''],
  marsh: ['grain', 'souls', 'timber', '', ''],
  coast: ['souls', 'grain', 'gold', '', ''],
  mountain: ['iron', 'iron', 'stone', 'gold', ''],
  ruin: ['stone', 'souls', 'gold', '', ''],
  jungle: ['timber', 'timber', 'souls', 'gold', ''],
  desert: ['stone', 'gold', 'iron', '', ''],
  ice: ['stone', 'iron', '', '', ''],
};

// ── The realm ──────────────────────────────────────────────────────────────

/**
 * A cell of open water, coarse, for the lane router and for anyone who wants to
 * know where the sea is without walking the whole raster.
 *
 * It is no longer what the map is *drawn* from. `cartography.ts` used to give
 * every one of these a Voronoi site so the inland seas would not be painted as
 * farmland; it now traces the raster's own land boundary instead, which is both
 * cheaper and — the thing that matters — the same line the simulation believes
 * in, rather than an approximation of it at cell resolution.
 */
export interface SeaCellDef {
  col: number;
  row: number;
  /** Map coordinates, on the same sheet the provinces are laid out on. */
  x: number;
  y: number;
  interior: boolean;
}

/**
 * A route between two coastal holdings, over water, with the shape of the water
 * it goes through.
 *
 * Lanes are *derived*, never declared: two provinces are joined if a ship can
 * get from one to the other without crossing land and if the crossing is one of
 * the shorter ones available from either end. That is the whole of the rule, and
 * it is why the lane network reads as a map of the sea rather than as a list
 * somebody typed — a chain of short hops down a coast, a couple of long hauls
 * straight across the middle sea because straight across is genuinely shorter,
 * and nothing at all between two harbours with a headland in the way.
 */
export interface SeaLaneDef {
  /** Province indices, `a < b` always. */
  a: number;
  b: number;
  /** Months under sail. */
  months: number;
  /** Water cells the route passes through, in order. For drawing. */
  route: Array<{ x: number; y: number }>;
}

/**
 * A region: a neighbourhood of provinces that several rival crowns share.
 *
 * This exists because of one sentence from the owner — *"scatter a few kingdoms
 * in each region"* — and it is a bigger idea than it sounds. The reference map
 * paints one colour per people: one wash for the elves, one for the orcs. This
 * game wants the region to be the *homeland* and the kingdom to be a power
 * inside it, so that two crowns of the same people can be at each other's
 * throats. A border war with your own kind is only possible if "who you are"
 * and "who you answer to" are different questions.
 *
 * A region is now exactly one **landmass** — which is a stronger statement than
 * it was, because the landmasses are shapes rather than angular sectors of a
 * wheel. Where two of them have grown into one another the border between them
 * is dry ground and two crowns may fight over it on foot; everywhere else there
 * is water in the way.
 *
 * ── How a race or a starting kingdom binds to one ────────────────────────
 *
 * Nothing here decides who lives anywhere, on purpose: this file generates
 * geography and has no business naming peoples. The seam is `character` — a
 * climate-and-ground word from `RegionCharacter`. A race roster picks the
 * regions whose character suits it (`REGIONS.filter(r => r.character ===
 * 'mountain')` for a people of holds under the peaks) and binds itself there.
 *
 * A starting kingdom is then a `NationDef` whose capital is in that region:
 * `nationsInRegion(r)` gives the crowns to choose between, and every one of
 * them is a legal player seat. That is the whole of the start-of-game picker.
 */
export interface RegionDef {
  id: string;
  name: string;
  /** The parenthesised subtitle, the way `MAPREF.md` labels its regions. */
  subtitle: string;
  /** The seam a race roster binds to. Climate and ground, never a people. */
  character: RegionCharacter;
  /**
   * …and the same fact in `races.ts`'s vocabulary: whose country this is.
   *
   * Derived from `character` through `GROUND_HOMELAND`, never declared, so the
   * two can never disagree. `racesOfHomeland(region.homeland)` is the peoples
   * this region can seat, which is the join the race contract asked for.
   */
  homeland: Homeland;
  /** Province indices, ascending. */
  provinces: number[];
  /**
   * Nobody's homeland, and no generated crown is seated here.
   *
   * The reference's largest region is its contested middle, and the heartland
   * already is one: four of its twenty-four holdings are masterless on purpose
   * and the opening of every campaign is built on that. So the contested region
   * is not a new idea bolted on — it is the authored heartland, named.
   */
  contested: boolean;
  /** True where this region runs into the frozen north or the howling south. */
  hostile: boolean;
  /** Bearing of the mass, degrees. For the map's label placement. */
  deg: number;
  /** Mean of the region's province sites: where to write its name. */
  x: number;
  y: number;
}

export interface Realm {
  provinces: ProvinceDef[];
  nations: NationDef[];
  regions: RegionDef[];
  specialLinks: SpecialLink[];
  /** How many of the provinces are the hand-authored heartland. Always 24. */
  heartlandCount: number;
  /** Every cell of open water, coarse. */
  seas: SeaCellDef[];
  /** The sea-lane network, over coastal provinces. */
  lanes: SeaLaneDef[];
  /** The ground itself, at two units a cell. The chart is traced from this. */
  raster: RealmRaster;
}

/**
 * Months under sail per map unit of water, and the whole argument for why a navy
 * is worth having.
 *
 * A land link costs one to three months (`LINK_MARCH`) and covers about twenty
 * units. A ship covers twenty units in a little under half a month. So a fleet
 * moves between three and seven times faster than a host on the same bearing,
 * which is the number that has to be true for any of this to matter: if ships
 * were merely *another* way to get somewhere, nobody would build one. They are
 * the fast way, and the price is that you must own both ends of the journey and
 * a port to leave from.
 */
const SEA_MONTHS_PER_UNIT = 0.0222;

/**
 * The two rules that decide what is a lane, and why there have to be two.
 *
 * **Coasting.** Each harbour is joined to the two nearest others by water. That
 * is the short-haul traffic that hugs a shore, and on its own it is all you get:
 * every province on a coast is a step or two from the next one along it, so a
 * "nearest three" rule joins each of them to its neighbours and *nothing* ever
 * crosses open water.
 *
 * **Crossing.** So there is a second rule, and it is the one that makes the
 * chart interesting: a lane also exists where the water is *much* shorter than
 * the road. Land hops are counted on the graph that has just been built, sea
 * distance on the water, and the two best shortcuts from each harbour become
 * lanes if the saving is worth the shipwright. That produces exactly the routes
 * a cartographer would have drawn by hand — straight across the middle sea,
 * round the great peninsulas, and out to the isles, which have no road at all
 * and therefore an infinite saving.
 */
const LANE_COASTING = 2;
const LANE_CROSSINGS = 2;
/** Hops of road a crossing must save before it is worth being a lane. */
const LANE_MIN_SAVING = 4;
/** No lane is longer than this, in map units. Beyond it, ships coast. */
const LANE_MAX_UNITS = 340;
/**
 * What "no road at all" is worth, in hops, when weighing a crossing.
 *
 * Further than any road on this board, so an island always reads as the furthest
 * thing there is — which is what it is. A *finite* number rather than an
 * enormous one, because the ranking divides by voyage length and an infinity
 * would swamp the division it is supposed to be weighed against.
 */
const LANE_NO_ROAD_HOPS = 24;
/** The lane router's grid: three raster cells, so six map units a step. */
const LANE_STRIDE = 3;
/**
 * Cells of shoreline a holding needs before it counts as standing on the sea —
 * ten of them, so twenty map units of frontage, a little over a province's own
 * width.
 *
 * Higher is tempting and was tried. At fourteen the board drops to seventy-one
 * per cent coastal, which is close to what the lattice world had, and it also
 * changes the lane graph enough that two of `trade`'s emergent-property tests
 * stop finding the crowns they are written to find. Those tests are fragile on
 * purpose — they assert the board still produces a hemmed-in council — and
 * breaking them to tidy a ratio would be trading somebody else's real property
 * for one of mine. Ten is the setting that leaves every one of them green.
 *
 * The honest note is that this world is an archipelago world: eight scattered
 * masses about three provinces across, so most holdings genuinely are on the
 * water and the gate is looser than it was. If the sea layer wants scarcity
 * back, the knob is *fewer, fatter landmasses*, not a bigger number here.
 *
 * The continents pass did exactly what the paragraph above prescribes — the
 * masses are fewer and fatter now — and briefly raised this to 16 as well,
 * which was the double dose the paragraph warns against: 22% of the board
 * coastal, a Hearthlands facing the drowned coast with two harbours in
 * twenty-five holdings, and a *player* whose opening five could not raise a
 * port at all. The shape is the knob that moved; this one goes back to the
 * value the trade tests were tuned green against.
 */
const MIN_FRONTAGE = 10;

/**
 * A piece of ground with this many holdings or fewer has no interior, so every
 * holding on it is on the shore. See the note in `buildRealm`.
 */
const NO_INTERIOR_PIECE = 3;

interface Adjacency {
  a: number;
  b: number;
  /** Raster cells of shared border. A long frontier is a real one. */
  shared: number;
}

function buildRealm(): Realm {
  const r = buildRaster();
  // Rainfall, off the finished mask: `terrainAt` reads it and nothing before
  // this line does.
  RAIN_FIELD = buildRain(r);
  const sites = solveSites(r);
  const cellOwner = grow(r, sites);
  fillHoles(r, cellOwner);
  polish(r, cellOwner, sites);
  fillHoles(r, cellOwner);
  reseat(cellOwner, sites);

  // ── What every site turned out to own ──────────────────────────────────
  /**
   * How much shoreline each holding has, in raster cells — and why it is
   * counted rather than merely noticed.
   *
   * `coastal` is the gate on the whole sea layer: only a coastal province may
   * raise a port, be landed on, or carry a lane. On a coast traced at two units
   * a cell, "touches one cell of water" is true of a holding that happens to
   * reach a creek in the next valley, and on this world that was nine holdings
   * in ten — which makes the word mean nothing. A harbour needs a *frontage*, so
   * a holding is on the sea when it has at least `MIN_FRONTAGE` cells of it.
   *
   * With one exception, and it is load-bearing: a holding with no land
   * neighbour at all is an islet, and an islet must be able to take ship
   * whatever its frontage, or it is ground nobody can ever reach.
   */
  const shoreCells = new Int32Array(sites.length);
  const landNeighbours: Array<Set<number>> = sites.map(() => new Set<number>());
  const adjacent = new Map<number, Adjacency>();
  const pairKey = (a: number, b: number): number => Math.min(a, b) * 8192 + Math.max(a, b);
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const k = rasterKey(col, row);
      const mine = cellOwner[k];
      if (mine < 0) continue;
      for (const [dc, dr] of N4) {
        const c = col + dc;
        const rw = row + dr;
        if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) {
          shoreCells[mine]++;
          continue;
        }
        const other = cellOwner[rasterKey(c, rw)];
        if (other < 0) {
          shoreCells[mine]++;
          continue;
        }
        landNeighbours[mine].add(other);
        if (other === mine || other < mine) continue;
        const key = pairKey(mine, other);
        const hit = adjacent.get(key);
        if (hit) hit.shared++;
        else adjacent.set(key, { a: mine, b: other, shared: 1 });
      }
    }
  }

  const coastalSite = sites.map((_s, i) => shoreCells[i] >= MIN_FRONTAGE);

  /**
   * **A rock in the sea has no interior**, and the frontage rule forgot it.
   *
   * `coastal` is the gate on the whole sea layer and it wants a *frontage*: a
   * holding that happens to reach a creek in the next valley is not a port, and
   * counting one cell of water made nine holdings in ten "coastal", which makes
   * the word mean nothing. That rule is right about continents and wrong about
   * rocks. It used to carry one exception — a holding with no land neighbour at
   * all is an islet and can take ship whatever its frontage — and that exception
   * was one province short of the truth.
   *
   * A **two**-province island is the same fault with a witness. Neither half is
   * a lone province, so neither gets the exception; one of them happens to clear
   * the frontage and the other does not, because its neighbour occupies most of
   * its perimeter. `Stonereach`, eighty-four square units of rock in the Gale,
   * came out `coastal: false` — so it carried no port, `buildLanes` gave it no
   * lane, and the trade builder (which attaches islands *by lane*) found it in
   * nobody's catchment. A holding stood on the board that no army, no fleet and
   * no caravan could ever arrive at, and the only thing that noticed was a
   * coverage assertion three layers away in `tests/trade.test.ts`.
   *
   * So the exception is restated about the **piece of ground**, which is what it
   * was always about, and in two parts:
   *
   *   · a piece too small to have an interior — three holdings or fewer — is
   *     coastal throughout, because every holding on it *is* the shore;
   *   · and any larger piece that somehow ended up with no harbour at all gets
   *     one on its longest shore, because a landmass nobody can walk off and
   *     nobody can sail to is ground that does not exist.
   */
  {
    const piece = new Int32Array(sites.length).fill(-1);
    let pieces = 0;
    for (let i = 0; i < sites.length; i++) {
      if (piece[i] >= 0) continue;
      const id = pieces++;
      const queue = [i];
      piece[i] = id;
      for (let head = 0; head < queue.length; head++) {
        for (const n of landNeighbours[queue[head]]) {
          if (piece[n] >= 0) continue;
          piece[n] = id;
          queue.push(n);
        }
      }
    }
    const size = new Int32Array(pieces);
    const best = new Int32Array(pieces).fill(-1);
    const anyPort = new Uint8Array(pieces);
    for (let i = 0; i < sites.length; i++) {
      const id = piece[i];
      size[id]++;
      if (coastalSite[i]) anyPort[id] = 1;
      if (best[id] < 0 || shoreCells[i] > shoreCells[best[id]]) best[id] = i;
    }
    for (let i = 0; i < sites.length; i++) {
      if (size[piece[i]] <= NO_INTERIOR_PIECE) coastalSite[i] = true;
    }
    for (let id = 0; id < pieces; id++) {
      if (!anyPort[id] && best[id] >= 0) coastalSite[best[id]] = true;
    }
  }

  // ── The order of the table ─────────────────────────────────────────────
  // The hand-authored twenty-four are 0..23 and every id an existing test looks
  // up still resolves to the province it always did.
  // Which region each square of ground fell to, before anything is named:
  // terrain is a property of the *place*, so a holding has to know its region
  // before it knows whether it is desert or fen.
  const regionCells = growRegions(r);
  const seats = seatHeartland(sites, coastalSite, components(r.land).label, regionCells);
  const provinceOf = new Int32Array(sites.length).fill(-1);
  seats.forEach((s, i) => {
    provinceOf[s] = i;
  });
  let next = HEARTLAND.length;
  for (let i = 0; i < sites.length; i++) {
    if (provinceOf[i] < 0) provinceOf[i] = next++;
  }

  const provinces: ProvinceDef[] = new Array(sites.length);
  const regionOfProvince = new Int32Array(sites.length).fill(0);
  const taken = new Set<string>(HEARTLAND.map((h) => h.name));

  seats.forEach((siteIdx, hi) => {
    const h = HEARTLAND[hi];
    const s = sites[siteIdx];
    provinces[hi] = {
      id: h.id,
      name: h.name,
      flavour: h.flavour,
      x: s.x,
      y: s.y,
      terrain: h.terrain ?? 'plain',
      wealth: h.wealth ?? 10,
      levy: h.levy ?? 8,
      fort: h.fort ?? 0,
      resource: h.resource ?? '',
      yield: h.yield ?? 0,
      owner: h.owner ?? '',
      coastal: coastalSite[siteIdx],
      island: false,
      links: [],
    };
    regionOfProvince[hi] = HEART_REGION;
  });

  const wealthBase: Record<ProvinceTerrain, number> = {
    plain: 11,
    hill: 10,
    forest: 9,
    marsh: 8,
    coast: 11,
    mountain: 12,
    ruin: 8,
    // Rich ground nobody can farm easily, poor ground nobody wants, and ice.
    jungle: 10,
    desert: 8,
    ice: 6,
  };
  const levyBase: Record<ProvinceTerrain, number> = {
    plain: 10,
    hill: 9,
    forest: 11,
    marsh: 11,
    coast: 9,
    mountain: 7,
    ruin: 8,
    jungle: 11,
    desert: 9,
    ice: 6,
  };

  for (let i = 0; i < sites.length; i++) {
    const idx = provinceOf[i];
    if (idx < HEARTLAND.length) continue;
    const s = sites[i];
    const region = Math.max(0, regionCells[rasterKey(s.col, s.row)]);
    const terrain = terrainAt(s.x, s.y, coastalSite[i], REGION_SEEDS[region].character);
    const key = s.key;
    const name = placeName(key, taken);
    const res = pick(RESOURCE_BY_TERRAIN[terrain], key, 3, 1543);
    provinces[idx] = {
      id: slug(name) || `march_${idx}`,
      name,
      flavour: pick(FLAVOURS[terrain], key, 11, 2749),
      x: s.x,
      y: s.y,
      terrain,
      wealth: Math.round(span(-3, 6, key, 21, 3371) + wealthBase[terrain]),
      levy: Math.round(span(-3, 5, key, 31, 4271) + levyBase[terrain]),
      // Most ground is open. A wall is news.
      fort: hash2(key, 41, 5279) < 0.17 ? 1 : 0,
      resource: res,
      yield: res ? Math.round(span(3, 9, key, 51, 6199)) : 0,
      owner: '',
      coastal: coastalSite[i],
      island: true,
      links: [],
    };
    regionOfProvince[idx] = region;
  }

  // The raster, restated in province indices, for the chart.
  const owner = new Int32Array(COLS * ROWS).fill(-1);
  for (let i = 0; i < cellOwner.length; i++) {
    owner[i] = cellOwner[i] < 0 ? -1 : provinceOf[cellOwner[i]];
  }

  // ── The graph, and then `island` re-derived from it ─────────────────────
  const edges: Adjacency[] = [...adjacent.values()]
    .map((e) => ({ a: provinceOf[e.a], b: provinceOf[e.b], shared: e.shared }))
    .map((e) => ({ a: Math.min(e.a, e.b), b: Math.max(e.a, e.b), shared: e.shared }))
    .sort((x, y) => x.a - y.a || x.b - y.b);
  const specialLinks = buildLinks(provinces, edges);
  markIslands(provinces);

  // ── The sea, and the routes through it ─────────────────────────────────
  const seas = buildSeas(r);
  const lanes = buildLanes(provinces, r, owner);

  // ── The regions, before the powers — a crown is seated *in* a region ────
  const regions = assignRegions(provinces, regionOfProvince);

  // ── The powers, and who holds what ─────────────────────────────────────
  const nations = buildNations(provinces, regions);

  return {
    provinces,
    nations,
    regions,
    specialLinks,
    heartlandCount: HEARTLAND.length,
    seas,
    lanes,
    raster: {
      cols: COLS,
      rows: ROWS,
      cell: WORLD.cell,
      originX: WORLD.originX,
      originY: WORLD.originY,
      owner,
      offshore: r.offshore,
      height: HEIGHT_FIELD,
    },
  };
}

/**
 * Set `island` from the road graph itself, once the roads exist.
 *
 * `island` is the simulation's answer to *can a host walk here from home*, the
 * AI reads it to decide whether a target needs ships, and a single province
 * where the flag and the graph disagree is a host ordered to march somewhere it
 * can never arrive. So it is derived from the thing it is a claim about rather
 * than from anything the mask happened to know earlier.
 */
function markIslands(provinces: ProvinceDef[]): void {
  const byId = new Map<string, number>();
  provinces.forEach((p, i) => byId.set(p.id, i));
  const adj: number[][] = provinces.map(() => []);
  provinces.forEach((p, i) => {
    for (const other of p.links) {
      const j = byId.get(other);
      if (j === undefined) continue;
      adj[i].push(j);
      adj[j].push(i);
    }
  });

  for (const p of provinces) p.island = true;
  provinces[0].island = false;
  const queue = [0];
  for (let head = 0; head < queue.length; head++) {
    for (const n of adj[queue[head]]) {
      if (!provinces[n].island) continue;
      provinces[n].island = false;
      queue.push(n);
    }
  }
}

// ── The sea ────────────────────────────────────────────────────────────────

/** The lane router's coarse water grid, padded by a ring of open ocean. */
interface LaneGrid {
  cols: number;
  rows: number;
  cell: number;
  water: Uint8Array;
}

function laneGrid(r: LandRaster): LaneGrid {
  const cols = Math.ceil(COLS / LANE_STRIDE) + 2;
  const rows = Math.ceil(ROWS / LANE_STRIDE) + 2;
  const water = new Uint8Array(cols * rows).fill(1);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      let dry = 0;
      let seen = 0;
      for (let dr = 0; dr < LANE_STRIDE; dr++) {
        for (let dc = 0; dc < LANE_STRIDE; dc++) {
          const c = (col - 1) * LANE_STRIDE + dc;
          const rw = (row - 1) * LANE_STRIDE + dr;
          if (c < 0 || rw < 0 || c >= COLS || rw >= ROWS) continue;
          seen++;
          if (r.land[rasterKey(c, rw)]) dry++;
        }
      }
      // A ship needs room: a block that is half beach is not navigable.
      if (seen > 0 && dry * 2 >= seen) water[row * cols + col] = 0;
    }
  }
  return { cols, rows, cell: WORLD.cell * LANE_STRIDE, water };
}

function laneX(g: LaneGrid, col: number): number {
  return WORLD.originX + (col - 1) * g.cell + g.cell / 2;
}
function laneY(g: LaneGrid, row: number): number {
  return WORLD.originY + (row - 1) * g.cell + g.cell / 2;
}

/** Every cell of open water on the coarse grid. Kept for callers, not for the chart. */
function buildSeas(r: LandRaster): SeaCellDef[] {
  const g = laneGrid(r);
  const out: SeaCellDef[] = [];
  for (let row = 0; row < g.rows; row++) {
    for (let col = 0; col < g.cols; col++) {
      if (!g.water[row * g.cols + col]) continue;
      const x = laneX(g, col);
      const y = laneY(g, row);
      out.push({
        col,
        row,
        x,
        y,
        interior:
          x > WORLD.originX && y > WORLD.originY &&
          x < WORLD.originX + WORLD.w && y < WORLD.originY + WORLD.h,
      });
    }
  }
  return out;
}

/** Road hops from every province to every other. -1 where no road runs. */
function landDistances(provinces: ProvinceDef[]): number[][] {
  const byId = new Map<string, number>();
  provinces.forEach((p, i) => byId.set(p.id, i));
  const adj: number[][] = provinces.map(() => []);
  provinces.forEach((p, i) => {
    for (const other of p.links) {
      const j = byId.get(other);
      if (j === undefined) continue;
      adj[i].push(j);
      adj[j].push(i);
    }
  });
  return provinces.map((_, from) => {
    const dist = new Array<number>(provinces.length).fill(-1);
    dist[from] = 0;
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
      const at = queue[head];
      for (const n of adj[at]) {
        if (dist[n] >= 0) continue;
        dist[n] = dist[at] + 1;
        queue.push(n);
      }
    }
    return dist;
  });
}

/**
 * The lane network, read off the water rather than declared.
 *
 * Breadth-first from every coastal holding's own doorstep over the eight-
 * neighbourhood of the water — diagonals included, because a ship does not turn
 * square corners — which gives the distance between every pair of coasts and the
 * actual route between them. Then two rules pick which of those crossings is a
 * lane: the few shortest from each end, and anything that saves a long walk.
 * Finally union-find restores whatever the picking cut off, per basin, so no
 * harbour is left off the network by an unlucky tie.
 *
 * The consequence worth stating: nothing here knows what a port is. A lane is a
 * fact about the water, and a port is a decision a crown makes on top of one.
 */
function buildLanes(
  provinces: ProvinceDef[],
  r: LandRaster,
  owner: Int32Array,
): SeaLaneDef[] {
  const g = laneGrid(r);
  const maxSteps = Math.round(LANE_MAX_UNITS / g.cell);

  /** The water each coastal holding can push off into. */
  const doorstep = new Map<number, number[]>();
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const p = owner[rasterKey(col, row)];
      if (p < 0 || !provinces[p].coastal) continue;
      for (const [dc, dr] of N8) {
        const c = col + dc;
        const rw = row + dr;
        const wet = c < 0 || rw < 0 || c >= COLS || rw >= ROWS || !r.land[rasterKey(c, rw)];
        if (!wet) continue;
        const lc = Math.floor(c / LANE_STRIDE) + 1;
        const lr = Math.floor(rw / LANE_STRIDE) + 1;
        for (const [ec, er] of [[0, 0], ...N8]) {
          const gc = Math.max(0, Math.min(g.cols - 1, lc + ec));
          const gr = Math.max(0, Math.min(g.rows - 1, lr + er));
          const gk = gr * g.cols + gc;
          if (!g.water[gk]) continue;
          const list = doorstep.get(p);
          if (list) {
            if (!list.includes(gk)) list.push(gk);
          } else doorstep.set(p, [gk]);
        }
      }
    }
  }

  const coasts = provinces
    .map((_, i) => i)
    .filter((i) => provinces[i].coastal && (doorstep.get(i)?.length ?? 0) > 0);
  if (coasts.length < 2) return [];

  const sweep = (sources: number[]): Map<number, { d: number; from: number }> => {
    const seen = new Map<number, { d: number; from: number }>();
    const queue: number[] = [];
    for (const k of sources) {
      if (seen.has(k)) continue;
      seen.set(k, { d: 0, from: -1 });
      queue.push(k);
    }
    for (let head = 0; head < queue.length; head++) {
      const k = queue[head];
      const here = seen.get(k);
      if (!here || here.d >= maxSteps + 2) continue;
      const col = k % g.cols;
      const row = (k - col) / g.cols;
      for (const [dc, dr] of N8) {
        const c = col + dc;
        const rw = row + dr;
        if (c < 0 || rw < 0 || c >= g.cols || rw >= g.rows) continue;
        const nk = rw * g.cols + c;
        if (!g.water[nk] || seen.has(nk)) continue;
        seen.set(nk, { d: here.d + 1, from: k });
        queue.push(nk);
      }
    }
    return seen;
  };

  interface Crossing {
    a: number;
    b: number;
    steps: number;
    route: Array<{ x: number; y: number }>;
  }
  const crossings = new Map<number, Crossing>();
  const pairKey = (a: number, b: number): number => Math.min(a, b) * 8192 + Math.max(a, b);
  const fromHere = new Map<number, Crossing[]>();

  for (const a of coasts) {
    const reach = sweep(doorstep.get(a) ?? []);
    const mine: Crossing[] = [];
    for (const b of coasts) {
      if (b === a) continue;
      let best = Infinity;
      let landing = -1;
      for (const k of doorstep.get(b) ?? []) {
        const hit = reach.get(k);
        if (hit && hit.d < best) {
          best = hit.d;
          landing = k;
        }
      }
      if (landing < 0 || best > maxSteps) continue;
      const route: Array<{ x: number; y: number }> = [];
      for (let k = landing; k >= 0; ) {
        const col = k % g.cols;
        const row = (k - col) / g.cols;
        route.push({ x: laneX(g, col), y: laneY(g, row) });
        const step = reach.get(k);
        if (!step || step.from < 0) break;
        k = step.from;
      }
      route.reverse();
      mine.push({ a, b, steps: best, route });
      const crossing: Crossing = {
        a: Math.min(a, b),
        b: Math.max(a, b),
        steps: best,
        route: a < b ? route : route.slice().reverse(),
      };
      const key = pairKey(a, b);
      const seen = crossings.get(key);
      if (!seen || crossing.steps < seen.steps) crossings.set(key, crossing);
    }
    mine.sort((x, y) => x.steps - y.steps || x.b - y.b);
    fromHere.set(a, mine);
  }

  const landHops = landDistances(provinces);
  const wanted = new Set<number>();
  for (const a of coasts) {
    const mine = fromHere.get(a) ?? [];
    for (let i = 0; i < mine.length && i < LANE_COASTING; i++) wanted.add(pairKey(a, mine[i].b));
    /**
     * …and the crossings: where the sea is much shorter than the road.
     *
     * Ranked by saving *per step of voyage* rather than by saving outright. An
     * end with no road to it counts as `LANE_NO_ROAD_HOPS` away, which on raw
     * saving is overwhelming — every harbour's two best shortcuts would be the
     * isles, whichever side of the world they were on, and almost nothing would
     * cross the middle sea. Dividing by the length of the voyage asks the
     * question a shipmaster would: not "how much walking does this save" but
     * "how much walking does this save *per month at sea*".
     */
    const hops = landHops[a];
    const saving = (c: Crossing): number =>
      (hops[c.b] < 0 ? LANE_NO_ROAD_HOPS : hops[c.b]) - c.steps * g.cell * 0.05;
    const worth = (c: Crossing): number => saving(c) / (1 + c.steps);
    const best = mine
      .filter((c) => saving(c) >= LANE_MIN_SAVING)
      .sort((x, y) => worth(y) - worth(x) || x.steps - y.steps || x.b - y.b);
    for (let i = 0; i < best.length && i < LANE_CROSSINGS; i++) wanted.add(pairKey(a, best[i].b));
  }

  // Anything the picking cut off gets a lane back, per basin: two coasts on the
  // same water must be able to reach each other by sea or the network is a lie.
  const parent = new Map<number, number>();
  const find = (x: number): number => {
    let root = x;
    while ((parent.get(root) ?? root) !== root) root = parent.get(root) ?? root;
    let cur = x;
    while ((parent.get(cur) ?? cur) !== cur) {
      const nxt = parent.get(cur) ?? cur;
      parent.set(cur, root);
      cur = nxt;
    }
    return root;
  };
  const union = (a: number, b: number): boolean => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return false;
    parent.set(ra, rb);
    return true;
  };
  for (const i of coasts) parent.set(i, i);
  const ordered = [...crossings.values()].sort(
    (x, y) => x.steps - y.steps || x.a - y.a || x.b - y.b,
  );
  for (const c of ordered) if (wanted.has(pairKey(c.a, c.b))) union(c.a, c.b);
  for (const c of ordered) {
    if (wanted.has(pairKey(c.a, c.b))) continue;
    if (!union(c.a, c.b)) continue;
    wanted.add(pairKey(c.a, c.b));
  }

  const out: SeaLaneDef[] = [];
  for (const c of ordered) {
    if (!wanted.has(pairKey(c.a, c.b))) continue;
    out.push({
      a: c.a,
      b: c.b,
      months: Math.max(1, Math.round(c.steps * g.cell * SEA_MONTHS_PER_UNIT)),
      route: c.route,
    });
  }
  out.sort((x, y) => x.a - y.a || x.b - y.b);
  return out;
}

// ── Roads, fords and passes ────────────────────────────────────────────────

/**
 * Every province that touches another is a candidate road; roughly a third are
 * dropped so the board has chokepoints rather than a mesh, and then union-find
 * restores any edge whose loss would have split a landmass — which is cheaper
 * and far more reliable than trying to be careful about which edges to drop.
 *
 * **The rule that makes a pass a pass.** You cannot walk along a crest. An edge
 * between two mountain provinces is almost always cut, so a range is a wall
 * rather than a road; edges from a mountain down to the country either side
 * mostly survive, so a range is crossed *through* a mountain province and only
 * through the ones that kept a link on both sides. Which ones those are is not a
 * die either: the chance of sparing a crest crossing rises where the range's own
 * crest runs low, so **the passes come out at the saddles**, which is where they
 * are on a real map and where a player would go looking for them.
 *
 * The heartland's own edges are taken verbatim and no generated edge is ever
 * added between two heartland provinces. The old board survives intact inside
 * the new one; only its outward connections are grown.
 */
function buildLinks(provinces: ProvinceDef[], adjacency: Adjacency[]): SpecialLink[] {
  const edges: Array<{ a: number; b: number }> = [];
  const have = new Set<number>();
  const edgeKey = (a: number, b: number): number => Math.min(a, b) * 8192 + Math.max(a, b);

  const heartIds = new Set(HEARTLAND.map((h) => h.id));
  const idToIdx = new Map<string, number>();
  provinces.forEach((p, i) => idToIdx.set(p.id, i));
  for (const h of HEARTLAND) {
    const a = idToIdx.get(h.id) ?? -1;
    for (const otherId of h.links) {
      const b = idToIdx.get(otherId) ?? -1;
      if (a < 0 || b < 0) continue;
      const k = edgeKey(a, b);
      if (have.has(k)) continue;
      have.add(k);
      edges.push({ a: Math.min(a, b), b: Math.max(a, b) });
    }
  }

  const candidates: Array<{ a: number; b: number; keep: boolean }> = [];
  for (const e of adjacency) {
    if (have.has(edgeKey(e.a, e.b))) continue;
    if (heartIds.has(provinces[e.a].id) && heartIds.has(provinces[e.b].id)) continue;
    // Two provinces that merely graze each other are not neighbours.
    if (e.shared < 3) continue;
    const roll = hash2(e.a, e.b, 3299);
    const alpineA = provinces[e.a].terrain === 'mountain';
    const alpineB = provinces[e.b].terrain === 'mountain';
    const mx = (provinces[e.a].x + provinces[e.b].x) / 2;
    const my = (provinces[e.a].y + provinces[e.b].y) / 2;
    const crest = ridgeAt(mx, my);
    const brief = e.shared < 6;
    let keep: boolean;
    if (alpineA && alpineB) {
      // The saddles are the passes: a low crest is a way through.
      keep = roll < 0.07 + 0.44 * Math.max(0, 1 - crest.crest);
    } else if (alpineA !== alpineB) {
      keep = roll < 0.55;
    } else {
      keep = roll < (brief ? 0.3 : 0.74);
    }
    candidates.push({ a: e.a, b: e.b, keep });
  }

  const parent = provinces.map((_, i) => i);
  const find = (x: number): number => {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    let cur = x;
    while (parent[cur] !== cur) {
      const nxt = parent[cur];
      parent[cur] = root;
      cur = nxt;
    }
    return root;
  };
  const union = (a: number, b: number): boolean => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return false;
    parent[ra] = rb;
    return true;
  };

  for (const e of edges) union(e.a, e.b);
  for (const cand of candidates) {
    if (!cand.keep) continue;
    const k = edgeKey(cand.a, cand.b);
    if (have.has(k)) continue;
    have.add(k);
    edges.push({ a: cand.a, b: cand.b });
    union(cand.a, cand.b);
  }
  // Anything the dropping cut off gets its road back.
  for (const cand of candidates) {
    const k = edgeKey(cand.a, cand.b);
    if (have.has(k)) continue;
    if (!union(cand.a, cand.b)) continue;
    have.add(k);
    edges.push({ a: cand.a, b: cand.b });
  }

  edges.sort((x, y) => x.a - y.a || x.b - y.b);
  for (const e of edges) provinces[e.a].links.push(provinces[e.b].id);

  const special: SpecialLink[] = HEART_SPECIAL.slice();
  const declared = new Set(HEART_SPECIAL.map((s) => `${s.a}|${s.b}`));
  const isDeclared = (a: string, b: string): boolean =>
    declared.has(`${a}|${b}`) || declared.has(`${b}|${a}`);

  for (const e of edges) {
    const pa = provinces[e.a];
    const pb = provinces[e.b];
    if (heartIds.has(pa.id) && heartIds.has(pb.id)) continue;
    if (isDeclared(pa.id, pb.id)) continue;
    const kind = linkKindBetween(pa, pb, e.a, e.b);
    if (kind === LinkKind.Wild) continue;
    special.push({ a: pa.id, b: pb.id, kind });
  }
  return special;
}

/**
 * What kind of crossing two provinces share.
 *
 * Mountains mean a pass and that is not negotiable — it is the one piece of
 * terrain the player must be able to read off the map before committing to a
 * march. Water means a ford. Two prosperous, walkable provinces are likely to
 * have built a road between them, because roads follow money.
 */
function linkKindBetween(a: ProvinceDef, b: ProvinceDef, ia: number, ib: number): LinkKindId {
  const roll = hash2(ia, ib, 8623);
  if (a.terrain === 'mountain' || b.terrain === 'mountain') return LinkKind.Pass;
  const wet = (t: ProvinceTerrain): boolean => t === 'marsh' || t === 'coast';
  if (wet(a.terrain) || wet(b.terrain)) {
    return roll < 0.55 ? LinkKind.River : LinkKind.Wild;
  }
  const walkable = (t: ProvinceTerrain): boolean => t === 'plain' || t === 'hill' || t === 'ruin';
  if (walkable(a.terrain) && walkable(b.terrain)) {
    const money = (a.wealth + b.wealth) / 30;
    return roll < 0.22 + money * 0.28 ? LinkKind.Road : LinkKind.Wild;
  }
  return roll < 0.14 ? LinkKind.Road : LinkKind.Wild;
}

/**
 * The regions, assembled from the ground each seed won.
 *
 * The heartland is forced whole into its own region regardless of where its
 * holdings ended up. Those twenty-four are authored and their region is authored
 * with them.
 */
function assignRegions(provinces: ProvinceDef[], regionOfProvince: Int32Array): RegionDef[] {
  const contested = REGION_SEEDS.findIndex((r) => r.contested === true);
  const members: number[][] = REGION_SEEDS.map(() => []);
  for (let i = 0; i < provinces.length; i++) {
    const k = i < HEARTLAND.length ? contested : Math.max(0, regionOfProvince[i]);
    members[k].push(i);
  }

  return REGION_SEEDS.map((spec, i) => {
    const list = members[i].slice().sort((a, b) => a - b);
    let x = 0;
    let y = 0;
    for (const p of list) {
      x += provinces[p].x;
      y += provinces[p].y;
    }
    const heart = { x: spec.x, y: spec.y };
    let deg = (Math.atan2((heart.y - CY) / HY, (heart.x - CX) / HX) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    return {
      id: spec.id,
      name: spec.name,
      subtitle: spec.subtitle,
      character: spec.character,
      homeland: GROUND_HOMELAND[spec.character],
      provinces: list,
      contested: spec.contested === true,
      hostile:
        list.filter((p) => isWasteAt(provinces[p].y)).length >=
        Math.max(2, list.length * 0.25),
      deg,
      x: list.length ? x / list.length : heart.x,
      y: list.length ? y / list.length : heart.y,
    };
  }).filter((r) => r.provinces.length > 0);
}


// ── The powers ─────────────────────────────────────────────────────────────

/**
 * Thirty kingdoms wearing eight coats is not thirty kingdoms.
 *
 * A nation's whole personality is three numbers and a sentence, which is a
 * deliberate constraint from the original eight: an AI whose temperament fits on
 * one line is an AI a player can learn to read. Generating sixteen more by
 * rolling three numbers uniformly would produce sixteen indistinguishable
 * mediocrities clustered on 0.5 — the mean of a uniform is where everything ends
 * up. So each generated power is cut from a *doctrine*: a named archetype with
 * its own corner of the space, its own naming register and its own creeds. The
 * variation within a doctrine is what stops two raiders being the same raider;
 * the doctrines are what stop everybody being the same nation.
 */
interface Doctrine {
  id: string;
  aggression: [number, number];
  caution: [number, number];
  militarism: [number, number];
  bodies: readonly string[];
  creeds: readonly string[];
}

const DOCTRINES: readonly Doctrine[] = [
  {
    id: 'raider',
    aggression: [0.78, 0.95],
    caution: [0.12, 0.3],
    militarism: [0.78, 0.92],
    bodies: ['Host', 'Reavers', 'Riding', 'Wolfmoot', 'Warband', 'Scourge'],
    creeds: [
      'We do not hold. We arrive, and then we are somewhere else, richer.',
      'A wall is a promise that there is something behind it worth the climb.',
      'Winter is coming for all of us. We would rather it found us fed.',
      'Ask anyone what we want. Then look at what we have taken. They agree.',
    ],
  },
  {
    id: 'bastion',
    aggression: [0.12, 0.3],
    caution: [0.78, 0.92],
    militarism: [0.42, 0.6],
    bodies: ['Wardens', 'Bulwark', 'Watch', 'Keepers', 'Threshold', 'Bastion'],
    creeds: [
      'Nothing we hold has ever been taken. That is not a boast, it is a ledger.',
      'Let them come. Let them come a second time. Few do.',
      'We have no ambitions past the last stone of our own wall.',
      'Patience is cheaper than soldiers and we are not a wealthy people.',
    ],
  },
  {
    id: 'merchant',
    aggression: [0.28, 0.45],
    caution: [0.6, 0.78],
    militarism: [0.3, 0.48],
    bodies: ['Concord', 'League', 'Ledger', 'Charter', 'Bench', 'Compact'],
    creeds: [
      'Everything has a price. We have simply written more of them down.',
      'We buy the war before it is fought, and usually we buy it from happening.',
      'Our soldiers are other people’s soldiers. That is the arrangement.',
      'A siege costs both sides. We would rather be the side selling the grain.',
    ],
  },
  {
    id: 'zealot',
    aggression: [0.68, 0.86],
    caution: [0.32, 0.5],
    militarism: [0.72, 0.88],
    bodies: ['Chorus', 'Covenant', 'Confession', 'Testament', 'Fervour', 'Sept'],
    creeds: [
      'We were told to. We were told when. We were not told to ask.',
      'The ground is not ours and never was. We are only clearing it.',
      'Doubt is a luxury of people nobody is coming for.',
      'Every province we take says the words back to us. Eventually it means them.',
    ],
  },
  {
    id: 'opportunist',
    aggression: [0.52, 0.7],
    caution: [0.42, 0.6],
    militarism: [0.55, 0.72],
    bodies: ['Company', 'Circuit', 'Muster', 'Free Banner', 'Signatory', 'Hand'],
    creeds: [
      'We are at war with whoever is losing. It has served us well.',
      'We arrive late, we arrive whole, and we arrive on the winning side.',
      'Alliances are seasonal here, like the flooding.',
      'Nobody has ever accused us of principle and nobody has ever beaten us either.',
    ],
  },
  {
    id: 'hermit',
    aggression: [0.08, 0.22],
    caution: [0.82, 0.95],
    militarism: [0.28, 0.44],
    bodies: ['Remnant', 'Recluse', 'Kindred', 'Fastness', 'Enclosure', 'Estate'],
    creeds: [
      'We want nothing from you and will give nothing to you. Walk on.',
      'The last time we went out into the realm we came back smaller.',
      'History has been tried. We are having a rest from it.',
      'Our borders are exactly where they were four hundred years ago.',
    ],
  },
  {
    id: 'warlord',
    aggression: [0.72, 0.9],
    caution: [0.3, 0.46],
    militarism: [0.68, 0.84],
    bodies: ['Throne', 'Crown', 'Marchers', 'Dominion', 'Yoke', 'Fist'],
    creeds: [
      'One hand on the realm, and it will be one hand or none.',
      'We are not conquering. We are correcting a very old mistake.',
      'A crown that stops taking is a crown somebody else is measuring.',
      'They will call it tyranny for a generation and order for six.',
    ],
  },
  {
    id: 'steward',
    aggression: [0.4, 0.56],
    caution: [0.52, 0.7],
    militarism: [0.52, 0.68],
    bodies: ['Assembly', 'Moot', 'Stewardship', 'Pact', 'Commons', 'Order'],
    creeds: [
      'We take what we can hold and hold what we take. Nothing else is policy.',
      'Every province we own can feed itself. Ask the others whether theirs can.',
      'War when it is necessary, and we are hard to convince about necessary.',
      'Our roads are mended, our garrisons are paid, and our neighbours are nervous.',
    ],
  },
];

const NATION_ADJECTIVES = [
  'Iron', 'Sable', 'Hollow', 'Pale', 'Riven', 'Ninefold', 'Weeping', 'Salt', 'Thorned',
  'Black', 'Long', 'Cold', 'Grave', 'Silent', 'Ragged', 'Crooked', 'Last', 'Quiet',
  'Wandering', 'Hungry', 'Patient', 'Brazen', 'Sunken', 'Twicefold', 'Ember', 'Grey',
] as const;

/**
 * How many powers sit at the table — and it is no longer a number written here.
 *
 * It was twenty-two generated crowns, chosen so that each held about five
 * provinces with roughly an eighth of the board left masterless. Both of those
 * ratios are load-bearing and both survive; what changed is who decides. The
 * count is now the sum of `crownsFor` over the regions, because the owner's
 * instruction was *scatter a few kingdoms in each region* and a global total
 * cannot honour that — a farthest-point sample over the whole world will
 * cheerfully put six crowns on one continent and none on another, which is
 * exactly what the old one did the moment the world stopped being walkable.
 *
 * Two to five per region, floored so that every region is somewhere a border
 * war can happen without crossing water, and none at all in the contested
 * middle. On this board that comes to a little over thirty in all.
 */


/**
 * How many crowns a region carries — declared, not derived.
 *
 * It used to be proportional to the ground, which was the only thing available
 * when a region *was* a landmass. Now that a region is a named place with a seed
 * of its own, the number of kingdoms in it is part of what the place *is* — a
 * crowded league of free cities and an empty frozen march are different in kind,
 * not only in size — so it is written down beside the name where lore can argue
 * with it. Floored at two whatever the table says, because a region with one
 * crown in it is a region where no border war can happen without crossing water,
 * and capped by the ground actually available.
 */
function crownsFor(region: RegionDef): number {
  if (region.contested) return 0;
  const spec = REGION_SEEDS.find((r) => r.id === region.id);
  const want = spec ? spec.crowns : 2;
  if (region.provinces.length < 4) return Math.min(1, want);
  return Math.max(2, Math.min(want, Math.round(region.provinces.length / 3)));
}

/**
 * Seats within one region, spread as far apart as the region allows.
 *
 * Farthest-point sampling on *map distance* rather than on road hops, and that
 * is a change the fragmented world forced. Hops were the right measure when the
 * realm was one walkable component — they are what an army spends. They are
 * useless now: half the board has no road to the other half, so a hop count
 * from the existing seats comes back -1 almost everywhere. The old
 * whole-world sampler duly seated fifteen crowns on the heartland's continent
 * and *none at all* on the other six, which is the failure this whole function
 * exists to make impossible. Distance across the map is the honest measure of
 * "spread out" when the thing between two provinces might be an ocean.
 */
function seatsInRegion(provinces: ProvinceDef[], region: RegionDef, count: number): number[] {
  /**
   * Nobody is seated on the heartland's doorstep.
   *
   * The contested middle is the only neighbourhood the opening of a campaign is
   * balanced around, and a generated crown seated on a province that borders it
   * arrives holding ground the authored eight were meant to be fighting over. It
   * showed up as something worse than an imbalance: a crown seated on the one
   * stretch of shore the Ashen Crown could reach left the *player* landlocked
   * from the first month, with no province they could ever raise a harbour on
   * and the whole naval layer invisible. A seat one province further out costs
   * that crown nothing and costs the opening nothing.
   */
  const byId = new Map<string, number>();
  provinces.forEach((p, i) => byId.set(p.id, i));
  const doorstep = new Set<number>();
  provinces.forEach((p, i) => {
    for (const id of p.links) {
      const j = byId.get(id);
      if (j === undefined) continue;
      if (i < HEARTLAND.length) doorstep.add(j);
      if (j < HEARTLAND.length) doorstep.add(i);
    }
  });
  const pool = region.provinces.filter(
    (i) => i >= HEARTLAND.length && !provinces[i].owner && !doorstep.has(i),
  );
  if (pool.length === 0 || count <= 0) return [];

  const out: number[] = [];
  for (let k = 0; k < count && out.length < pool.length; k++) {
    let best = -1;
    let bestScore = -Infinity;
    for (const i of pool) {
      if (out.includes(i)) continue;
      let near = Infinity;
      for (const seat of out) {
        near = Math.min(
          near,
          Math.hypot(provinces[i].x - provinces[seat].x, provinces[i].y - provinces[seat].y),
        );
      }
      // The first seat has nothing to be far from, so wealth and the hash pick
      // it; after that distance dominates and the rest only break ties.
      const spread = out.length === 0 ? 0 : near;
      const score = spread * 10 + provinces[i].wealth + hash2(i, k, 9137) * 6;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0) break;
    out.push(best);
  }
  return out;
}

/**
 * Which people a crown belongs to, decided by the ground it is seated on.
 *
 * **This is the whole answer to "does each race live somewhere that suits it".**
 * A crown's people is not rolled and is not dealt round-robin over the faction
 * table — it is read off the region, whose `homeland` is read off its
 * `character`, which is read off one line of `REGION_SEEDS`. The dwarves are in
 * Karrun-Dur because Karrun-Dur is the `'mountain'` region, and moving that
 * word to another line moves the dwarves with their kingdoms, their leaders and
 * their banners.
 *
 * Two rules beyond that, and both come from the owner's standing instruction
 * that a region is a *neighbourhood of rivals* rather than a wash of colour:
 *
 *   · Where a homeland has more than one people — the heartland has two — the
 *     crowns of a region **alternate**, so a border war between two peoples who
 *     both belong there is possible without crossing water.
 *   · Two crowns of one people are never the same *house*. `leaderForKingdom`
 *     is indexed by how many crowns of that race have already been seated
 *     anywhere in the world, and it wraps, so the second Karrun hold is a
 *     different hall with a different grudge.
 *
 * Nothing here rolls a die, and that is deliberate: `leaderForKingdom` is a
 * pure index, so which house a kingdom gets cannot perturb the world's noise
 * stream. Change who lives where and the coastline does not move.
 */
function raceForSeat(region: RegionDef, ordinalInRegion: number): RaceId {
  const pool = racesOfHomeland(region.homeland);
  // Ground no people claims still gets crowns; they are whoever the world's
  // first-declared people are, which is what a frontier march is.
  if (pool.length === 0) return RACES[0].id;
  return pool[ordinalInRegion % pool.length].id;
}

function buildNations(provinces: ProvinceDef[], regions: RegionDef[]): NationDef[] {
  const nations: NationDef[] = HEART_NATIONS.map((n) => ({ ...n }));
  /**
   * A few crowns in every region, rather than a sample over the whole world —
   * and the region each seat came from is *kept*, because a crown's people is
   * decided by the ground it stands on and a flat list of seats has thrown that
   * away by the time anybody asks.
   */
  const seats: Array<{ seat: number; region: RegionDef; nth: number }> = [];
  for (const region of regions) {
    seatsInRegion(provinces, region, crownsFor(region)).forEach((seat, nth) => {
      seats.push({ seat, region, nth });
    });
  }
  const takenNames = new Set<string>(nations.map((n) => n.name));

  /**
   * How many crowns of each people have been seated so far.
   *
   * The authored eight count, which is why this is seeded from them rather
   * than started at zero: the Karrun-Dur of the heartland is the first Karrun
   * house, and the hold generated in the peaks is the second.
   */
  const houses = new Map<RaceId, number>();
  const nextHouse = (raceId: RaceId): number => {
    const n = houses.get(raceId) ?? 0;
    houses.set(raceId, n + 1);
    return n;
  };
  for (const n of nations) n.leaderId = leaderForKingdom(n.raceId, nextHouse(n.raceId)).id;

  seats.forEach(({ seat, region, nth }, k) => {
    const doctrine = DOCTRINES[Math.floor(hash2(seat, 7, 1319) * DOCTRINES.length) % DOCTRINES.length];
    const raceId = raceForSeat(region, nth);
    const capital = provinces[seat];
    const seatName = capital.name.replace(/^The\s+/, '');

    let name = '';
    for (let salt = 0; salt < 48 && !name; salt++) {
      const form = hash2(seat, salt + 100, 3457);
      const body = pick(doctrine.bodies, seat, salt, 4523);
      const candidate =
        form < 0.55
          ? `The ${pick(NATION_ADJECTIVES, seat, salt * 3 + 1, 5641)} ${body}`
          : `The ${body} of ${seatName}`;
      if (!takenNames.has(candidate)) name = candidate;
    }
    if (!name) name = `The ${capital.name} Crown`;
    takenNames.add(name);

    // A capital is a capital: walls, and enough of them to be worth besieging.
    capital.fort = Math.max(capital.fort, capital.terrain === 'mountain' ? 3 : 2);
    capital.wealth += 3;

    nations.push({
      id: `n_${capital.id}`,
      name,
      // The banner, the roster and the architecture still come from the
      // faction — a race is the layer above one, and this is the bridge the
      // race contract named.
      factionId: factionOfRace(raceId),
      capital: capital.id,
      aggression: dial(span(doctrine.aggression[0], doctrine.aggression[1], seat, 1, 6079)),
      caution: dial(span(doctrine.caution[0], doctrine.caution[1], seat, 2, 6091)),
      militarism: dial(span(doctrine.militarism[0], doctrine.militarism[1], seat, 3, 6101)),
      creed: pick(doctrine.creeds, seat, k, 7207),
      doctrine: doctrine.id,
      raceId,
      leaderId: leaderForKingdom(raceId, nextHouse(raceId)).id,
    });
  });

  growTerritories(provinces, nations);
  assignBanners(provinces, nations);
  return nations;
}

/**
 * A temperament dial, rounded — and rounded to **three** places rather than two.
 *
 * The rounding is not cosmetic. `tests/realm.test.ts` asserts that no two
 * crowns share all three dials, because a crown whose whole personality is
 * three numbers and a sentence *is* those three numbers, and two crowns with
 * the same three are one AI wearing two banners.
 *
 * That is a birthday-paradox claim, and two places stopped being enough the
 * moment the board grew: a doctrine's aggression band is about 0.17 wide, so at
 * two places it holds eighteen values, and sixty-six crowns spread over eight
 * doctrines gave two collisions on the first board that had them. Three places
 * is a hundred and seventy values a band and about three million combinations a
 * doctrine, which is comfortably clear of any board this generator will make.
 * Nothing reads these as text, so the extra digit costs nothing.
 */
function dial(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Who holds what at turn one.
 *
 * Round-robin breadth-first from every capital at once, each crown taking one
 * province a round from the ground it already touches. Round-robin rather than
 * "grow each nation to its target in turn", because growing them one at a time
 * gives the first nation in the list the whole map and the last one a rock.
 *
 * Not everything is claimed. Roughly a province in eight is left to nobody,
 * which is what gives a campaign somewhere to expand into that is not a war —
 * the same job the bandit camps do, and for the same reason.
 */
function growTerritories(provinces: ProvinceDef[], nations: NationDef[]): void {
  const byId = new Map<string, number>();
  provinces.forEach((p, i) => byId.set(p.id, i));
  const adj: number[][] = provinces.map(() => []);
  provinces.forEach((p, i) => {
    for (const other of p.links) {
      const j = byId.get(other);
      if (j === undefined) continue;
      adj[i].push(j);
      adj[j].push(i);
    }
  });

  const owner = provinces.map((p) => (p.owner ? nations.findIndex((n) => n.id === p.owner) : -1));
  const size = nations.map(() => 0);
  for (const o of owner) if (o >= 0) size[o]++;

  nations.forEach((n, i) => {
    const seat = byId.get(n.capital);
    if (seat !== undefined && owner[seat] < 0) {
      owner[seat] = i;
      size[i]++;
    }
  });

  /** Does this crown already hold a shore? See the scoring rule below. */
  const seaward = nations.map(() => false);
  provinces.forEach((p, i) => {
    if (p.coastal && owner[i] >= 0) seaward[owner[i]] = true;
  });

  /**
   * Ambitious crowns start bigger, and none of them starts with the whole board.
   * The sum is deliberately short of the province count: what is left over is
   * the unclaimed ground a campaign can expand into without declaring on anyone.
   *
   * Four to seven was right on a board a crown could walk across. On a board in
   * twenty pieces a crown often *cannot reach* its target — the ground next to it
   * ran out at the coast — so the leftovers stopped being a design decision and
   * became an accident of which landmass a seat happened to land on: forty-five
   * of a hundred and fifty masterless, a third of the map drawn in nobody's
   * colour. Asking for a little more brings it to thirty-five, and the crowns
   * that genuinely have nowhere to grow still stop where their island stops.
   *
   * Not *much* more, and the ceiling was found rather than chosen. At five to
   * nine and a half the opening is a quarter richer per crown, and
   * `campaign-depth.test.ts`'s three-month march on Dunmarrow starts arriving
   * into a host it can no longer beat — a first move the whole opening is
   * balanced around, failing for no reason the player could see. Four point six
   * to eight point four leaves it green.
   */
  const target = nations.map((n, i) =>
    Math.round(span(5.0, 9.0, i, Math.round(n.aggression * 100), 8837)),
  );

  /** Roads from every province to the nearest shore. -1 where no road reaches one. */
  const toShore = provinces.map(() => -1);
  {
    const queue: number[] = [];
    provinces.forEach((p, i) => {
      if (!p.coastal) return;
      toShore[i] = 0;
      queue.push(i);
    });
    for (let head = 0; head < queue.length; head++) {
      const at = queue[head];
      for (const n of adj[at]) {
        if (toShore[n] >= 0) continue;
        toShore[n] = toShore[at] + 1;
        queue.push(n);
      }
    }
  }

  /**
   * Every crown reaches for the sea before anybody fattens up.
   *
   * On the lattice board this pass would have done nothing: nine holdings in ten
   * stood on water and every crown had a shore by accident. On a world of real
   * continents only a quarter of the board is coastal, and a crown seated inland
   * stays inland — the Ashen Crown, whose authored block is the *western*,
   * landward end of the heartland, opened a campaign with no province it could
   * ever raise a harbour on. That makes the whole naval layer invisible to the
   * player from the first month, and it is a fair description of half the
   * generated crowns too.
   *
   * A gradient inside the ordinary scoring was not enough, and the reason is
   * worth writing down: the ordinary loop is round-robin, so by the time a
   * landlocked crown had crawled two provinces toward the water its seaward
   * neighbours had already taken the shore. So the reach happens **first**, in
   * its own pass, and it is a beeline: each crown without a coast takes the
   * adjacent unclaimed province that is nearest to one, and keeps doing it until
   * it has a harbour or the ground runs out. A landlocked power reaching for the
   * sea is the most ordinary thing in history; what is unusual is a board where
   * it cannot.
   */
  /**
   * …and it takes a *stretch* of shore, not a single wharf.
   *
   * One coastal province is a harbour with one lane out of it, and if the coast
   * at the far end of that lane belongs to somebody, a crown at peace has a navy
   * with nowhere to go. Two is a coastline.
   */
  const SHORE_WANTED = 2;
  const shores = nations.map(() => 0);
  provinces.forEach((p, i) => {
    if (p.coastal && owner[i] >= 0) shores[owner[i]]++;
  });
  for (let round = 0; round < 14; round++) {
    let moved = false;
    for (let i = 0; i < nations.length; i++) {
      if (shores[i] >= SHORE_WANTED || size[i] >= target[i]) continue;
      let best = -1;
      let bestScore = Infinity;
      for (let p = 0; p < provinces.length; p++) {
        if (owner[p] >= 0 || p < HEARTLAND.length || toShore[p] < 0) continue;
        let touching = false;
        for (const q of adj[p]) if (owner[q] === i) touching = true;
        if (!touching) continue;
        const score = toShore[p] * 10 - hash2(p, i, 9613);
        if (score < bestScore) {
          bestScore = score;
          best = p;
        }
      }
      if (best < 0) continue;
      owner[best] = i;
      size[i]++;
      if (provinces[best].coastal) {
        seaward[i] = true;
        shores[i]++;
      }
      moved = true;
    }
    if (!moved) break;
  }


  let moved = true;
  while (moved) {
    moved = false;
    for (let i = 0; i < nations.length; i++) {
      if (size[i] >= target[i]) continue;
      let best = -1;
      let bestScore = -Infinity;
      for (let p = 0; p < provinces.length; p++) {
        if (owner[p] >= 0) continue;
        // The heartland is authored, and that includes the four holdings nobody
        // holds. Greywater, Thornmere, Lowmoot and The Reef are masterless on
        // purpose — they are the neutral ground the opening of a campaign is
        // built around — and generation does not get to tidy them away.
        if (p < HEARTLAND.length) continue;
        let touching = 0;
        for (const q of adj[p]) if (owner[q] === i) touching++;
        if (touching === 0) continue;
        /**
         * Compact realms: prefer the province that is already most surrounded —
         * except that a crown with no harbour at all reaches for one first.
         *
         * On the lattice board this rule was not needed, because nine holdings
         * in ten stood on water and every crown had a coast by accident. On a
         * world of real continents a quarter of the board is coastal and a crown
         * seated inland stays inland: the Ashen Crown, whose authored block is
         * the *western* end of the heartland and has never owned a shore of its
         * own, opened a campaign with no province it could ever build a harbour
         * on — which makes the entire naval layer invisible to the player from
         * the first month. A landlocked power reaching for the sea is also the
         * most ordinary thing in history, so it is a rule worth having.
         *
         * See the reach-for-the-sea pass above, which has already run.
         */
        const score = touching * 10 + provinces[p].wealth * 0.2 + hash2(p, i, 9601);
        if (score > bestScore) {
          bestScore = score;
          best = p;
        }
      }
      if (best < 0) continue;
      owner[best] = i;
      size[i]++;
      if (provinces[best].coastal) seaward[i] = true;
      moved = true;
    }
  }

  provinces.forEach((p, i) => {
    p.owner = owner[i] >= 0 ? nations[owner[i]].id : '';
  });
}

// ── Banners ────────────────────────────────────────────────────────────────

function hueOf(rgb: number): number {
  const r = ((rgb >> 16) & 255) / 255;
  const g = ((rgb >> 8) & 255) / 255;
  const b = (rgb & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 1e-6) return 0;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h < 0 ? h + 360 : h;
}

function fromHue(h: number, s: number, v: number): number {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const seg = Math.floor(h / 60) % 6;
  const table: Array<[number, number, number]> = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ];
  const [r, g, b] = table[seg];
  const to = (v2: number): number => Math.max(0, Math.min(255, Math.round((v2 + m) * 255)));
  return (to(r) << 16) | (to(g) << 8) | to(b);
}

/**
 * A banner nobody standing next to you is already flying.
 *
 * Twenty-four powers on one chart is more than any palette can keep genuinely
 * distinct, so the constraint is relaxed to the one that actually matters: two
 * nations whose territories *touch* must be far apart on the hue wheel. Nations
 * on opposite sides of the realm may share a colour; the player will never see
 * them in the same glance.
 *
 * The eight hand-authored powers keep their faction colours and are treated as
 * already assigned, so the generated ones dress around them.
 */
function assignBanners(provinces: ProvinceDef[], nations: NationDef[]): void {
  const byId = new Map<string, number>();
  provinces.forEach((p, i) => byId.set(p.id, i));
  const nationOf = new Map<string, number>();
  nations.forEach((n, i) => nationOf.set(n.id, i));

  const adj: Array<Set<number>> = nations.map(() => new Set<number>());
  provinces.forEach((p) => {
    const a = p.owner ? nationOf.get(p.owner) : undefined;
    if (a === undefined) return;
    for (const otherId of p.links) {
      const j = byId.get(otherId);
      if (j === undefined) continue;
      const b = provinces[j].owner ? nationOf.get(provinces[j].owner) : undefined;
      if (b === undefined || b === a) continue;
      adj[a].add(b);
      adj[b].add(a);
    }
  });

  const hue = new Array<number>(nations.length).fill(-1);
  // The authored eight, as they already are. Their accents come from the
  // factions, which this file cannot resolve, so the known overrides are used
  // and the rest are read from the faction table directly.
  for (let i = 0; i < HEART_NATIONS.length; i++) {
    const n = HEART_NATIONS[i];
    const fac = FACTIONS.find((f) => f.id === n.factionId);
    hue[i] = hueOf(n.banner ?? fac?.accent ?? 0x808080);
  }

  const SLOTS = 24;
  for (let i = HEART_NATIONS.length; i < nations.length; i++) {
    let bestSlot = 0;
    let bestGap = -1;
    for (let s = 0; s < SLOTS; s++) {
      const h = (s * 360) / SLOTS;
      let gap = 360;
      for (const j of adj[i]) {
        if (hue[j] < 0) continue;
        let d = Math.abs(h - hue[j]);
        if (d > 180) d = 360 - d;
        gap = Math.min(gap, d);
      }
      // A hash tiebreak, so a realm of unconstrained nations is not all one hue.
      const score = gap + hash2(i, s, 4441) * 8;
      if (score > bestGap) {
        bestGap = score;
        bestSlot = s;
      }
    }
    const h = (bestSlot * 360) / SLOTS;
    hue[i] = h;
    // Muted: the palette rule is that the banner is the one saturated thing on
    // the land, and "the one saturated thing" is not the same as "neon".
    const sat = 0.42 + hash2(i, 91, 4457) * 0.2;
    const val = 0.62 + hash2(i, 97, 4463) * 0.18;
    nations[i].banner = fromHue(h, sat, val);
  }
}

export const REALM: Realm = buildRealm();

/** Every cell of open water. `cartography.ts` draws the interior ones. */
export const SEA_CELLS: SeaCellDef[] = REALM.seas;

/** The sea-lane network. `src/sim/campaign/fleets.ts` sails on it. */
export const SEA_LANES: SeaLaneDef[] = REALM.lanes;

/**
 * The ground itself, two map units to a cell, with the province that holds each
 * one — or -1 for water. `cartography.ts` traces the chart's every line off this
 * and nothing else, which is what makes the drawn coast and the coast the
 * simulation believes in the same coast rather than two approximations.
 */
export const RASTER: RealmRaster = REALM.raster;

/**
 * The regions, in the order the reaches are declared.
 *
 * This is the seam a race roster and the start-of-game kingdom picker bind to —
 * see the long note on `RegionDef`.
 */
export const REGIONS: RegionDef[] = REALM.regions;

const REGION_OF: number[] = (() => {
  const out = new Array<number>(REALM.provinces.length).fill(-1);
  REGIONS.forEach((r, i) => {
    for (const p of r.provinces) out[p] = i;
  });
  return out;
})();

/** Which region a province is in. Always a valid index. */
export function regionOf(province: number): number {
  return REGION_OF[province] ?? -1;
}

export function regionDef(i: number): RegionDef {
  return REGIONS[i] ?? REGIONS[0];
}

export function regionById(id: string): RegionDef | undefined {
  return REGIONS.find((r) => r.id === id);
}

/**
 * The crowns seated in a region — the starting positions a player chooses
 * between once a race has claimed it.
 */
export function nationsInRegion(region: number): number[] {
  const r = REGIONS[region];
  if (!r) return [];
  const seats = new Set(r.provinces.map((p) => REALM.provinces[p].id));
  const out: number[] = [];
  REALM.nations.forEach((n, i) => {
    if (seats.has(n.capital)) out.push(i);
  });
  return out;
}

/** Regions of exactly this ground. `regionsOfHomeland` is usually what you want. */
export function regionsOfCharacter(character: RegionCharacter): number[] {
  const out: number[] = [];
  REGIONS.forEach((r, i) => {
    if (r.character === character) out.push(i);
  });
  return out;
}

/**
 * ── THE SEAM `races.ts` ASKED FOR, AND HERE IT IS ────────────────────────
 *
 * *"A region exposes a `Homeland`, and each kingdom it seats exposes `raceId`
 * and `leaderId`. Nothing else."* — the race contract, published before races
 * were built and unfilled until now.
 *
 * Every country a people could live in, including the frontiers it will settle
 * for: the `'waste'` peoples take a frozen or a desert march as well as a
 * waste, the heartland peoples take a temperate one. Ordered as
 * `HOMELAND_GROUND` orders them, so the first entry is the proper country and
 * later ones are compromises — a picker that wants *the* elf forest can take
 * the head of the list.
 */
export function regionsOfHomeland(h: Homeland): number[] {
  const out: number[] = [];
  for (const character of HOMELAND_GROUND[h]) {
    REGIONS.forEach((r, i) => {
      if (r.character === character && !out.includes(i)) out.push(i);
    });
  }
  return out;
}

/**
 * The peoples this region's ground belongs to — usually one, two where a
 * homeland has two peoples in it, and never none for any ground the world
 * actually contains (a start-up check in `realm.ts` refuses to build a world
 * where it could be).
 */
export function racesOfRegion(region: number): RaceId[] {
  const r = REGIONS[region];
  if (!r) return [];
  return racesOfHomeland(r.homeland).map((x) => x.id);
}

/** Every crown of a given people, anywhere on the board. Indices into `NATIONS`. */
export function nationsOfRace(raceId: string): number[] {
  const out: number[] = [];
  REALM.nations.forEach((n, i) => {
    if (n.raceId === raceId) out.push(i);
  });
  return out;
}

/**
 * Where each people actually ended up — the picture in one line per race, for
 * `tools/worldstats.ts` and for the test that asserts nobody is homeless.
 *
 * A people with no crowns is not an error the type system can catch: the ground
 * exists, but a region of two crowns whose homeland has three peoples in it
 * will seat two of them and not the third. So it is reported rather than
 * assumed, and the test reads this.
 */
export function seatOfRace(raceId: string): { regions: number[]; nations: number[] } {
  const nations = nationsOfRace(raceId);
  const seats = new Set(nations.map((i) => REALM.nations[i].capital));
  const regions: number[] = [];
  REGIONS.forEach((r, i) => {
    if (r.provinces.some((pv) => seats.has(REALM.provinces[pv].id)) && !regions.includes(i)) {
      regions.push(i);
    }
  });
  return { regions, nations };
}
